import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, CheckCircle, Clock, Play, Pause, Square,
  Plus, Search, FileText, Calendar, Upload, Trash2, FileImage, FileType,
  Stethoscope, Pill, Activity,
  X, CheckCircle2, ArrowRight,
  RefreshCw, ChevronDown,
  Eye, AlertTriangle,
  FlaskConical, Thermometer, Droplets, Wind, Scale, Ruler,
  HeartPulse, Gauge, BadgeCheck, CloudUpload, Download, Info,
  ChevronUp, ShieldCheck, Circle, FileCheck2
} from "lucide-react";
import { generatePrescriptionPDF } from "../../utils/PrescriptionTemplate";
import { uploadPrescription } from "../../services/uploadPrescription";
import prescriptionImage from "../../assets/images/prescription.png";
import clinicLogo from "../../assets/images/web_logo.png";
import { PrescriptionPaymentWorkflow } from "../../components/PrescriptionPaymentWorkflow";
import DynamicVitalsCaptureModal from "../../components/DynamicVitalsCaptureModal";
import WalkInModal from "../../components/WalkInModal";
import ConsultationPaymentModal from "../../components/ConsultationPaymentModal";
import PatientInvoiceBillingModal from "../../components/PatientInvoiceBillingModal";
import ClinicalDocumentsPanel, { ReportUploadModal } from "../../components/ClinicalDocumentsPanel";
import VisitReviewScreen from "./VisitReviewScreen";
import { useAuth } from "../../context/AuthContext";
import appConfig from "../../config/appConfig";
import { buildApiUrl } from '../../services/apiHelpers';
import { vitalMeasurementService } from "../../services/vitalMeasurementService";

/* ─── Time Helpers ─────────────────────────────────────────── */
const getISTDate = () => new Date();
const getISTTime = (date) => new Date(date).toLocaleString("en-IN", {
  hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata"
});
const formatIST = (date, options = {}) =>
  new Date(date).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", ...options });
const formatSlotTime = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours < 24 && minutes < 60) {
      const istTime = new Date(Date.UTC(1970, 0, 1, hours, minutes) - (5 * 60 + 30) * 60 * 1000);
      return istTime.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? raw
    : parsed.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
};

const parseObservationValue = (value) => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
};

const formatObservationValue = (value) => {
  const parsed = parseObservationValue(value);
  if (parsed == null) return "";
  if (typeof parsed === "object") {
    if (parsed.systolic != null || parsed.diastolic != null) return `${parsed.systolic ?? "—"}/${parsed.diastolic ?? "—"}`;
    return Object.entries(parsed).map(([key, entry]) => `${key}: ${entry}`).join(" · ");
  }
  return String(parsed);
};

const groupMeasurementHistory = (rows = []) => {
  const visits = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const appointmentId = row.appointmentId ?? row.appointment_id ?? null;
    const groupKey = appointmentId == null ? `unlinked-${row.recordedAt || row.id}` : String(appointmentId);
    let visit = visits.get(groupKey);
    if (!visit) {
      visit = { id: `visit-vitals-${groupKey}`, appointmentId, recordedAt: row.recordedAt, measurements: [] };
      visits.set(groupKey, visit);
    }
    const key = row.measurementKey ?? row.measurement_key;
    const value = parseObservationValue(row.value ?? row.value_json);
    const measurement = {
      id: row.id,
      measurementId: row.measurementId ?? row.measurement_id,
      measurementKey: key,
      name: row.name ?? row.measurementName ?? row.measurement_name ?? key ?? "Measurement",
      unit: row.unit ?? row.unitSnapshot ?? row.unit_snapshot ?? "",
      canonicalNumericValue: row.canonicalNumericValue ?? row.canonical_numeric_value ?? null,
      value,
    };
    visit.measurements.push(measurement);
    if (key === "m001" && value && typeof value === "object") {
      visit.bloodPressureSystolic ??= value.systolic;
      visit.bloodPressureDiastolic ??= value.diastolic;
      visit.bloodPressureUnit ??= measurement.unit;
    } else if (key === "m002") { visit.heartRate ??= value; visit.heartRateUnit ??= measurement.unit; }
    else if (key === "m003") { visit.respiratoryRate ??= value; visit.respiratoryRateUnit ??= measurement.unit; }
    else if (key === "m004") { visit.temperature ??= value; visit.temperatureUnit ??= measurement.unit; }
    else if (key === "m005") { visit.spO2 ??= value; visit.spO2Unit ??= measurement.unit; }
    else if (key === "m006") { visit.weight ??= value; visit.weightUnit ??= measurement.unit; visit.canonicalWeight ??= measurement.canonicalNumericValue; }
    else if (key === "m007") { visit.height ??= value; visit.heightUnit ??= measurement.unit; visit.canonicalHeight ??= measurement.canonicalNumericValue; }
    else if (key === "m008") visit.bmi ??= value;
    else if (key === "m051") visit.bloodSugar ??= value;
    else if (key === "legacy-notes") visit.notes ??= value;
  });
  return Array.from(visits.values());
};

/* ─── Design System ─────────────────────────────────────────── */
const DESIGN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --white:        #ffffff;
  --bg:           #f8fafc;
  --surface:      #ffffff;
  --border:       #e2e8f0;
  --border-light: #f1f5f9;

  --gray-50:  #f8fafc;
  --gray-100: #f1f5f9;
  --gray-200: #e2e8f0;
  --gray-300: #cbd5e1;
  --gray-400: #94a3b8;
  --gray-500: #64748b;
  --gray-600: #475569;
  --gray-700: #334155;
  --gray-800: #1e293b;
  --gray-900: #0f172a;

  --blue-50:  #eff6ff;
  --blue-100: #dbeafe;
  --blue-200: #bfdbfe;
  --blue-400: #60a5fa;
  --blue-500: #3b82f6;
  --blue-600: #2563eb;
  --blue-700: #1d4ed8;
  --blue-800: #1e40af;

  --green-50:  #f0fdf4;
  --green-100: #dcfce7;
  --green-200: #bbf7d0;
  --green-500: #22c55e;
  --green-600: #16a34a;
  --green-700: #15803d;

  --amber-50:  #fffbeb;
  --amber-100: #fef3c7;
  --amber-200: #fde68a;
  --amber-500: #f59e0b;
  --amber-600: #d97706;
  --amber-700: #b45309;

  --red-50:  #fff1f2;
  --red-100: #ffe4e6;
  --red-200: #fecdd3;
  --red-500: #ef4444;
  --red-600: #dc2626;
  --red-700: #b91c1c;

  --violet-50:  #f5f3ff;
  --violet-100: #ede9fe;
  --violet-200: #ddd6fe;
  --violet-500: #8b5cf6;
  --violet-600: #7c3aed;
  --violet-700: #6d28d9;

  --cyan-50:  #ecfeff;
  --cyan-500: #06b6d4;
  --cyan-600: #0891b2;

  --font:      'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --mono:      'JetBrains Mono', 'Fira Code', monospace;

  --r-xs:  3px;
  --r-sm:  6px;
  --r-md:  8px;
  --r-lg:  10px;
  --r-xl:  14px;
  --r-2xl: 18px;
  --r-3xl: 24px;
  --r-full: 9999px;

  --sh-xs: 0 1px 2px rgba(0,0,0,0.05);
  --sh-sm: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);
  --sh-md: 0 4px 8px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04);
  --sh-lg: 0 8px 24px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.04);
  --sh-blue: 0 4px 14px rgba(37,99,235,0.28);

  --ease: cubic-bezier(0.4,0,0.2,1);
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}

html, body, #root { height: 100%; overflow: hidden; }
body { font-family: var(--font); background: var(--bg); color: var(--gray-900); -webkit-font-smoothing: antialiased; }

/* Scrollbars */
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--gray-300); border-radius: 99px; }
::-webkit-scrollbar-thumb:hover { background: var(--gray-400); }

/* Keyframes */
@keyframes rise { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes fade { from { opacity:0; } to { opacity:1; } }
@keyframes scale-in { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }
@keyframes pulse-ring { 0% { transform:scale(1); opacity:0.7; } 100% { transform:scale(2.4); opacity:0; } }
@keyframes spin { to { transform:rotate(360deg); } }
@keyframes shimmer { 0% { background-position:-200% 0; } 100% { background-position:200% 0; } }
@keyframes ticker { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-1px); } }

/* Utility */
.anim-rise   { animation: rise 0.35s var(--ease) both; }
.anim-fade   { animation: fade 0.2s var(--ease) both; }
.anim-scale  { animation: scale-in 0.28s var(--spring) both; }
.anim-spin   { animation: spin 0.65s linear infinite; }

/* Input base */
.rx-label {
  display: block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  color: var(--gray-600);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.opd-input {
  width: 100%; padding: 14px 16px;
  background: var(--white); border: 1.5px solid var(--gray-200);
  border-radius: var(--r-2xl); font-family: var(--font); font-size: 14px;
  color: var(--gray-900); outline: none;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
}
.opd-input:focus { border-color: var(--blue-500); box-shadow: 0 0 0 5px rgba(59,130,246,0.12); transform: translateY(-0.5px); }
.opd-input::placeholder { color: var(--gray-400); }

.opd-honor-input-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  border: 1.5px solid var(--blue-100);
  border-radius: var(--r-2xl);
  background: rgba(59,130,246,0.06);
  overflow: hidden;
}
.opd-honor-input-wrapper:focus-within {
  border-color: var(--blue-500);
  box-shadow: 0 0 0 5px rgba(59,130,246,0.12);
}
.opd-honor-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  padding: 0 12px;
  background: var(--blue-50);
  color: var(--blue-700);
  font-size: 13px;
  font-weight: 800;
  border-right: 1px solid var(--blue-100);
}
.opd-input-divider {
  width: 1px;
  height: 70%;
  background: var(--blue-100);
  align-self: center;
}
.opd-name-input {
  flex: 1;
  border: none;
  border-radius: 0;
  padding: 0 16px;
  min-width: 0;
  font-family: var(--font);
  font-size: 14px;
  color: var(--gray-900);
  outline: none;
}
.opd-name-input:focus {
  box-shadow: none;
}
.opd-input {
  width: 100%; padding: 14px 16px;
  background: var(--white); border: 1.5px solid var(--gray-200);
  border-radius: var(--r-2xl); font-family: var(--font); font-size: 14px;
  color: var(--gray-900); outline: none;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
}
.opd-input:focus { border-color: var(--blue-500); box-shadow: 0 0 0 5px rgba(59,130,246,0.12); transform: translateY(-0.5px); }
.opd-input::placeholder { color: var(--gray-400); }

.opd-select {
  width: 100%; padding: 14px 40px 14px 16px;
  background: var(--white) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 14px center;
  border: 1.5px solid var(--gray-200); border-radius: var(--r-2xl);
  font-family: var(--font); font-size: 14px; color: var(--gray-900);
  outline: none; appearance: none; cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.opd-select:focus { border-color: var(--blue-500); box-shadow: 0 0 0 5px rgba(59,130,246,0.12); }

/* rx-input */
.rx-input {
  width: 100%; padding: 9px 12px;
  background: var(--white); border: 1.5px solid var(--gray-200);
  border-radius: var(--r-lg); font-family: var(--font); font-size: 13px;
  color: var(--gray-900); outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rx-input:focus { border-color: var(--blue-500); box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
.rx-input::placeholder { color: var(--gray-400); opacity: 1; font-size: inherit; }

.rx-select {
  width: 100%; padding: 9px 32px 9px 12px;
  background: var(--white) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 11px center;
  border: 1.5px solid var(--gray-200); border-radius: var(--r-lg);
  font-family: var(--font); font-size: 12px; color: var(--gray-900);
  outline: none; appearance: none; cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rx-select:focus { border-color: var(--blue-500); box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }

/* Table row hover */
.q-row { transition: background 0.12s; border-bottom: 1px solid var(--gray-100); }
.q-row:hover { background: #f0f6ff !important; }
.q-row:last-child { border-bottom: none; }

.hist-row { transition: background 0.12s; border-bottom: 1px solid var(--gray-100); }
.hist-row:hover { background: var(--blue-50) !important; }

/* Modal overlay */
.opd-overlay {
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(15,23,42,0.4);
  display: flex; align-items: center; justify-content: center;
  padding: 16px; animation: fade 0.18s ease;
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
}

/* Full page overlay */
.opd-fullpage {
  position: fixed; inset: 0; z-index: 1001;
  background: var(--bg); overflow: auto; animation: fade 0.22s ease;
}

.opd-prescription-shell { min-height: 100vh; }
.opd-prescription-topbar { gap: 12px; }
.opd-prescription-topbar-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.opd-prescription-mode-group { display: flex; background: var(--gray-100); border-radius: var(--r-lg); padding: 3px; }
.opd-prescription-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 300px);
  gap: 0;
  flex: 1;
  overflow: hidden;
}
.opd-prescription-main { min-width: 0; }
.opd-prescription-side { min-width: 0; border-left: 1px solid var(--gray-200); }
.opd-prescription-savebar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1010;
}
.opd-medicine-grid,
.opd-medicine-row {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) minmax(104px, 0.55fr) repeat(4, minmax(52px, 1fr)) minmax(60px, 72px) minmax(110px, 130px) 26px;
  gap: 5px;
  align-items: start;
}
.opd-medicine-header {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) minmax(104px, 0.55fr) repeat(4, minmax(52px, 1fr)) minmax(60px, 72px) minmax(110px, 130px) 26px;
  gap: 5px;
  padding-bottom: 7px;
  border-bottom: 1px solid var(--gray-100);
  margin-bottom: 7px;
}
.opd-medicine-row { padding: 7px; margin: 0 -7px; border-radius: var(--r-lg); transition: background 0.12s; }
.opd-medicine-row:hover { background: var(--gray-50); }
.opd-medicine-row > * { min-width: 0; }
.opd-medicine-row > :nth-child(1) { min-width: 0; }
.opd-medicine-row > :nth-child(1) > div { min-width: 0; }
.opd-medicine-row > :nth-child(2),
.opd-medicine-row > :nth-child(8) { min-width: 0; }

@media (max-width: 1280px) {
  .opd-prescription-body { grid-template-columns: minmax(0, 1fr) 280px !important; }
  .opd-prescription-topbar { padding: 10px 18px !important; }
  .opd-prescription-main { padding: 18px 18px 108px !important; }
  .opd-prescription-side { padding: 18px 16px !important; }
}

@media (max-width: 1024px) {
  .opd-prescription-body { display: flex !important; flex-direction: column !important; overflow: visible !important; }
  .opd-prescription-side { border-left: none; border-top: 1px solid var(--gray-200); }
  .opd-prescription-main,
  .opd-prescription-side { overflow: visible !important; }
  .opd-prescription-main { padding-bottom: 18px !important; }
  .opd-prescription-side { padding-top: 0 !important; order: 0; }
  .opd-prescription-main { order: 1; }
  .opd-medicine-grid,
  .opd-medicine-header,
  .opd-medicine-row { min-width: 0; }
}

@media (max-width: 768px) {
  .opd-prescription-shell { min-height: 100dvh; }
  .opd-prescription-topbar { padding: 10px 14px !important; flex-wrap: wrap; gap: 10px !important; }
  .opd-prescription-topbar-actions { width: 100%; justify-content: flex-start; flex-wrap: wrap; gap: 8px; }
  .opd-prescription-mode-group { width: 100%; justify-content: space-between; }
  .opd-prescription-mode-group > button { flex: 1 1 0; padding-inline: 10px !important; }
  .opd-prescription-body { display: flex !important; flex-direction: column !important; }
  .opd-prescription-main { padding: 14px 14px 108px !important; }
  .opd-prescription-side { padding: 0 14px 18px !important; }
  .opd-prescription-main > form > div,
  .opd-prescription-main > form > div:last-child { padding: 16px 16px !important; }
  .opd-medicine-grid,
  .opd-medicine-header,
  .opd-medicine-row { min-width: 0; }
  .opd-prescription-savebar { padding: 10px 14px !important; }
}

@media (max-width: 640px) {
  .opd-prescription-shell { min-height: 100dvh; }
  .opd-prescription-topbar { padding: 10px 12px !important; }
  .opd-prescription-topbar > div:first-child { width: 100%; gap: 10px; align-items: flex-start; }
  .opd-prescription-topbar > div:last-child { width: 100%; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
  .opd-prescription-topbar-actions { justify-content: space-between; align-items: stretch !important; }
  .opd-prescription-mode-group { width: 100%; }
  .opd-prescription-mode-group > button { padding: 5px 10px !important; font-size: 11px !important; }
  .opd-prescription-main { padding: 12px 12px 112px !important; }
  .opd-prescription-side { padding: 0 12px 14px !important; }
  /* Compact action buttons for small screens */
  .q-action-btn--compact { padding: 4px 6px !important; font-size:12px !important; }
  /* Target Btn components passing data-compact attribute (default) */
  button[data-compact] { padding: 4px 6px !important; font-size:12px !important; }
  button[data-compact][disabled] { opacity:0.9 !important; }
  @media (max-width: 420px) {
    .q-action-btn--compact { padding: 3px 6px !important; font-size:11px !important; }
    /* Reduce overall button sizing when screen is small */
    .q-table .q-action-btn--compact { font-size:11px !important; }
    /* Target Btn components passing data-compact attribute (mobile) */
    button[data-compact] { padding: 3px 6px !important; font-size:11px !important; }
  }
  .opd-prescription-main > form > div,
  .opd-prescription-main > form > div:last-child { padding: 14px 14px !important; margin-bottom: 12px !important; }
  .opd-prescription-main > form > div:nth-child(2) { overflow: visible !important; }
  .opd-prescription-main > form > div:nth-child(2) > div { min-width: 0; }
  .opd-prescription-main > form > div:nth-child(2) { overflow: hidden !important; }
  .opd-medicine-header { display: none !important; }
  .opd-medicine-row {
    display: flex !important;
    flex-direction: column !important;
    gap: 5px !important;
    padding: 6px !important;
    margin: 0 0 6px !important;
    border: 1px solid var(--gray-200);
    border-radius: var(--r-lg);
    background: var(--gray-50);
  }
  .opd-medicine-line-primary,
  .opd-medicine-line-doses,
  .opd-medicine-line-meta {
    display: grid !important;
    gap: 4px !important;
    width: 100% !important;
  }
  .opd-medicine-line-primary { grid-template-columns: minmax(0, 1fr) 34px 38px !important; }
  .opd-medicine-line-doses { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 2px !important; justify-items: center; }
  .opd-medicine-line-meta { grid-template-columns: 40px minmax(0, 1fr) 20px !important; }
  .opd-medicine-line-primary > *,
  .opd-medicine-line-doses > *,
  .opd-medicine-line-meta > * { min-width: 0; }
  .opd-medicine-line-primary input,
  .opd-medicine-line-primary select,
  .opd-medicine-line-doses input,
  .opd-medicine-line-meta input,
  .opd-medicine-line-meta select {
    padding: 4px 5px !important;
    font-size: 9px !important;
    min-height: 24px !important;
    border-radius: 6px !important;
  }
  .opd-medicine-line-primary > div:first-child { min-width: 0; }
  .opd-medicine-line-primary > div:first-child > div { width: 100% !important; }
  .opd-medicine-line-primary > div:first-child > div > div:first-child { width: 100% !important; }
  .opd-medicine-row .rx-input,
  .opd-medicine-row .rx-select {
    padding: 6px 8px !important;
    font-size: 12px !important;
    border-radius: 6px !important;
    min-height: 32px;
    width: 100% !important;
  }
  .opd-medicine-row .rx-input::placeholder { font-size: 12px !important; color: var(--gray-400) !important; opacity: 1 !important; }
  .opd-medicine-line-doses > div {
    width: 100%;
    max-width: 38px;
  }
  .opd-medicine-line-doses input {
    padding-left: 2px !important;
    padding-right: 2px !important;
    text-align: center !important;
  }
  .opd-medicine-line-doses > div > div {
    font-size: 7px !important;
    margin-top: 0 !important;
    line-height: 1.1 !important;
  }
  .opd-prescription-main > form > div:last-child input[type="date"] { width: 100%; max-width: none !important; }
  .opd-prescription-savebar { padding: 10px 12px !important; background: rgba(255,255,255,0.98); backdrop-filter: blur(14px); border-top: 1px solid var(--gray-150); box-shadow: 0 -4px 20px rgba(0,0,0,0.05); flex-direction: column !important; align-items: stretch !important; gap: 10px; }
  .opd-prescription-savebar > div:first-child { text-align: center; width: 100%; }
  .opd-prescription-savebar > div:last-child { width: 100%; display: flex; gap: 8px; }
  .opd-prescription-savebar > div:last-child > button,
  .opd-prescription-savebar > div:last-child > .opd-save-primary { flex: 1 1 0; }
  .opd-medicine-grid,
  .opd-medicine-header,
  .opd-medicine-row { min-width: 680px; }
}

.q-token-sub, .q-phone-sub { display: none; }

/* ── RESPONSIVE LAYOUT ── */

/* Phone: stack everything vertically, compact header */
@media (max-width: 640px) {
  .opd-shell          { flex-direction: column !important; }
  .opd-header         { padding: 10px 14px !important; gap: 10px !important; }
  .opd-header-clock   { display: none !important; }
  .opd-toolbar        { flex-wrap: wrap !important; padding: 10px 14px !important; gap: 8px !important; }
  .opd-toolbar-search { order: 3; width: 100% !important; flex: none !important; }
  .opd-stat-bar       { grid-template-columns: 1fr 1fr !important; gap: 0 !important; }
  .opd-stat-card:nth-child(2n) { border-right: none !important; }
  .opd-stat-card:nth-child(-n+2) { border-bottom: 1px solid var(--gray-200) !important; }
  .opd-stat-card:nth-child(n+3) { border-bottom: none !important; }
  .opd-body           { padding: 12px 0 0 !important; }
  .opd-table-header   { padding: 12px 0 !important; }
  .q-slot-col         { display: none !important; }
  .q-phone-col        { display: none !important; }
  .q-token-sub        { display: block !important; }
  .q-phone-sub        { display: block !important; }
  .q-table-scroll     { padding: 0 0 76px !important; }
  .q-table            { width: 100% !important; margin: 0 auto !important; }
  .q-table thead th:nth-child(1), .q-table tbody td:nth-child(1) { width: 15% !important; padding-left: 12px !important; padding-right: 0 !important; }
  .q-table thead th:nth-child(3), .q-table tbody td:nth-child(3) { width: 35% !important; padding-left: 20px !important; padding-right: 4px !important; }
  .q-table thead th:nth-child(5), .q-table tbody td:nth-child(5) { width: 23% !important; padding-left: 2px !important; padding-right: 2px !important; }
  .q-table thead th:nth-child(6), .q-table tbody td:nth-child(6) { width: 27% !important; padding-left: 0 !important; padding-right: 0 !important; }
  .q-token-col        { display: table-cell !important; vertical-align: top !important; }
  .q-token-badge      { width: 20px !important; height: 20px !important; font-size: 8px !important; }
  .q-name-col         { width: auto !important; vertical-align: middle !important; }
  .q-phone-sub        { font-size: 9px !important; line-height: 1.1 !important; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .mobile-action-compact { width: 100% !important; flex-direction: column !important; align-items: center !important; gap: 2px !important; }
  .mobile-action-compact > * { width: 92px !important; min-width: 92px !important; justify-content: center !important; padding: 4px 0 !important; min-height: 26px !important; font-size: 10px !important; line-height: 1 !important; }
  .q-table            { font-size: 10.5px !important; }
  .opd-header-title   { font-size: 14px !important; }
  .opd-session-info   { display:flex !important; flex-wrap:wrap !important; gap:4px !important; }
  .opd-session-info > span { font-size:10px !important; }
  .opd-session-info code { font-size:9.5px !important; padding:1px 5px !important; }
}

/* Tablet: 2-col stat bar, hide some elements */
@media (min-width: 641px) and (max-width: 1024px) {
  .opd-stat-bar       { grid-template-columns: repeat(2, 1fr) !important; }
  .opd-stat-card:nth-child(2n) { border-right: none !important; }
  .opd-stat-card:nth-child(-n+2) { border-bottom: 1px solid var(--gray-200) !important; }
  .opd-stat-card:nth-child(n+3) { border-bottom: none !important; }
  .q-phone-sub        { display: none !important; }
}

/* Large screens */
@media (min-width: 1025px) {
  .opd-stat-bar { grid-template-columns: repeat(4, 1fr) !important; }
}

/* ─── RX RESPONSIVE ADDITIONS ─────────────────────────────── */
.rx-topbar{background:rgba(255,255,255,0.95);border:none;padding:10px 16px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;box-shadow:0 18px 45px rgba(15,23,42,0.08);position:sticky;top:0;z-index:20;gap:10px;backdrop-filter:blur(18px);padding-right:86px;}
.rx-topbar-left{display:flex;align-items:flex-start;gap:10px;min-width:0;flex:0 1 auto;max-width:45%;}
.rx-topbar-right{display:flex;align-items:center;gap:12px;flex:1 1 auto;flex-wrap:nowrap;justify-content:flex-end;min-width:0;overflow:visible;margin-right:12px;}
.rx-shell{min-height:100vh;max-height:100vh;overflow:hidden;display:flex;flex-direction:column;background:var(--bg);font-family:var(--font);}
.rx-patient-header-line{display:flex;align-items:center;gap:8px;flex-wrap:nowrap;min-width:0;}
.rx-patient-name{font-size:15px;font-weight:700;color:var(--gray-900);display:inline-flex;align-items:center;gap:8px;min-width:0;line-height:1.15;}
.rx-patient-token{font-family:var(--mono);font-size:11px;background:var(--blue-50);color:var(--blue-700);padding:3px 8px;border-radius:var(--r-sm);border:1px solid var(--blue-200);font-weight:700;flex-shrink:0;white-space:nowrap;}
.rx-patient-sub{font-size:12px;color:var(--gray-600);margin-top:4px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;line-height:1.4;}
.rx-patient-sub.rx-patient-agegender-line{gap:0;}
.rx-patient-doctor{font-weight:600;color:var(--gray-700);}
.rx-patient-clinic{color:var(--gray-500);}
.rx-patient-agegender{font-weight:600;color:var(--gray-600);white-space:nowrap;}
.rx-tab-btn{padding:5px 10px;border-radius:999px;border:1px solid transparent;cursor:pointer;font-size:10.5px;font-weight:700;font-family:var(--font);transition:all 0.18s var(--ease);background:transparent;color:var(--gray-600);min-width:0;flex-shrink:0;white-space:nowrap;}
.rx-tab-btn.active{background:linear-gradient(135deg, var(--blue-600), var(--violet-600));color:#fff;box-shadow:0 8px 20px rgba(59,130,246,0.16);border-color:transparent;}
.desktop-report-history{display:inline-flex;}
.mobile-report-history{display:none!important;}
.mobile-create-label{display:none;}
.reports-history-btn{color:#087f7a!important;border:1px solid #9adbd6!important;background:linear-gradient(135deg,#f2fffe,#dff8f4)!important;}
.reports-history-btn:hover{color:#fff!important;border-color:#087f7a!important;background:linear-gradient(135deg,#087f7a,#0e9f98)!important;box-shadow:0 8px 18px rgba(8,127,122,.22);transform:translateY(-1px);}
.rx-report-actions{align-items:stretch;margin-bottom:18px;}
.add-report-btn,.rx-report-actions .reports-history-btn{position:relative;overflow:hidden;min-height:44px!important;border-radius:14px!important;font-weight:800!important;letter-spacing:.01em;transition:transform .18s var(--ease),box-shadow .18s var(--ease),background .18s var(--ease)!important;}
.add-report-btn{color:#075985!important;border:1px solid #9ccff0!important;background:linear-gradient(135deg,#f3faff,#dcefff)!important;box-shadow:0 5px 14px rgba(37,99,235,.08);}
.add-report-btn:hover{color:#fff!important;border-color:#2563eb!important;background:linear-gradient(135deg,#2563eb,#0f7f82)!important;box-shadow:0 10px 22px rgba(37,99,235,.2);transform:translateY(-1px);}
.add-report-btn svg,.reports-history-btn svg{filter:drop-shadow(0 1px 1px rgba(0,0,0,.08));}
.rx-mode-btn{padding:5px 10px;border-radius:999px;border:1px solid var(--gray-200);cursor:pointer;font-size:10.5px;font-weight:700;font-family:var(--font);transition:all 0.18s var(--ease);background:var(--white);color:var(--gray-700);min-width:0;}
.rx-mode-btn.active{background:var(--blue-600);color:#fff;border-color:transparent;box-shadow:0 10px 25px rgba(59,130,246,0.12);}
.rx-badge-pill{display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border-radius:999px;background:var(--green-50);color:var(--green-700);font-size:11px;font-weight:700;border:1px solid var(--green-200);}
.rx-body{display:grid;grid-template-columns:minmax(0,1fr) minmax(260px,300px);gap:0;flex:1;overflow:hidden;min-height:0;}
.rx-body.history-mode,.rx-body.documents-mode{grid-template-columns:minmax(0,1fr);min-height:0;}
.rx-main-scroll{overflow-y:auto;padding:20px 24px 120px;min-width:0;min-height:0;}
.rx-body.history-mode .rx-main-scroll{display:flex;flex-direction:column;overflow:hidden;min-height:0;flex:1;height:100%;min-height:calc(100vh - 160px);}
.rx-body.documents-mode .rx-main-scroll{overflow-y:auto;}
.rx-history-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;min-height:0;height:100%;}
.rx-body.history-mode .rx-history-wrap{flex:1;overflow-y:auto;min-height:0;}
.rx-vitals-panel{background:var(--white);border-left:1px solid var(--gray-200);overflow-y:auto;padding:20px 18px;display:flex;flex-direction:column;gap:18px;flex-shrink:0;min-width:0;}
.rx-bottom-bar{position:fixed;bottom:0;left:0;right:0;background:rgba(255,255,255,0.95);color:var(--gray-900);backdrop-filter:blur(12px);border-top:1px solid var(--gray-200);padding:8px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;z-index:1010;box-shadow:0 -6px 18px rgba(15,23,42,0.06);}
.rx-bottom-hint{font-size:12px;color:var(--gray-500);flex:1;text-align:center;margin:0 12px;}
.rx-bottom-left{display:flex;gap:9px;align-items:center;}
.rx-bottom-right{display:flex;gap:9px;align-items:center;}
.rx-finalise-btn{padding:12px 24px;border:none;border-radius:999px;font-family:var(--font);font-weight:700;font-size:13.5px;cursor:pointer;display:inline-flex;align-items:center;gap:10px;transition:all 0.16s var(--ease);min-height:44px;}
.rx-med-header-row{display:grid;grid-template-columns:1.2fr 0.5fr 50px 50px 50px 50px 66px 110px 26px;gap:5px;padding-bottom:9px;border-bottom:1px solid var(--gray-200);margin-bottom:8px;}
.rx-med-row{display:grid;grid-template-columns:1.2fr 0.5fr 50px 50px 50px 50px 66px 110px 26px;gap:8px;align-items:start;padding:14px; margin:0 -7px;border-radius:var(--r-2xl);background:var(--white);border:1px solid var(--gray-200);transition:transform 0.2s,var(--ease);}
.rx-med-row:hover{background:var(--blue-50);transform:translateY(-1px);}
.rx-med-table-wrap{overflow-x:auto;}
.rx-vitals-toggle{display:flex;width:100%;padding:12px 16px;background:var(--white);border:1.5px solid var(--gray-200);border-radius:var(--r-2xl);color:var(--gray-700);font-family:var(--font);font-size:13px;font-weight:700;cursor:pointer;align-items:center;justify-content:space-between;gap:10px;transition:all 0.15s;margin-bottom:14px;box-shadow:var(--sh-xs);}
.rx-vitals-toggle:hover{background:var(--blue-50);border-color:var(--blue-200);}
.rx-vitals-inline{display:block;background:var(--white);border:1.5px solid var(--gray-200);border-radius:var(--r-xl);padding:18px;margin-bottom:14px;animation:scale-in 0.22s var(--spring) both;box-shadow:var(--sh-xs);}
.rx-med-name-form-row{display:grid;grid-template-columns:minmax(0,1fr) 72px;gap:6px;}
.rx-med-strength-timing-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:6px;}
.rx-med-dose-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:4px;}
.rx-med-days-del-row{display:flex;align-items:center;justify-content:space-between;gap:8px;}
.rx-med-days-input{max-width:80px;}
.rx-dose-mini-label{font-size:8px;font-weight:700;color:var(--gray-400);text-transform:uppercase;letter-spacing:0.06em;text-align:center;margin-bottom:2px;}
.rx-history-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;min-height:0;height:100%;}
.rx-section-card{overflow:hidden;background:var(--white);border:1px solid var(--gray-200);border-radius:var(--r-2xl);box-shadow:var(--sh-xs);}
.rx-section-title{display:inline-block;font-size:11px;font-weight:700;color:var(--gray-500);text-transform:uppercase;letter-spacing:0.08em;}
.rx-notes-followup-row{display:flex;gap:14px;margin-bottom:14px;align-items:stretch;}
.rx-notes-followup-row > .rx-section-card{min-width:0;}

@media (max-width: 640px) {
  .rx-notes-followup-row{flex-direction:column;}
  .rx-notes-followup-row > .rx-section-card{width:100%;}
  .rx-notes-followup-row > .rx-section-card textarea,
  .rx-notes-followup-row > .rx-section-card input{width:100%;}
}

@media (max-width: 1024px) {
  .rx-body{grid-template-columns:1fr;}
  .rx-vitals-panel{display:none!important;}
  .rx-vitals-toggle{display:flex;}
  .rx-main-scroll{padding:16px 20px 120px;}
  .rx-med-table-wrap{overflow-x:auto;padding-bottom:10px;}
  .rx-med-header-row{grid-template-columns:1.2fr 0.5fr 50px 50px 50px 50px 66px 110px 26px;}
  .rx-med-row{grid-template-columns:1.2fr 0.5fr 50px 50px 50px 50px 66px 110px 26px;}
  .rx-med-col-dose-split{display:grid;grid-template-columns:repeat(4,1fr);gap:4px;}
  .rx-med-col-timing{font-size:11px;}
  .rx-col-dose-desktop{display:block;}
  .rx-col-dose-tablet{display:block;}
}

@media (min-width: 1025px) {
  /* keep vitals toggle visible on desktop too */
}

@media (max-width: 640px) {
  .rx-topbar{padding:10px 14px;flex-wrap:wrap;gap:10px;}
  .rx-web_logo-badge{width:32px!important;height:32px!important;font-size:10px!important;border-radius:var(--r-lg)!important;}
  .rx-patient-name{font-size:13px;max-width:36vw;}
  .rx-patient-sub{font-size:10px;}
  .rx-tab-btn{padding:5px 9px!important;font-size:11px!important;flex-shrink:0!important;white-space:nowrap!important;}
  .rx-topbar-right{margin-right:10px!important;gap:10px!important;}
  .desktop-report-history{display:none!important;}
  .rx-report-actions{margin-bottom:22px!important;}
  .mobile-report-history{display:inline-flex!important;}
  .desktop-create-label{display:none;}
  .mobile-create-label{display:inline;}
  .rx-main-scroll{padding:20px 12px 60px;}
  /* Mobile: use stacked card rows (MobileCardRow) showing all inputs in separate lines */
  .rx-med-table-wrap{overflow:visible;padding-bottom:0;}
  .rx-med-header-row{display:none!important;}
  .rx-med-row{display:flex!important;flex-direction:column;gap:8px;background:var(--gray-50);border:1px solid var(--gray-200);border-radius:var(--r-lg);padding:12px!important;margin:0 0 8px!important;}
  .rx-med-row:hover{background:var(--blue-50);}
  .rx-med-desktop-only{display:none!important;}
  .rx-med-mobile-only{display:block!important;}
  .rx-med-name-form-row{display:grid;grid-template-columns:minmax(0,1fr) 72px;gap:6px;}
  .rx-med-strength-timing-row{display:grid;grid-template-columns:1fr;gap:6px;}
  .rx-med-dose-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:4px;}
  .rx-med-days-del-row{display:flex;align-items:center;justify-content:space-between;gap:8px;}
  .rx-med-days-input{max-width:80px;}
  .rx-dose-mini-label{font-size:8px;font-weight:700;color:var(--gray-400);text-transform:uppercase;letter-spacing:0.06em;text-align:center;margin-bottom:2px;}
  .rx-col-strength-desktop{display:block!important;}
  .rx-col-dose-desktop,.rx-col-dose-tablet{display:block!important;}
  .rx-add-med-btn{width:100%;justify-content:center!important;border-radius:999px!important;}
  .rx-vitals-toggle{font-size:12px;padding:9px 14px;}
  .rx-vitals-inline-grid{grid-template-columns:1fr 1fr!important;gap:6px!important;}
  .rx-bottom-bar{padding:8px 12px;display:flex;flex-wrap:nowrap;align-items:center;justify-content:space-between;gap:8px;}
  .rx-bottom-hint{font-size:10.5px;flex:1;text-align:center;color:var(--gray-500);}
  .rx-bottom-left{width:auto;}
  .rx-bottom-right{width:auto;}
  .rx-finalise-btn{flex:none;justify-content:center!important;padding:8px 14px;min-height:36px;border-radius:12px;}
  .rx-upload-label{padding:20px 12px!important;}
  .rx-notes-textarea{height:68px!important;}
  .rx-followup-input{max-width:100%!important;}
}

@media (max-width: 380px) {
  .rx-patient-name{max-width:26vw;font-size:12px;}
  .rx-tab-btn{padding:4px 7px!important;font-size:10px!important;flex-shrink:0!important;white-space:nowrap!important;}
  .rx-topbar-right{margin-right:8px!important;gap:8px!important;}
}
`;

function GlobalCSS() {
  useEffect(() => {
    const id = "opd-v5-fresh";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id; el.textContent = DESIGN_CSS;
    document.head.appendChild(el);
  }, []);
  return null;
}

/* ─── Tokens ─────────────────────────────────────────────── */
const card = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-xl)",
  boxShadow: "var(--sh-sm)",
};

/* ─── Button ─────────────────────────────────────────────── */
const VARIANTS = {
  primary: { base: { background:"var(--blue-600)", color:"#fff", border:"1.5px solid transparent", boxShadow:"var(--sh-blue)" }, hover: { background:"var(--blue-700)" } },
  success: { base: { background:"var(--green-600)", color:"#fff", border:"1.5px solid transparent" }, hover: { background:"var(--green-700)" } },
  danger:  { base: { background:"linear-gradient(180deg, var(--red-600), var(--red-700))", color:"#fff", border:"1.5px solid transparent", boxShadow:"0 6px 18px rgba(220,38,38,0.24)" }, hover: { background:"linear-gradient(180deg, var(--red-700), #991b1b)" } },
  warn:    { base: { background:"var(--amber-50)", color:"var(--amber-700)", border:"1.5px solid var(--amber-200)" }, hover: { background:"var(--amber-100)" } },
  ghost:   { base: { background:"var(--white)", color:"var(--gray-700)", border:"1.5px solid var(--gray-200)", boxShadow:"var(--sh-xs)" }, hover: { background:"var(--gray-100)", borderColor:"var(--gray-300)" } },
  view:    { base: { background:"var(--blue-50)", color:"var(--blue-700)", border:"1.5px solid var(--blue-200)" }, hover: { background:"var(--blue-100)" } },
  violet:  { base: { background:"var(--violet-600)", color:"#fff", border:"1.5px solid transparent" }, hover: { background:"var(--violet-700)" } },
  soft:    { base: { background:"var(--blue-50)", color:"var(--blue-700)", border:"1.5px solid var(--blue-200)" }, hover: { background:"var(--blue-100)" } },
};

function Btn({ children, variant="primary", onClick, disabled, type="button", size="md", style:sx={}, title, dataCompact }) {
  const [hov, setHov] = useState(false);
  const sizes = {
    xs: { padding:"3px 4px", fontSize:12, gap:3, borderRadius:"var(--r-sm)" },
    sm: { padding:"4px 6px", fontSize:12, gap:4, borderRadius:"var(--r-md)" },
    md: { padding:"7px 15px", fontSize:13, gap:5, borderRadius:"var(--r-lg)" },
    lg: { padding:"9px 20px", fontSize:13.5, gap:6, borderRadius:"var(--r-lg)" },
  };
  const v = VARIANTS[variant] || VARIANTS.ghost;
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} data-compact={dataCompact?"true":undefined}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display:"inline-flex", alignItems:"center", justifyContent:"center",
        fontFamily:"var(--font)", fontWeight:600, cursor:disabled?"not-allowed":"pointer",
        whiteSpace:"nowrap", opacity:disabled?0.5:1, letterSpacing:"-0.01em",
        transition:"all 0.14s var(--ease)",
        transform: hov&&!disabled?"translateY(-1px)":"none",
        ...sizes[size], ...v.base, ...(hov&&!disabled?v.hover:{}), ...sx }}>
      {children}
    </button>
  );
}

/* ─── Status Badges ─────────────────────────────────────── */
const STATUS_MAP = {
  COMPLETED:         { bg:"var(--green-50)",   border:"var(--green-200)",   text:"var(--green-700)",   dot:"var(--green-500)",   label:"Completed",       pulse:false },
  PAYMENT_COMPLETED: { bg:"var(--green-50)",   border:"var(--green-200)",   text:"var(--green-700)",   dot:"var(--green-500)",   label:"Paid",             pulse:false },
  PAYMENT_PENDING:   { bg:"var(--red-50)",     border:"var(--red-200)",     text:"var(--red-700)",     dot:"var(--red-500)",     label:"Pending Payment",  pulse:false },
  CONSULTING:        { bg:"var(--blue-50)",    border:"var(--blue-200)",    text:"var(--blue-700)",    dot:"var(--blue-500)",    label:"Consulting",       pulse:true  },
  ARRIVED:           { bg:"var(--violet-50)",  border:"var(--violet-200)",  text:"var(--violet-700)",  dot:"var(--violet-500)",  label:"Arrived",          pulse:false },
  WALK_IN_PENDING:   { bg:"var(--amber-50)",   border:"var(--amber-200)",   text:"var(--amber-700)",   dot:"var(--amber-500)",   label:"Walk-in",          pulse:false },
  WAITING:           { bg:"var(--amber-50)",   border:"var(--amber-200)",   text:"var(--amber-700)",   dot:"var(--amber-500)",   label:"Waiting",          pulse:false },
  BOOKED:            { bg:"#f0feff",           border:"#bae6fd",            text:"#0369a1",            dot:"#0ea5e9",            label:"Scheduled",        pulse:false },
  NO_SHOW:           { bg:"var(--red-50)",     border:"var(--red-200)",     text:"var(--red-700)",     dot:"var(--red-500)",     label:"No Show",          pulse:false },
};

function StatusBadge({ status }) {
  const t = STATUS_MAP[status] || { bg:"var(--gray-100)", border:"var(--gray-200)", text:"var(--gray-500)", dot:"var(--gray-400)", label:status||"—", pulse:false };
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5,
      padding:"3px 9px", borderRadius:"var(--r-full)",
      fontSize:11, fontWeight:600, background:t.bg, color:t.text, border:`1px solid ${t.border}` }}>
      <span style={{ position:"relative", width:6, height:6, borderRadius:"50%", background:t.dot, display:"inline-block", flexShrink:0 }}>
        {t.pulse && <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:t.dot, animation:"pulse-ring 1.6s ease-out infinite" }} />}
      </span>
      {t.label}
    </span>
  );
}

function SessionBadge({ status }) {
  const map = {
    RUNNING:     { bg:"var(--green-50)",  border:"var(--green-200)",  text:"var(--green-700)",  dot:"var(--green-500)",  label:"Live",    pulse:true  },
    PAUSED:      { bg:"var(--amber-50)",  border:"var(--amber-200)",  text:"var(--amber-700)",  dot:"var(--amber-500)",  label:"Paused",  pulse:false },
    ENDED:       { bg:"var(--red-50)",    border:"var(--red-200)",    text:"var(--red-700)",    dot:"var(--red-500)",    label:"Ended",   pulse:false },
    NOT_STARTED: { bg:"var(--gray-100)",  border:"var(--gray-200)",   text:"var(--gray-600)",   dot:"var(--gray-400)",   label:"Standby", pulse:false },
  };
  const t = map[status] || map.NOT_STARTED;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5,
      padding:"4px 10px", borderRadius:"var(--r-full)",
      fontSize:11.5, fontWeight:600, background:t.bg, color:t.text, border:`1px solid ${t.border}` }}>
      <span style={{ position:"relative", width:7, height:7, borderRadius:"50%", background:t.dot, flexShrink:0 }}>
        {t.pulse && <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:t.dot, animation:"pulse-ring 1.6s ease-out infinite" }} />}
      </span>
      {t.label}
    </span>
  );
}

/* ─── Stat Card ─────────────────────────────────────────── */
function StatCard({ label:lbl, value, icon:Icon, accent, borderRight=true, borderBottom=false, delay=0 }) {
  return (
    <div className="opd-stat-card" style={{
      padding:"16px 20px",
      borderRight: borderRight ? `1px solid var(--gray-200)` : "none",
      borderBottom: borderBottom ? `1px solid var(--gray-200)` : "none",
      animation:`rise 0.35s var(--ease) ${delay}s both`,
      background:"var(--surface)",
    }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontSize:10, fontWeight:700, color:"var(--gray-400)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>{lbl}</div>
          <div style={{ fontSize:28, fontWeight:800, color:"var(--gray-900)", fontFamily:"var(--mono)", letterSpacing:"-1.5px", lineHeight:1 }}>{value ?? 0}</div>
        </div>
        <div style={{ width:40, height:40, borderRadius:"var(--r-xl)", background:`${accent}14`,
          display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${accent}20`, flexShrink:0 }}>
          {Icon && <Icon size={20} color={accent} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

/* ─── Overlay ────────────────────────────────────────────── */
function Overlay({ children, onBackdropClick, maxWidth = 500 }) {
  return (
    <div className="opd-overlay" onClick={onBackdropClick}>
      <div onClick={e => e.stopPropagation()}
        style={{ maxHeight:"92vh", overflowY:"auto", borderRadius:"var(--r-2xl)", width:"100%", maxWidth }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Full Page View ─────────────────────────────────────── */
function FullPageView({ children, onClose }) {
  return (
    <div className="opd-fullpage">
      <button onClick={onClose}
        style={{ position:"fixed", top:16, right:16, zIndex:1002,
          width:38, height:38, borderRadius:"var(--r-xl)",
          background:"var(--white)", border:"1.5px solid var(--gray-200)",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"var(--sh-md)", color:"var(--gray-600)", transition:"all 0.15s" }}
        onMouseEnter={e => { e.currentTarget.style.background="var(--red-50)"; e.currentTarget.style.color="var(--red-600)"; e.currentTarget.style.borderColor="var(--red-200)"; }}
        onMouseLeave={e => { e.currentTarget.style.background="var(--white)"; e.currentTarget.style.color="var(--gray-600)"; e.currentTarget.style.borderColor="var(--gray-200)"; }}>
        <X size={17} strokeWidth={2.5} />
      </button>
      {children}
    </div>
  );
}

function WalkInSuccessModal({ onClose, data }) {
  return (
    <div className="anim-scale" style={{ ...card, padding:24, maxWidth:460, margin:"0 auto", textAlign:"center" }}>
      <div style={{ width:54, height:54, borderRadius:"var(--r-xl)", background:"var(--green-50)", border:"1px solid var(--green-200)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
        <CheckCircle2 size={26} color="var(--green-600)" strokeWidth={2.3} />
      </div>
      <h2 style={{ fontSize:20, fontWeight:800, color:"var(--gray-900)", marginBottom:6 }}>Walk-in completed</h2>
      <p style={{ fontSize:13.5, color:"var(--gray-600)", marginBottom:18, lineHeight:1.6 }}>Patient details are saved. {data?.status ? data.status + "." : "Vitals step complete."}</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:10, marginBottom:18 }}>
        <div style={{ padding:14, borderRadius:"var(--r-lg)", background:"var(--gray-50)", border:"1px solid var(--border)" }}>
          <div style={{ fontSize:11.5, color:"var(--gray-500)", fontWeight:700, marginBottom:6 }}>Patient</div>
          <div style={{ fontSize:16, fontWeight:700, color:"var(--gray-900)", overflowWrap:"anywhere" }}>{data?.name || "Patient"}</div>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18 }}>
        <div style={{ padding:14, borderRadius:"var(--r-lg)", background:"var(--blue-50)", border:"1px solid var(--blue-100)" }}>
          <div style={{ fontSize:11.5, color:"var(--gray-500)", fontWeight:700, marginBottom:6 }}>Token</div>
          <div style={{ fontSize:18, fontWeight:800, color:"var(--blue-700)", fontFamily:"var(--mono)" }}>{data?.token || "-"}</div>
        </div>
        <div style={{ padding:14, borderRadius:"var(--r-lg)", background:"var(--gray-50)", border:"1px solid var(--border)" }}>
          <div style={{ fontSize:11.5, color:"var(--gray-500)", fontWeight:700, marginBottom:6 }}>Appointment ID</div>
          <div style={{ fontSize:16, fontWeight:700, color:"var(--gray-900)", fontFamily:"var(--mono)" }}>{data?.appointmentId || "-"}</div>
        </div>
      </div>
      <Btn variant="primary" onClick={onClose}>Done</Btn>
    </div>
  );
}

function EndSessionModal({ onClose, onConfirm, done, total }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdownActive, setCountdownActive] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10);
  const intervalRef = useRef(null);
  const pending = Math.max((Number(total) || 0) - (Number(done) || 0), 0);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  const performEnd = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to end session.");
    } finally {
      setIsSubmitting(false);
      setCountdownActive(false);
      setSecondsLeft(10);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    }
  };

  const startCountdown = () => {
    if (countdownActive) return;
    setCountdownActive(true);
    setSecondsLeft(10);
    intervalRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          performEnd();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const cancelCountdown = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCountdownActive(false);
    setSecondsLeft(10);
  };

  const progressPct = Math.round(((10 - secondsLeft) / 10) * 100);

  return (
    <div className="anim-scale" style={{ ...card, padding:24, maxWidth:500, margin:"0 auto" }}>
      <div style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:18 }}>
        <div style={{ width:48, height:48, borderRadius:12, background:"#fef2f2", border:"1px solid #fecaca", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <AlertTriangle size={22} color="#991b1b" strokeWidth={2.3} />
        </div>
        <div style={{ flex:1 }}>
          <h2 style={{ fontSize:18, fontWeight:800, color:"var(--gray-900)", marginBottom:8 }}>End OPD session?</h2>
          <p style={{ fontSize:13, color:"var(--gray-500)", lineHeight:1.5, marginBottom:8 }}>Completed {done || 0} of {total || 0} patients. {pending > 0 ? `${pending} patient(s) are still pending.` : "All patients are completed."}</p>
          {countdownActive && (
            <div style={{ marginTop:6 }}>
              <div style={{ height:8, background:"rgba(0,0,0,0.06)", borderRadius:999, overflow:"hidden" }}>
                <div style={{ height:8, background:"linear-gradient(90deg,#ef4444,#b91c1c)", width:`${progressPct}%`, transition:"width 400ms linear" }} />
              </div>
              <div style={{ marginTop:8, fontSize:13, color:"#b91c1c", fontWeight:700 }}>
                Ending in {secondsLeft}s - you can cancel now
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end", gap:10, alignItems:"center" }}>
        <Btn variant="ghost" onClick={countdownActive ? cancelCountdown : onClose} disabled={isSubmitting}>
          {countdownActive ? "Cancel" : "Cancel"}
        </Btn>

        {!countdownActive ? (
          <Btn variant="danger" onClick={startCountdown} disabled={isSubmitting} style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
            <Square size={14} /> End Session
          </Btn>
        ) : (
          <Btn variant="danger" onClick={() => {}} disabled style={{ display:"inline-flex", alignItems:"center", gap:8, opacity:0.95 }}>
            <RefreshCw size={14} className="anim-spin" /> Ending... {secondsLeft}s
          </Btn>
        )}
      </div>
    </div>
  );
}

/* ─── Prescription Pad ───────────────────────────────────── */
function VitalsContent({ appointmentVital, isLoadingVital, fetchVitals, onOpenVitalsModal }) {
  const dynamicMeasurements = Array.isArray(appointmentVital)
    ? appointmentVital
    : Array.isArray(appointmentVital?.measurements) ? appointmentVital.measurements : null;
  const hasVitals = dynamicMeasurements ? dynamicMeasurements.length > 0 : Boolean(appointmentVital);
  const recordedAt = dynamicMeasurements?.[0]?.recordedAt || appointmentVital?.recordedAt || appointmentVital?.updatedAt;

      return (
        <div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, gap:10, flexWrap:"wrap" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <HeartPulse size={13} color="var(--blue-600)" strokeWidth={2} />
              <span style={{ fontSize:10.5, fontWeight:700, color:"var(--gray-500)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Visit Vitals</span>
            </div>
            <button type="button" onClick={onOpenVitalsModal}
              style={{ padding:"6px 12px", borderRadius:"999px", border:"1px solid var(--blue-200)", background:"var(--blue-50)", color:"var(--blue-700)", fontWeight:700, cursor:"pointer", fontSize:11, whiteSpace:"nowrap" }}>
              {hasVitals ? "Add / edit measurements" : "Add measurements"}
            </button>
            <button type="button" onClick={fetchVitals} disabled={isLoadingVital}
              style={{ width:28, height:28, border:"1.5px solid var(--gray-200)", borderRadius:"var(--r-md)", background:"var(--white)", cursor:isLoadingVital?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--gray-500)", transition:"all 0.14s" }}
              onMouseEnter={e => !isLoadingVital && (e.currentTarget.style.background="var(--blue-50)")}
              onMouseLeave={e => e.currentTarget.style.background="var(--white)"}>
              <RefreshCw size={12} strokeWidth={2} style={isLoadingVital ? { animation:"spin 0.65s linear infinite" } : {}} />
            </button>
          </div>

          {isLoadingVital ? (
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ width:22, height:22, border:"2.5px solid var(--blue-100)", borderTopColor:"var(--blue-500)", borderRadius:"50%", animation:"spin 0.65s linear infinite", margin:"0 auto 8px" }} />
              <div style={{ fontSize:11.5, color:"var(--gray-400)" }}>Loading vitals...</div>
            </div>
          ) : !hasVitals ? (
            <div style={{ textAlign:"center", padding:"20px 14px", background:"var(--gray-50)", borderRadius:"var(--r-xl)", border:"1.5px dashed var(--gray-200)" }}>
              <Activity size={26} color="var(--gray-300)" strokeWidth={1.5} style={{ display:"block", margin:"0 auto 8px" }} />
              <div style={{ fontSize:12.5, fontWeight:700, color:"var(--gray-700)", marginBottom:3 }}>No Vitals Recorded</div>
              <div style={{ fontSize:11, color:"var(--gray-400)", lineHeight:1.5, marginBottom:14 }}>Not entered at check-in</div>
              <button type="button" onClick={onOpenVitalsModal}
                style={{ padding:"7px 16px", borderRadius:"var(--r-lg)", background:"var(--blue-600)", color:"#fff", border:"none", fontSize:12, fontWeight:600, cursor:"pointer", boxShadow:"var(--sh-blue)", transition:"background 0.14s" }}
                onMouseEnter={e => e.currentTarget.style.background="var(--blue-700)"}
                onMouseLeave={e => e.currentTarget.style.background="var(--blue-600)"}>
                Add Vitals
              </button>
            </div>
          ) : (
            <div>
              {recordedAt && <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 11px", background:"var(--blue-50)", borderRadius:"var(--r-lg)", marginBottom:12, border:"1px solid var(--blue-100)" }}>
                <div><div style={{ fontSize:9.5, fontWeight:700, color:"var(--blue-600)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Recorded</div><div style={{ fontFamily:"var(--mono)", fontSize:11.5, fontWeight:700, color:"var(--blue-800)", marginTop:1 }}>{new Date(recordedAt).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",timeZone:"Asia/Kolkata"})}</div></div>
                <span style={{ background:"var(--green-600)", color:"#fff", fontSize:8.5, fontWeight:800, padding:"2px 7px", borderRadius:"var(--r-sm)", textTransform:"uppercase" }}>This Visit</span>
              </div>}
              {dynamicMeasurements ? <div className="rx-vitals-inline-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7 }}>
                {dynamicMeasurements.map((measurement) => <VitalCell key={measurement.id ?? `${measurement.measurementKey}-${measurement.name}`} label={measurement.name ?? measurement.measurementName ?? "Measurement"} value={formatObservationValue(measurement.value)} unit={measurement.unit || ""} color="var(--blue-600)" icon={Activity} />)}
              </div> : <div className="rx-vitals-inline-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7 }}>
                {appointmentVital.bloodPressureSystolic != null && <VitalCell label="Blood Pressure" value={`${appointmentVital.bloodPressureSystolic}/${appointmentVital.bloodPressureDiastolic ?? "—"}`} unit="mmHg" icon={HeartPulse} />}
                {appointmentVital.heartRate != null && <VitalCell label="Heart Rate" value={appointmentVital.heartRate} unit="bpm" icon={Activity} />}
                {appointmentVital.temperature != null && <VitalCell label="Temperature" value={appointmentVital.temperature} unit="°C" icon={Thermometer} />}
                {appointmentVital.respiratoryRate != null && <VitalCell label="Respiratory Rate" value={appointmentVital.respiratoryRate} unit="br/min" icon={Wind} />}
                {appointmentVital.spO2 != null && <VitalCell label="SpO₂" value={appointmentVital.spO2} unit="%" icon={Droplets} />}
                {appointmentVital.bloodSugar != null && <VitalCell label="Blood Sugar" value={appointmentVital.bloodSugar} unit="mg/dL" icon={FlaskConical} />}
                {appointmentVital.weight != null && <VitalCell label="Weight" value={appointmentVital.weight} unit="kg" icon={Scale} />}
                {appointmentVital.height != null && <VitalCell label="Height" value={appointmentVital.height} unit="cm" icon={Ruler} />}
                {appointmentVital.bmi != null && <VitalCell label="BMI" value={appointmentVital.bmi} unit="kg/m²" icon={Gauge} />}
              </div>}
            </div>
          )}
        </div>
      );
    }

  function MobileCardRow({ row, i, updateRow, removeRow, showRemove, suggestions, activeRowIdx, selectDrug, deriveForm, getUnit, renderSuggestionList, hasExactSuggestion, dropdownInputRefs, setActiveRowIdx }) {
      return (
        <div className="rx-med-row">
          <div className="rx-med-name-form-row">
            <div style={{ position:"relative" }}>
              <input data-dropdown-anchor="true" data-dropdown-row={i} ref={el => {
                  if (el) {
                    const visible = el.offsetParent !== null && getComputedStyle(el).display !== "none" && getComputedStyle(el).visibility !== "hidden";
                    if (visible) dropdownInputRefs.current[i] = el;
                  } else {
                    if (dropdownInputRefs.current[i]) delete dropdownInputRefs.current[i];
                  }
                }} className="rx-input" placeholder="Search medicine…" value={row.name} onChange={e => updateRow(i,"name",e.target.value)} onFocus={() => setActiveRowIdx(i)} autoComplete="off" style={{ fontSize:12.5 }} />
            </div>
            <input className="rx-input" value={row.form} onChange={e => updateRow(i,"form",e.target.value)} placeholder="Form" style={{ fontSize:11 }} />
          </div>

          <div className="rx-med-strength-timing-row">
            <input className="rx-input" placeholder="Strength (mg/ml)" value={row.strength} onChange={e => updateRow(i,"strength",e.target.value)} style={{ fontSize:11 }} />
          </div>

          <div className="rx-med-dose-row" style={{ marginTop:10 }}>
            {['morning','afternoon','evening','night'].map(slot => (
              <div key={slot} style={{ display:'grid', gap:4 }}>
                <span className="rx-dose-mini-label">{slot.slice(0,1).toUpperCase()}</span>
                <input className="rx-input" placeholder="0" value={row[slot]} onChange={e => updateRow(i,slot,e.target.value)} style={{ textAlign:'center', fontSize:12.5, padding:'8px 10px' }} />
              </div>
            ))}
          </div>
          <div className="rx-med-days-del-row" style={{ marginTop:10 }}>
            <input className="rx-input" placeholder="Days" value={row.duration} onChange={e => updateRow(i,'duration',e.target.value)} style={{ width: '100px', textAlign:'center', fontSize:11 }} />
            <select className="rx-select" value={row.timing||'After Food'} onChange={e => updateRow(i,'timing',e.target.value)} style={{ fontSize:11, minWidth:'140px' }}>
              <option>After Food</option><option>Before Food</option><option>With Food</option><option>Empty Stomach</option><option>At Bedtime</option><option>SOS / As needed</option>
            </select>
          </div>
          {showRemove && (
              <button type="button" onClick={() => removeRow(i)} style={{ padding:"5px 12px", borderRadius:"var(--r-md)", background:"var(--red-50)", border:"1px solid var(--red-200)", color:"var(--red-600)", fontSize:11, fontWeight:600, cursor:"pointer", transition:"all 0.13s", display:"flex", alignItems:"center", gap:4 }} onMouseEnter={e => { e.currentTarget.style.background="var(--red-600)"; e.currentTarget.style.color="#fff"; }} onMouseLeave={e => { e.currentTarget.style.background="var(--red-50)"; e.currentTarget.style.color="var(--red-600)"; }}>
                <X size={11} strokeWidth={2.5} /> Remove
              </button>
            )}
        
        </div>
      );
    }

  function DesktopMedRow({ row, i, updateRow, removeRow, showRemove, suggestions, activeRowIdx, selectDrug, deriveForm, getUnit, renderSuggestionList, hasExactSuggestion, dropdownInputRefs, setActiveRowIdx }) {
      return (
        <div className="rx-med-row" onMouseEnter={e => e.currentTarget.style.background="var(--gray-50)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
          <div>
            <div style={{ display:"flex", gap:5 }}>
              <div style={{ flex:1, position:"relative" }}>
                <input data-dropdown-anchor="true" data-dropdown-row={i} ref={el => {
                    if (el) {
                      const visible = el.offsetParent !== null && getComputedStyle(el).display !== "none" && getComputedStyle(el).visibility !== "hidden";
                      if (visible) dropdownInputRefs.current[i] = el;
                    } else {
                      if (dropdownInputRefs.current[i]) delete dropdownInputRefs.current[i];
                    }
                  }} className="rx-input" placeholder="Search medicine…" value={row.name} onChange={e => updateRow(i,"name",e.target.value)} onFocus={() => setActiveRowIdx(i)} autoComplete="off" />
              </div>
              <input className="rx-input" value={row.form} onChange={e => updateRow(i,"form",e.target.value)} placeholder="Form" style={{ width:74, fontSize:11 }} />
            </div>
          </div>
          <input className="rx-input rx-col-strength-desktop" placeholder="mg/ml" value={row.strength} onChange={e => updateRow(i,"strength",e.target.value)} style={{ fontSize:11 }} />
          {["morning","afternoon","evening","night"].map(slot => (
            <div key={slot} className="rx-col-dose-desktop">
              <input className="rx-input" placeholder="0" value={row[slot]} onChange={e => updateRow(i,slot,e.target.value)} style={{ textAlign:"center", fontSize:12.5, fontWeight:600 }} />
              <div style={{ fontSize:8, color:"var(--gray-400)", textAlign:"center", marginTop:1 }}>{getUnit(row.form)}</div>
            </div>
          ))}
          <input className="rx-input" placeholder="7" value={row.duration} onChange={e => updateRow(i,"duration",e.target.value)} style={{ textAlign:"center" }} />
          <select className="rx-select rx-med-col-timing" value={row.timing||"After Food"} onChange={e => updateRow(i,"timing",e.target.value)} style={{ fontSize:11 }}>
            <option>After Food</option><option>Before Food</option><option>With Food</option><option>Empty Stomach</option><option>At Bedtime</option><option>SOS / As needed</option>
          </select>
          {showRemove ? (
            <button type="button" onClick={() => removeRow(i)} style={{ width:26, height:26, border:"none", background:"var(--red-50)", borderRadius:"var(--r-md)", color:"var(--red-500)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.13s" }} onMouseEnter={e => { e.currentTarget.style.background="var(--red-600)"; e.currentTarget.style.color="#fff"; }} onMouseLeave={e => { e.currentTarget.style.background="var(--red-50)"; e.currentTarget.style.color="var(--red-500)"; }}>
              <X size={12} strokeWidth={2.5} />
            </button>
          ) : <div />}
        </div>
      );
    }

  function PrescriptionPad({ activePatient, patientHistory, vitalsHistory, currentAppointmentVital, appointmentStatusMap={}, doctorName, sessionInfo, clinicId, onClose, onSave, onOpenVitalsModal }) {
    const navigate = useNavigate();
      const { auth } = useAuth();
      const CLINIC_ID = auth?.clinic?.id || null;
      const [mode, setMode] = useState("DIGITAL");
      const [padTab, setPadTab] = useState("create");
      const [diagnosis, setDiagnosis] = useState("");
      const [notes, setNotes] = useState("");
      const [nextFollowUpDate, setNextFollowUpDate] = useState("");
      const [medicineRows, setMedicineRows] = useState([{ name:"", form:"", strength:"", morning:"", afternoon:"", evening:"", night:"", duration:"", timing:"After Food" }]);
      const [uploading, setUploading] = useState(false);
      const [showSaveConfirm, setShowSaveConfirm] = useState(false);
      const [saveProgress, setSaveProgress] = useState({ stage: "idle", completed: 0 });
      const [savedCountdown, setSavedCountdown] = useState(5);
      const [saveStartedAt, setSaveStartedAt] = useState(null);
      const [saveElapsedMs, setSaveElapsedMs] = useState(0);
      const [visibleSaveProgress, setVisibleSaveProgress] = useState(0);
      const [savedIndicator, setSavedIndicator] = useState(false);
      const [imageFile, setImageFile] = useState(null);
      const [imageError, setImageError] = useState("");
      const [suggestions, setSuggestions] = useState([]);
      const [activeRowIdx, setActiveRowIdx] = useState(null);
      const [dropdownPosition, setDropdownPosition] = useState(null);
      const dropdownInputRefs = useRef({});
      const [appointmentVital, setAppointmentVital] = useState(null);
      const [isLoadingVital, setIsLoadingVital] = useState(false);
      const [vitalsOpen, setVitalsOpen] = useState(false);
      const [clinicalDocuments, setClinicalDocuments] = useState([]);
      const diagRef = useRef(null);

      useEffect(() => {
        let cancelled = false;
        if (!activePatient?.id || !clinicId) return undefined;
        axios.get(buildApiUrl(`clinical-documents/patient/${activePatient.patientId}`), { headers:{ "X-Clinic-Id":clinicId } })
          .then(response => { if (!cancelled) setClinicalDocuments(Array.isArray(response.data) ? response.data : []); })
          .catch(() => { if (!cancelled) setClinicalDocuments([]); });
        return () => { cancelled = true; };
      }, [activePatient?.id, activePatient?.patientId, clinicId]);

      const resolveAge = p => {
        const raw = p?.age??p?.patientAge??p?.ageYears;
        if (raw!=null&&raw!=="") return Number(raw);
        if (p?.dob) { const d=new Date(p.dob); if (!isNaN(d)) return Math.floor((Date.now()-d)/31557600000); }
        return null;
      };
      const resolveGender = p => {
        const raw = String(p?.gender||p?.patientGender||"").trim().toLowerCase();
        if (!raw) return "—";
        if (["m","male"].includes(raw)) return "Male";
        if (["f","female"].includes(raw)) return "Female";
        return raw.charAt(0).toUpperCase()+raw.slice(1);
      };
      const resolveHonorific = p => {
        const raw = String(p?.gender||p?.patientGender||"").trim().toLowerCase();
        if (["m","male"].includes(raw)) return "Mr.";
        if (["f","female"].includes(raw)) return "Ms/Mrs";
        return "";
      };
      const genCode = did => {
        const n=new Date();
        return `${String(n.getFullYear()).slice(-2)}${String(n.getMonth()+1).padStart(2,"0")}${String(n.getDate()).padStart(2,"0")}-${did||"DR"}-${String(Math.floor(Math.random()*10000)).padStart(4,"0")}`;
      };
      const deriveForm = drug => {
        const s = ((drug.packSizeLabel||"") + (drug.drugName||"")).toLowerCase();
        if (/inject|ampoule|vial/.test(s)) return "Injection";
        if (/drop|drops?\b/.test(s)) return "Drops";
        if (/cream|ointment|gel/.test(s)) return "Ointment";
        if (/inhaler|spray|puff|puffs/.test(s)) return "Inhaler";
        if (/capsule|capsules|caps\b/.test(s)) return "Capsule";
        if (/syrup|suspension|oral\s*liquid|ml\b/.test(s)) return "Syrup";
        if (/patch|patches/.test(s)) return "Patch";
        if (/sachet|powder|powders/.test(s)) return "Powder";
        if (/lozenge|piece|pieces/.test(s)) return "Lozenge";
        if (/tablet|tab|tabs|tab\b|tabs\b|nos\b/.test(s)) return "Tablet";
        return "Tablet";
      };
      const resolveForm = drug => {
        if (drug?.form && String(drug.form).trim() !== "") {
          return String(drug.form).trim();
        }
        return deriveForm(drug);
      };
      const getUnit = form => ({ Tablet:"tabs", Capsule:"caps", Syrup:"ml", Drops:"drops", Injection:"ml", Inhaler:"puffs", Ointment:"g", Patch:"patches", Powder:"g", Lozenge:"lozenges" }[form]||"dose");
      const [savingCustomDrug, setSavingCustomDrug] = useState(false);

      const hasExactSuggestion = name => {
        if (!name) return false;
        return suggestions.some(drug => String(drug?.drugName || "").trim().toLowerCase() === name.toLowerCase());
      };

      const saveCustomMedicine = async drugName => {
        if (!drugName) return;
        if (!sessionInfo?.doctorId) {
          toast.error("Doctor context missing. Cannot save custom medicine.");
          return;
        }
        const row = medicineRows[activeRowIdx];
        const payload = {
          drugName: drugName.trim(),
          form: String(row?.form||"").trim() || deriveForm({ drugName }),
          packSizeLabel: "",
          genericName: "",
        };
        setSavingCustomDrug(true);
        try {
          const res = await fetch(buildApiUrl(`drugs/add-new`), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Doctor-Id": String(sessionInfo.doctorId),
              "X-Clinic-Id": CLINIC_ID || "",
            },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            const errorBody = await res.text();
            throw new Error(errorBody || "Unable to save custom medicine.");
          }
          const saved = await res.json();
          if (!saved || !saved.id) {
            throw new Error("Unexpected response from custom medicine save.");
          }
          setMedicineRows(r => {
            const next = [...r];
            next[activeRowIdx] = {
              ...next[activeRowIdx],
              name: saved.drugName || drugName,
              form: saved.form || deriveForm({ drugName }),
            };
            return next;
          });
          setSuggestions(prev => [saved, ...(prev || [])]);
          setActiveRowIdx(null);
          toast.success(`Saved custom medicine: ${saved.drugName}`);
        } catch (err) {
          console.error(err);
          toast.error(`Could not save custom medicine: ${err.message}`);
        } finally {
          setSavingCustomDrug(false);
        }
      };

      const renderSuggestionLabel = drug => {
        const formLabel = resolveForm(drug);
        if (drug?.isCustom) {
          if (drug.addedByDrId && drug.addedByDrId === sessionInfo?.doctorId) {

            return `${formLabel} • Added by you`;
          }
          return drug.addedByDrId ? `${formLabel} • Dr. #${drug.addedByDrId}` : formLabel;

        }
        return formLabel;
      };

      const renderSuggestionList = (row, i) => {
        const query = String(row?.name || "").trim();
        const showSaveOption = query.length > 1 && !hasExactSuggestion(query);
        return (
          <>
            {suggestions.map(drug => (
              <div key={drug.id} onClick={() => selectDrug(drug)} style={{ padding:"9px 13px", cursor:"pointer", borderBottom:"1px solid var(--gray-100)", display:"flex", justifyContent:"space-between", alignItems:"center", transition:"background 0.1s" }} onMouseEnter={e => e.currentTarget.style.background="var(--blue-50)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                <div>
                  <div style={{ fontWeight:600, fontSize:12.5, color:"var(--gray-900)" }}>{drug.drugName}</div>
                  {drug.genericName && drug.genericName !== drug.drugName && <div style={{ fontSize:10.5, color:"var(--gray-400)", marginTop:1 }}>{drug.genericName}</div>}
                </div>
                <span style={{ fontSize:10, background:"var(--violet-50)", color:"var(--violet-700)", padding:"2px 7px", borderRadius:"var(--r-full)", fontWeight:700, border:"1px solid var(--violet-200)", marginLeft:9, flexShrink:0 }}>{renderSuggestionLabel(drug)}</span>
              </div>
            ))}
            {showSaveOption && (
              <div onClick={() => saveCustomMedicine(query)} style={{ padding:"9px 13px", cursor:savingCustomDrug?"not-allowed":"pointer", borderBottom:"1px solid var(--gray-100)", display:"flex", justifyContent:"space-between", alignItems:"center", transition:"background 0.1s", background:savingCustomDrug?"var(--gray-100)":"transparent" }} onMouseEnter={e => !savingCustomDrug && (e.currentTarget.style.background="var(--blue-50)")} onMouseLeave={e => !savingCustomDrug && (e.currentTarget.style.background="transparent")}> 
                <div>
                  <div style={{ fontWeight:700, fontSize:12.5, color:"var(--gray-900)" }}>Save custom medicine</div>
                  <div style={{ fontSize:10.5, color:"var(--gray-500)", marginTop:1 }}>{query}</div>
                </div>
                <span style={{ fontSize:10, background:"var(--blue-50)", color:"var(--blue-700)", padding:"2px 7px", borderRadius:"var(--r-full)", fontWeight:700, border:"1px solid var(--blue-200)", marginLeft:9, flexShrink:0 }}>{savingCustomDrug?"Saving...":"Save"}</span>
              </div>
            )}
          </>
        );
      };

      const fetchVitals = useCallback(async () => {
        if (!activePatient?.id) return;
        setIsLoadingVital(true);
        try {
          const rows = await vitalMeasurementService.getAppointmentMeasurements(activePatient.id, sessionInfo?.doctorId, CLINIC_ID);
          setAppointmentVital(groupMeasurementHistory(rows)[0] || null);
        } catch {
          setAppointmentVital(null);
        } finally {
          setIsLoadingVital(false);
        }
      },[activePatient?.id,CLINIC_ID,sessionInfo?.doctorId]);

      const refreshDropdownPosition = useCallback(() => {
        if (activeRowIdx === null) {
          setDropdownPosition(null);
          return;
        }
        const focused = document.activeElement;
        const input = focused?.dataset?.dropdownAnchor === "true"
          ? focused
          : dropdownInputRefs.current[activeRowIdx];
        if (!input) {
          setDropdownPosition(null);
          return;
        }
        const rect = input.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      },[activeRowIdx]);

      useEffect(() => { fetchVitals(); },[fetchVitals]);
      useEffect(() => { setAppointmentVital(currentAppointmentVital); }, [currentAppointmentVital]);
      useEffect(() => {
        if (activeRowIdx !== null) {
          refreshDropdownPosition();
          window.addEventListener("scroll", refreshDropdownPosition, true);
          window.addEventListener("resize", refreshDropdownPosition);
          return () => {
            window.removeEventListener("scroll", refreshDropdownPosition, true);
            window.removeEventListener("resize", refreshDropdownPosition);
          };
        }
        setDropdownPosition(null);
      }, [activeRowIdx, refreshDropdownPosition, suggestions.length]);
      useEffect(() => { setTimeout(() => diagRef.current?.focus(), 120); },[]);

      const updateRow = (i, field, val) => {
        setMedicineRows(r => { const n=[...r]; n[i][field]=val; return n; });
        if (field==="name") {
          setActiveRowIdx(i);
          if (val.length>1) fetch(buildApiUrl(`drugs/search?q=${val.toLowerCase()}`)).then(r=>r.json()).then(setSuggestions).catch(()=>setSuggestions([]));
          else setSuggestions([]);
        }
      };
      const removeRow = i => setMedicineRows(r => r.filter((_,idx) => idx!==i));
      const selectDrug = async drug => {
        if (activeRowIdx===null) return;
        const resolvedForm = resolveForm(drug);
        setMedicineRows(r => { const n=[...r]; n[activeRowIdx].name = drug.drugName; n[activeRowIdx].form = resolvedForm; n[activeRowIdx].strength = drug.strength || ""; return n; });
        if (!drug.form) {
          try {
            const res = await fetch(buildApiUrl(`drugs/by-name?name=${encodeURIComponent(drug.drugName)}`));
            const d = await res.json();
            if (d.success && d.drug) setMedicineRows(r => { const n=[...r]; n[activeRowIdx].form = resolveForm(d.drug); n[activeRowIdx].strength = d.drug.strength || ""; return n; });
          } catch {}
        }
        setSuggestions([]); setActiveRowIdx(null);
      };

      const handleSubmit = e => {
        e.preventDefault();
        setSaveProgress({ stage: "idle", completed: 0 });
        setSaveStartedAt(null);
        setSaveElapsedMs(0);
        setVisibleSaveProgress(0);
        setShowSaveConfirm(true);
      };

      const confirmSave = async () => {
        setUploading(true);
        setSaveStartedAt(Date.now());
        setSaveElapsedMs(0);
        setVisibleSaveProgress(0);
        setSaveProgress({ stage: "validating", completed: 0 });
        try {
          let file, prescriptionCode;
          if (mode==="DIGITAL") {
            prescriptionCode = genCode(sessionInfo?.doctorId);
            setSaveProgress({ stage: "generating", completed: 1 });
            file = await generatePrescriptionPDF({
              clinicId: CLINIC_ID,
              patientName:activePatient?.patientName, patientAge:resolveAge(activePatient), patientGender:resolveGender(activePatient), patientPhone:activePatient?.phone||activePatient?.phoneNumber||activePatient?.mobile, doctorName, prescriptionCode,
              diagnosis, medicines:medicineRows, notes, nextFollowUpDate, vitals:appointmentVital||{}, clinicalDocuments, clinicName:sessionInfo?.clinicName||"Clinic",
              doctorId:sessionInfo?.doctorId||"",
              doctorSpecialization:sessionInfo?.doctorSpecialization||"", doctorQualification:sessionInfo?.doctorQualification||"",
              doctorRegistrationNumber:sessionInfo?.doctorRegistrationNumber||"", clinicAddressLine:sessionInfo?.clinicAddressLine||"",
              clinicCity:sessionInfo?.clinicCity||"", clinicState:sessionInfo?.clinicState||"", clinicPincode:sessionInfo?.clinicPincode||""
            });
          } else {
            if (!imageFile) throw new Error("Please upload a prescription photo.");
            file = imageFile;
          }
          setSaveProgress({ stage: "saving", completed: 2 });
          await onSave({ mode, fileBlob:file, diagnosis, medicines:medicineRows, notes, nextFollowUpDate, prescriptionCode },
            progress => setSaveProgress(progress));
          setSaveProgress({ stage: "complete", completed: 4 });
          setVisibleSaveProgress(100);
          setSavedCountdown(5);
          await new Promise(resolve => setTimeout(resolve, 5000));
          setShowSaveConfirm(false);
          setSavedIndicator(true);
          setTimeout(()=>setSavedIndicator(false),3000);
          onClose();
        } catch (err) {
          setSaveProgress({ stage: "error", completed: 0 });
          alert("Error: "+err.message);
        } finally {
          setUploading(false);
        }
      };

      const medCount = medicineRows.filter(r=>r.name).length;
      const saveSteps = [
        { key: "validating", label: "Validating prescription details", Icon: FileCheck2 },
        { key: "generating", label: "Generating prescription document", Icon: FileText },
        { key: "saving", label: "Saving prescription", Icon: CloudUpload },
        { key: "updating", label: "Updating visit records", Icon: RefreshCw },
      ];
      const activeSaveStep = saveSteps.findIndex(step => step.key === saveProgress.stage);
      useEffect(() => {
        if (!uploading || !saveStartedAt || saveProgress.stage === "complete") return undefined;
        const timer = window.setInterval(() => {
          const elapsed = Date.now() - saveStartedAt;
          const timedProgress = Math.min(90, (elapsed / 40000) * 90);
          setSaveElapsedMs(elapsed);
          setVisibleSaveProgress(timedProgress);
        }, 50);
        return () => window.clearInterval(timer);
      }, [uploading, saveStartedAt, saveProgress.stage]);

      useEffect(() => {
        if (saveProgress.stage !== "complete") return undefined;
        const timer = window.setInterval(() => setSavedCountdown(current => Math.max(0, current - 1)), 1000);
        return () => window.clearInterval(timer);
      }, [saveProgress.stage]);
      const saveProgressPercent = Math.round(visibleSaveProgress);

      return (
        <div className={`rx-shell${saveProgress.stage === "complete" ? " save-complete" : ""}${saveElapsedMs >= 60000 && saveProgress.stage !== "complete" ? " save-slow" : ""}`}>
          <style>{`.rx-save-step.done .rx-save-step-icon circle{display:none}.rx-save-step.done .rx-save-step-indicator{width:30px;height:30px}.rx-save-step.done .rx-save-step-icon{width:17px;height:17px}.rx-save-confirm{width:min(594px,calc(100vw - 32px));max-height:calc(100dvh - 32px);overflow-y:auto}.rx-save-hero{align-items:center}.rx-save-brand{display:block;width:220px;height:52px;background:url(${clinicLogo}) left center/contain no-repeat;font-size:0}.rx-save-brand span{display:none}.rx-save-caption,.rx-save-hero-note{display:none}.rx-save-illustration{width:88px;height:88px;margin:20px auto 12px}.rx-save-confirm h2{max-width:100%;font-size:clamp(22px,3vw,28px);line-height:1.15}.rx-save-confirm>div>p{max-width:440px;margin-inline:auto}.rx-save-steps{width:min(100%,330px);margin-inline:auto}.rx-save-progress{width:min(100%,420px);margin-inline:auto}.rx-save-reassurance{width:min(100%,462px);margin-inline:auto}.rx-save-reassurance span:last-child{line-height:1.4}@media(max-width:560px){.rx-save-confirm{padding:22px 18px 20px;border-radius:18px}.rx-save-hero{min-height:52px}.rx-save-brand{width:180px;height:42px}.rx-save-illustration{width:72px;height:72px;margin-top:14px}.rx-save-illustration img{width:54px;height:54px}.rx-save-confirm h2{font-size:23px}.rx-save-confirm>div>p{font-size:13px;margin-bottom:18px}.rx-save-steps{gap:9px;margin-bottom:20px}.rx-save-step{font-size:12px;gap:9px}.rx-save-step-indicator{width:22px;height:22px}.rx-save-step.done .rx-save-step-indicator{width:26px;height:26px}.rx-save-step.done .rx-save-step-icon{width:15px;height:15px}.rx-save-progress{padding-inline:0}.rx-save-reassurance{padding:10px 12px}.rx-save-reassurance span{font-size:10px}}@media(max-width:360px){.rx-save-confirm{padding-inline:14px}.rx-save-brand{width:150px;height:36px}.rx-save-step{font-size:11px}.rx-save-step.done .rx-save-step-indicator{width:24px;height:24px}.rx-save-step.done .rx-save-step-icon{width:14px;height:14px}.rx-save-reassurance span{font-size:9px}}`}</style>
          <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');.rx-shell,.rx-shell button,.rx-shell input,.rx-shell textarea,.rx-shell select{font-family:'DM Sans',sans-serif}.rx-shell h1,.rx-shell h2,.rx-shell h3,.rx-shell .rx-patient-name{font-family:'Space Grotesk','DM Sans',sans-serif}.rx-save-confirm-backdrop{position:fixed;inset:0;z-index:16000;display:grid;place-items:center;padding:20px;background:rgba(8,15,35,.62);backdrop-filter:blur(12px)}.rx-save-confirm{width:min(460px,100%);padding:28px;border:1px solid rgba(255,255,255,.65);border-radius:24px;background:linear-gradient(145deg,#fff,#f7f9ff);box-shadow:0 28px 90px rgba(2,8,23,.32);animation:rx-confirm-in .22s ease-out}.rx-save-confirm-icon{width:46px;height:46px;display:grid;place-items:center;margin-bottom:16px;border-radius:15px;color:#fff;background:linear-gradient(135deg,#2563eb,#7c3aed);box-shadow:0 10px 24px rgba(37,99,235,.25)}.rx-save-confirm h2{margin:0;color:#101a36;font:700 23px/1.1 'Space Grotesk',sans-serif}.rx-save-confirm p{margin:8px 0 20px;color:#64748b;font-size:13px;line-height:1.55}.rx-save-summary{display:grid;gap:10px;padding:14px;margin-bottom:22px;border:1px solid #e4e9f4;border-radius:15px;background:#f8faff}.rx-save-summary div{display:flex;justify-content:space-between;gap:16px}.rx-save-summary span{color:#8290a8;font-size:11px;text-transform:uppercase;letter-spacing:.08em}.rx-save-summary strong{color:#263552;font-size:12px;text-align:right}.rx-save-progress{padding:15px 0 5px}.rx-save-progress-track{height:8px;overflow:hidden;border-radius:99px;background:#e4e9f4}.rx-save-progress-track span{display:block;width:45%;height:100%;border-radius:inherit;background:linear-gradient(90deg,#2563eb,#7c3aed);animation:rx-progress 1.2s ease-in-out infinite}.rx-save-progress-meta{display:flex;justify-content:space-between;gap:12px;margin-top:10px;color:#64748b;font-size:12px}.rx-save-progress-meta strong{color:#3154bb}.rx-save-confirm-actions{display:flex;justify-content:flex-end;gap:10px}.rx-save-cancel,.rx-save-confirm-btn{min-height:42px;padding:0 16px;border-radius:11px;font-weight:700;cursor:pointer}.rx-save-cancel{border:1px solid #dbe3f0;background:#fff;color:#52617b}.rx-save-confirm-btn{border:0;background:linear-gradient(135deg,#2563eb,#5b4be8);color:#fff;box-shadow:0 8px 20px rgba(37,99,235,.22)}@keyframes rx-progress{0%{transform:translateX(-110%)}100%{transform:translateX(250%)}}@keyframes rx-confirm-in{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:none}}@media(max-width:480px){.rx-save-confirm{padding:22px}.rx-save-confirm-actions{display:grid;grid-template-columns:1fr}.rx-save-cancel,.rx-save-confirm-btn{width:100%}}`}</style>
          <style>{`.rx-save-confirm-backdrop{background:rgba(22,36,66,.64);backdrop-filter:blur(9px)}.rx-save-confirm{width:min(594px,100%);padding:30px 34px 26px;border:1px solid #e3eaf6;border-radius:16px;background:linear-gradient(160deg,#fff 0%,#f7faff 100%);box-shadow:0 28px 80px rgba(15,37,76,.3)}.rx-save-hero{display:flex;align-items:flex-start;justify-content:space-between;gap:20px}.rx-save-brand{display:flex;align-items:center;gap:9px;color:#17366f;font-weight:800;font-size:16px;letter-spacing:-.03em}.rx-save-brand strong{color:#2d83ee}.rx-save-caption{margin-top:3px;color:#8494b0;font-size:10px}.rx-save-hero-note{max-width:110px;color:#8a9ab5;font-size:10px;line-height:1.35;text-align:right}.rx-save-illustration{display:grid;place-items:center;width:88px;height:88px;margin:18px auto 11px;border-radius:50%;background:#eaf3ff}.rx-save-illustration img{width:66px;height:66px;object-fit:contain}.rx-save-confirm h2{text-align:center;font-size:26px}.rx-save-confirm>div>p{text-align:center;max-width:390px;margin:8px auto 22px;font-size:14px}.rx-save-steps{display:grid;gap:11px;max-width:300px;margin:0 auto 24px}.rx-save-step{display:flex;align-items:center;gap:11px;color:#8190ab;font-size:13px}.rx-save-step-indicator{display:grid;place-items:center;width:24px;height:24px;border:2px solid #cdd9eb;border-radius:50%;flex:none;background:#fff}.rx-save-step.done{color:#20345e}.rx-save-step.done .rx-save-step-indicator{border-color:#3185ef;background:#3185ef;color:#fff}.rx-save-step.active{color:#1674ed;font-weight:700}.rx-save-step.active .rx-save-step-indicator{border-color:#70aaf5;color:#247ff0;box-shadow:0 0 0 4px #e7f1ff}.rx-save-step-icon{width:13px;height:13px}.rx-save-progress{padding:0 47px 2px}.rx-save-progress-track{height:13px;background:#dfe7f3}.rx-save-progress-track span{width:var(--save-progress);background:#3287f5;animation:none;transition:width .3s ease}.rx-save-progress-meta{margin-top:7px;color:#347fe3;font-weight:800;font-size:14px}.rx-save-reassurance{display:flex;align-items:center;gap:12px;margin-top:23px;padding:12px 16px;border-radius:12px;background:#edf5ff;color:#1f3765}.rx-save-reassurance-icon{display:grid;place-items:center;width:38px;height:38px;border-radius:50%;background:#d8ebff;color:#247ff0;flex:none}.rx-save-reassurance strong{display:block;font-size:12px}.rx-save-reassurance span{display:block;margin-top:3px;color:#647795;font-size:11px}.rx-save-confirm-actions{margin-top:20px}@media(max-width:520px){.rx-save-confirm{padding:24px 19px 20px}.rx-save-progress{padding-inline:0}.rx-save-hero-note{display:none}.rx-save-confirm h2{font-size:23px}}`}</style>
          <style>{`.rx-save-brand{display:block!important;width:220px!important;height:52px!important;background:url(${clinicLogo}) left center/contain no-repeat!important;color:transparent!important;font-size:0!important;line-height:0!important;text-indent:-9999px}.rx-save-brand span{display:none!important}.rx-save-caption,.rx-save-hero-note{display:none!important}.save-complete .rx-save-confirm h2{font-size:0}.save-complete .rx-save-confirm h2::after{content:"Prescription saved";font-size:clamp(22px,3vw,28px)}.save-complete .rx-save-confirm>div>p{font-size:0}.save-complete .rx-save-confirm>div>p::after{content:"The prescription and visit records were saved successfully.";font-size:14px}@media(max-width:560px){.rx-save-brand{width:180px!important;height:42px!important}.save-complete .rx-save-confirm h2::after{font-size:23px}.save-complete .rx-save-confirm>div>p::after{font-size:13px}}@media(max-width:360px){.rx-save-brand{width:150px!important;height:36px!important}}`}</style>
          <style>{`.save-complete .rx-save-confirm>p{font-size:0}.save-complete .rx-save-confirm>p::after{content:"The prescription and visit records were saved successfully.";font-size:14px}@media(max-width:560px){.save-complete .rx-save-confirm>p::after{font-size:13px}}`}</style>
          <style>{`.rx-save-confirm{display:flex;flex-direction:column;align-items:center;text-align:center}.rx-save-hero{width:100%;display:flex;justify-content:flex-start;text-align:left}.rx-save-illustration{flex:0 0 auto}.rx-save-confirm h2,.rx-save-confirm>p{width:100%;text-align:center}.rx-save-steps{align-self:center;text-align:left}.rx-save-progress{align-self:center;text-align:left}.rx-save-progress-meta{text-align:left}.rx-save-reassurance{align-self:center;text-align:left}.rx-save-summary{width:100%;text-align:left}.rx-save-confirm-actions{width:100%;justify-content:center}@media(max-width:560px){.rx-save-confirm{align-items:center}.rx-save-hero{justify-content:flex-start}.rx-save-steps,.rx-save-progress,.rx-save-reassurance{width:100%}}`}</style>
          <style>{`.rx-save-step.done .rx-save-step-indicator{width:21px!important;height:21px!important}.rx-save-step.done .rx-save-step-icon{width:21px!important;height:21px!important}@media(max-width:560px){.rx-save-step.done .rx-save-step-indicator{width:18px!important;height:18px!important}.rx-save-step.done .rx-save-step-icon{width:18px!important;height:18px!important}}@media(max-width:360px){.rx-save-step.done .rx-save-step-indicator{width:17px!important;height:17px!important}.rx-save-step.done .rx-save-step-icon{width:17px!important;height:17px!important}}`}</style>
          <style>{`.rx-save-step-indicator{position:relative;display:grid!important;place-items:center!important;align-items:center!important;justify-items:center!important;line-height:0!important;overflow:visible}.rx-save-step.done .rx-save-step-icon{position:absolute!important;top:50%!important;left:50%!important;display:block!important;margin:0!important;transform:translate(-50%,-50%) scale(1.3)!important;transform-origin:center}.rx-save-reassurance-icon{position:relative;display:grid!important;place-items:center!important;align-items:center!important;justify-items:center!important;line-height:0!important}.rx-save-reassurance-icon svg{position:absolute!important;top:50%!important;left:50%!important;display:block!important;margin:0!important;transform:translate(-50%,-50%)!important}.rx-save-progress-track span{transition:none!important}.save-complete .rx-save-steps,.save-complete .rx-save-progress,.save-complete .rx-save-reassurance{display:none!important}.save-complete .rx-save-confirm h2{font-size:0!important;margin-top:24px}.save-complete .rx-save-confirm h2::after{content:"Saved Successfully";font-size:clamp(28px,4vw,38px);color:#128348}.save-complete .rx-save-confirm>p{font-size:0!important;margin-bottom:8px}.save-complete .rx-save-confirm>p::after{content:"The prescription and visit records were saved successfully.";font-size:15px}.rx-save-countdown{position:fixed;left:50%;top:calc(50% + 190px);z-index:16005;transform:translateX(-50%);color:#64748b;font:600 13px/1.4 'DM Sans',sans-serif;text-align:center;white-space:nowrap}.rx-save-countdown strong{color:#2563eb;font-size:16px}@media(max-width:560px){.save-complete .rx-save-confirm h2::after{font-size:30px}.save-complete .rx-save-confirm>p::after{font-size:13px}.rx-save-countdown{top:calc(50% + 210px);font-size:12px}}`}</style>
          <style>{`.rx-save-step-indicator{position:relative;display:grid!important;place-items:center!important;align-items:center!important;justify-items:center!important;line-height:0!important;overflow:visible}.rx-save-step.done .rx-save-step-icon{position:absolute!important;top:50%!important;left:50%!important;display:block!important;margin:0!important;transform:translate(-50%,-50%) scale(1.3)!important;transform-origin:center}.rx-save-reassurance-icon{position:relative;display:grid!important;place-items:center!important;align-items:center!important;justify-items:center!important;line-height:0!important}.rx-save-reassurance-icon svg{position:absolute!important;top:50%!important;left:50%!important;display:block!important;margin:0!important;transform:translate(-50%,-50%)!important}.rx-save-progress-track span{transition:none!important}.save-complete .rx-save-steps,.save-complete .rx-save-progress,.save-complete .rx-save-reassurance{display:none!important}.save-complete .rx-save-confirm h2{font-size:0!important;margin-top:24px}.save-complete .rx-save-confirm h2::after{content:"Saved Successfully";font-size:clamp(28px,4vw,38px);color:#128348}.save-complete .rx-save-confirm>p{font-size:0!important;margin-bottom:8px}.save-complete .rx-save-confirm>p::after{content:"The prescription and visit records were saved successfully.";font-size:15px}.save-slow .rx-save-confirm>p{font-size:0!important}.save-slow .rx-save-confirm>p::after{content:"This is taking a little longer than usual. Please keep this window open while we securely finish saving the prescription and updating the visit records.";display:block;font-size:14px;line-height:1.55}.rx-save-countdown{position:fixed;left:50%;top:calc(50% + 190px);z-index:16005;transform:translateX(-50%);color:#64748b;font:600 13px/1.4 'DM Sans',sans-serif;text-align:center;white-space:nowrap}.rx-save-countdown strong{color:#2563eb;font-size:16px}@media(max-width:560px){.save-complete .rx-save-confirm h2::after{font-size:30px}.save-complete .rx-save-confirm>p::after{font-size:13px}.save-slow .rx-save-confirm>p::after{font-size:13px}.rx-save-countdown{top:calc(50% + 210px);font-size:12px}}`}</style>
          {saveProgress.stage === "complete" && <div className="rx-save-countdown">Closing automatically in <strong>{savedCountdown}</strong></div>}
          <style>{`.rx-save-progress-track span{transition:width .7s ease-out!important}`}</style>
          <div className="rx-topbar">
            <div className="rx-topbar-left">
              <div className="rx-web_logo-badge" style={{ width:46, height:46, borderRadius:"var(--r-2xl)", background:"linear-gradient(135deg, var(--violet-600), var(--blue-600))", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", boxShadow:"0 8px 24px rgba(124,58,237,0.28)", flexShrink:0 }}>
                <Pill size={22} strokeWidth={2} />
              </div>
              <div style={{ minWidth:0, width:"100%" }}>
                <div className="rx-patient-header-line">
                  <span className="rx-patient-token">#{activePatient?.tokenNo}</span>
                  <span className="rx-patient-name">{resolveHonorific(activePatient)} {activePatient?.patientName}</span>
                </div>
                <div className="rx-patient-sub rx-patient-agegender-line">
                  {resolveAge(activePatient)!=null && <span className="rx-patient-agegender">{resolveAge(activePatient)}y - {resolveGender(activePatient)}</span>}
                </div>
                <div className="rx-patient-sub rx-patient-doctorclinic">
                  <span className="rx-patient-doctor">Dr. {doctorName}</span>
                  <span className="rx-patient-clinic">{sessionInfo?.clinicName||"Clinic"}</span>
                </div>
              </div>
            </div>
            <div className="rx-topbar-right">
              <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--gray-100)", borderRadius:"999px", padding:"4px 6px", minWidth:0, flexShrink:1 }}>
                {[ ["create","Create Rx", FileText], ["history","History", Clock] ].map(([tab,lbl,Icon]) => (
                  <button key={tab} type="button" className={`rx-tab-btn ${padTab===tab?"active":""}`} onClick={() => setPadTab(tab)} style={{ display:"inline-flex", alignItems:"center", gap:6, minWidth:0, padding:"6px 10px", fontSize:11 }}>
                    <Icon size={14} strokeWidth={2} />
                    {tab === "create" ? <><span className="desktop-create-label">{lbl}</span><span className="mobile-create-label">Digital Rx</span></> : lbl}
                  </button>
                ))}
                <button type="button" className="rx-tab-btn desktop-report-history reports-history-btn" onClick={() => activePatient?.patientId && navigate(`/admin-dashboard/patients/${activePatient.patientId}/reports`)} style={{ alignItems:"center", gap:6, minWidth:0, padding:"6px 10px", fontSize:11 }} title="Open reports history">
                  <Upload size={14} strokeWidth={2} /> Reports history
                </button>
              </div>
              {padTab==="create" && (
                <div style={{ display:"flex", alignItems:"center", gap:6, background:"var(--gray-100)", borderRadius:"999px", padding:"4px 6px", minWidth:0, flexShrink:1 }}>
                  {[ ["DIGITAL","Digital Rx", BadgeCheck], ["PAPER","Scanned Rx", FileText] ].map(([m,lbl,Icon]) => (
                    <button key={m} type="button" className={`rx-mode-btn ${mode===m?"active":""}`} onClick={() => setMode(m)} style={{ display:"inline-flex", alignItems:"center", gap:6, minWidth:0, padding:"6px 10px", fontSize:11 }}>
                      <Icon size={14} strokeWidth={2} />
                      {lbl}
                    </button>
                  ))}
                </div>
              )}
              {savedIndicator && <span className="rx-badge-pill"><CheckCircle2 size={14} strokeWidth={2.2} /> Saved</span>}
            </div>
          </div>

          <div className={`rx-body${padTab==="history"?" history-mode":padTab==="documents"?" documents-mode":""}`}>
            <div className="rx-main-scroll">
              {padTab==="history" ? (
                <div className="rx-history-wrap">
                  <PatientHistoryTable activePatient={activePatient} clinicId={clinicId} patientHistory={patientHistory} vitalsHistory={vitalsHistory} currentAppointmentVital={currentAppointmentVital} appointmentStatusMap={appointmentStatusMap} onWritePrescription={() => { setPadTab("create"); setTimeout(()=>diagRef.current?.focus(),40); }} />
                </div>
              ) : padTab==="documents" ? (
                <ClinicalDocumentsPanel patient={activePatient} clinicId={clinicId} onClose={() => setPadTab("create")} />
              ) : (
                <form id="rx-form" onSubmit={handleSubmit}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <button type="button" className="rx-vitals-toggle" onClick={() => setVitalsOpen(o => !o)} style={{ flex: "1 1 360px", width: "auto" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <HeartPulse size={14} strokeWidth={2} />
                        Visit Vitals
                        {appointmentVital && <span style={{ background: "var(--green-600)", color: "#fff", fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: "var(--r-sm)", textTransform: "uppercase" }}>Recorded</span>}
                        {!appointmentVital && !isLoadingVital && <span style={{ background: "var(--red-100)", color: "var(--red-700)", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: "var(--r-sm)" }}>Missing</span>}
                      </span>
                      {vitalsOpen ? <ChevronUp size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />}
                    </button>
                    <div className="rx-report-actions" style={{ display:"flex", gap:8, flex:"1 1 360px" }}>
                      <button type="button" className="rx-tab-btn add-report-btn" onClick={() => setPadTab("documents")} style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:7, minHeight:44, padding:"10px 12px", flex:"1 1 0", minWidth:0, color:"var(--blue-700)", border:"1px solid var(--blue-200)", background:"var(--blue-50)" }}>
                        <Upload size={15} strokeWidth={2} /> Add Report
                      </button>
                      <button type="button" className="rx-tab-btn mobile-report-history reports-history-btn" onClick={() => activePatient?.patientId && navigate(`/admin-dashboard/patients/${activePatient.patientId}/reports`)} style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:7, minHeight:44, padding:"10px 12px", flex:"1 1 0", minWidth:0, color:"var(--teal-700, #0f766e)", border:"1px solid var(--teal-200, #99f6e4)", background:"var(--teal-50, #f0fdfa)" }}>
                        <FileText size={15} strokeWidth={2} /> Reports history
                      </button>
                    </div>
                  </div>
                  {vitalsOpen && (
                    <div className="rx-vitals-inline">
                      <VitalsContent appointmentVital={appointmentVital} isLoadingVital={isLoadingVital} fetchVitals={fetchVitals} onOpenVitalsModal={() => { setVitalsOpen(false); onOpenVitalsModal(); }} />
                    </div>
                  )}

                  <div className="rx-section-card" style={{ ...card, padding:"18px 20px", marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
                      <Stethoscope size={14} color="var(--blue-600)" strokeWidth={2} />
                      <span className="rx-section-title" style={{ fontSize:10.5, fontWeight:700, color:"var(--gray-500)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Chief Complaint / Diagnosis</span>
                    </div>
                    <input ref={diagRef} className="rx-input" required placeholder="Enter diagnosis or chief complaint..." value={diagnosis} onChange={e => setDiagnosis(e.target.value)} style={{ fontSize:14, padding:"11px 14px", fontWeight:500 }} />
                  </div>

                  {mode==="DIGITAL" ? (
                    <div className="rx-section-card" style={{ ...card, padding:"18px 20px", marginBottom:14, overflow:"visible" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
                        <Pill size={14} color="var(--blue-600)" strokeWidth={2} />
                        <span className="rx-section-title" style={{ fontSize:10.5, fontWeight:700, color:"var(--gray-500)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Prescribed Medicines</span>
                      </div>

                      <div className="rx-med-table-wrap" style={{ overflowX: "auto", overflowY: "visible", position: "relative" }}>
                        <div className="rx-med-header-row">
                          {["Medicine + Form","Strength","AM","Noon","PM","Night","Days","Timing",""].map((h,hi) => <div key={hi} style={{ fontSize:9.5, fontWeight:700, color:"var(--gray-400)", textTransform:"uppercase", letterSpacing:"0.07em", textAlign:["AM","Noon","PM","Night","Days"].includes(h)?"center":"left" }}>{h}</div>)}
                        </div>

                        <div>
                          {medicineRows.map((row, i) => (
                            <React.Fragment key={i}>
                              <div className="rx-med-desktop-only" style={{ display:"contents" }}>
                                <DesktopMedRow row={row} i={i} updateRow={updateRow} removeRow={removeRow} showRemove={medicineRows.length>1} suggestions={suggestions} activeRowIdx={activeRowIdx} selectDrug={selectDrug} deriveForm={deriveForm} getUnit={getUnit} renderSuggestionList={renderSuggestionList} hasExactSuggestion={hasExactSuggestion} dropdownInputRefs={dropdownInputRefs} setActiveRowIdx={setActiveRowIdx} />
                              </div>
                              <div className="rx-med-mobile-only" style={{ display:"none" }}>
                                <MobileCardRow row={row} i={i} updateRow={updateRow} removeRow={removeRow} showRemove={medicineRows.length>1} suggestions={suggestions} activeRowIdx={activeRowIdx} selectDrug={selectDrug} deriveForm={deriveForm} getUnit={getUnit} renderSuggestionList={renderSuggestionList} hasExactSuggestion={hasExactSuggestion} dropdownInputRefs={dropdownInputRefs} setActiveRowIdx={setActiveRowIdx} />
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                        {activeRowIdx !== null && dropdownPosition && (suggestions.length > 0 || (String(medicineRows[activeRowIdx]?.name||"").trim().length > 1 && !hasExactSuggestion(String(medicineRows[activeRowIdx]?.name||"").trim()))) && (
                          <div style={{ position:"fixed", top:dropdownPosition.top, left:dropdownPosition.left, width:dropdownPosition.width, zIndex:12000, background:"var(--white)", border:"1.5px solid var(--gray-200)", borderRadius:"var(--r-xl)", boxShadow:"var(--sh-lg)", maxHeight:240, overflowY:"auto" }}>
                            {renderSuggestionList(medicineRows[activeRowIdx], activeRowIdx)}
                          </div>
                        )}
                      </div>

                      <button type="button" className="rx-add-med-btn" onClick={() => setMedicineRows(r => [...r, { name:"", form:"", strength:"", morning:"", afternoon:"", evening:"", night:"", duration:"", timing:"After Food" }])} style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:7, border:"1.5px dashed var(--gray-200)", borderRadius:"999px", background:"transparent", color:"var(--blue-600)", fontSize:12.5, fontWeight:700, cursor:"pointer", padding:"10px 18px", transition:"all 0.14s", fontFamily:"var(--font)" }} onMouseEnter={e => { e.currentTarget.style.background="var(--blue-50)"; e.currentTarget.style.borderColor="var(--blue-300)"; }} onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor="var(--gray-200)"; }}>
                        <Plus size={16} strokeWidth={2.5} /> Add Medicine
                      </button>
                    </div>
                  ) : (
                    <div className="rx-section-card" style={{ ...card, padding:"18px 20px", marginBottom:14 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
                        <FileText size={14} color="var(--blue-600)" strokeWidth={2} />
                        <span className="rx-section-title" style={{ fontSize:10.5, fontWeight:700, color:"var(--gray-500)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Upload Prescription</span>
                      </div>
                      <label htmlFor="rx-photo" className="rx-upload-label" style={{ display:"block", border:"2px dashed var(--gray-200)", borderRadius:"var(--r-xl)", padding:"32px 20px", textAlign:"center", background:"var(--gray-50)", cursor:"pointer", transition:"all 0.18s" }} onMouseEnter={e => { e.currentTarget.style.borderColor="var(--blue-400)"; e.currentTarget.style.background="var(--blue-50)"; }} onMouseLeave={e => { e.currentTarget.style.borderColor="var(--gray-200)"; e.currentTarget.style.background="var(--gray-50)"; }}>
                        <input type="file" accept="image/*" id="rx-photo" style={{ display:"none" }} onChange={e => { const f=e.target.files?.[0]; if (!f||f.size>50*1024*1024) { setImageFile(null); setImageError(f?"File too large (max 50MB)":""); } else { setImageFile(f); setImageError(""); } }} />
                        <FileText size={32} color="var(--gray-300)" strokeWidth={1.5} style={{ display:"block", margin:"0 auto 10px" }} />
                        <div style={{ fontSize:13.5, fontWeight:700, color:"var(--gray-700)" }}>Click to upload</div>
                        <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:3 }}>{imageFile?`✓ ${imageFile.name}`:"JPG, PNG — Max 50MB"}</div>
                      </label>
                      {imageError && <div style={{ color:"var(--red-600)", fontSize:12, marginTop:7 }}>{imageError}</div>}
                    </div>
                  )}

                  <div className="rx-notes-followup-row">
                    <div className="rx-section-card" style={{ ...card, padding:"18px 20px", flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
                        <FileText size={14} color="var(--blue-600)" strokeWidth={2} />
                        <span className="rx-section-title" style={{ fontSize:10.5, fontWeight:700, color:"var(--gray-500)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Clinical Notes</span>
                      </div>
                      <textarea className="rx-input rx-notes-textarea" placeholder="Dietary advice, lifestyle changes, precautions..." value={notes} onChange={e => setNotes(e.target.value)} style={{ height:80, resize:"vertical", lineHeight:1.6 }} />
                    </div>

                    <div className="rx-section-card" style={{ ...card, padding:"18px 20px", flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
                        <Calendar size={14} color="var(--blue-600)" strokeWidth={2} />
                        <span className="rx-section-title" style={{ fontSize:10.5, fontWeight:700, color:"var(--gray-500)", textTransform:"uppercase", letterSpacing:"0.07em" }}>Next Follow-up</span>
                      </div>
                      <input type="date" className="rx-input rx-followup-input" value={nextFollowUpDate} onChange={e => setNextFollowUpDate(e.target.value)} style={{ width:"100%" }} />
                    </div>
                  </div>
                </form>
              )}
            </div>

            {padTab==="create" && (
              <div className="rx-vitals-panel">
                <VitalsContent appointmentVital={appointmentVital} isLoadingVital={isLoadingVital} fetchVitals={fetchVitals} onOpenVitalsModal={onOpenVitalsModal} />
              </div>
            )}
          </div>

          <div className="rx-bottom-bar">
            <div className="rx-bottom-left">
              <Btn variant="ghost" onClick={onClose} style={{ color:"var(--red-600)", borderColor:"var(--red-200)", background:"var(--red-50)", padding:"8px 12px", borderRadius:"10px" }}>{padTab==="create"?"Discard":"Close"}</Btn>
            </div>

            <div className="rx-bottom-hint" style={{ display:padTab==="create"?"block":"none" }}>
              {mode==="DIGITAL" ? `${medCount} medicine(s) - ${diagnosis?"Diagnosis set":"No diagnosis yet"}` : "Scanned prescription mode"}
            </div>

            <div className="rx-bottom-right">
              {padTab==="create" && (
                <button type="submit" form="rx-form" disabled={uploading} className="rx-finalise-btn" style={{ background:uploading?"var(--gray-400)":"linear-gradient(135deg, var(--violet-600), var(--blue-600))", color:"#fff", boxShadow:uploading?"none":"0 6px 22px rgba(59,65,120,0.12)", padding:"10px 18px", minHeight:40 }}>
                  {uploading ? <><RefreshCw size={14} strokeWidth={2} className="anim-spin" /> Processing...</> : <><CheckCircle2 size={14} strokeWidth={2} /> Save</>}
                </button>
              )}
            </div>
          </div>
          {showSaveConfirm && <div className="rx-save-confirm-backdrop" role="presentation" onMouseDown={e => !uploading && e.target === e.currentTarget && setShowSaveConfirm(false)}><div className="rx-save-confirm" role="dialog" aria-modal="true" aria-labelledby="rx-save-title"><div className="rx-save-hero"><div><div className="rx-save-brand">Clini<span>Queue</span></div><div className="rx-save-caption">Smarter Clinics. Happier Patients.</div></div><div className="rx-save-hero-note">Your care<br />workflow, simplified.</div></div><div className="rx-save-illustration"><img src={prescriptionImage} alt="Prescription" /></div><h2 id="rx-save-title">{uploading ? "Saving prescription" : "Save prescription?"}</h2><p>{uploading ? "Please wait while we generate the prescription and update this visit." : "Review the prescription before marking this visit as completed."}</p>{uploading ? <><div className="rx-save-steps" aria-live="polite">{saveSteps.map((step, index) => { const isDone = saveProgress.completed > index; const isActive = !isDone && activeSaveStep === index; const StepIcon = step.Icon; return <div className={`rx-save-step${isDone ? " done" : isActive ? " active" : ""}`} key={step.key}><span className="rx-save-step-indicator">{isDone ? <CheckCircle2 className="rx-save-step-icon" /> : isActive ? <StepIcon className="rx-save-step-icon anim-spin" /> : <Circle className="rx-save-step-icon" />}</span><span>{step.label}</span></div>; })}</div><div className="rx-save-progress"><div className="rx-save-progress-track"><span style={{ "--save-progress": `${saveProgressPercent}%` }} /></div><div className="rx-save-progress-meta"><strong>Progress: {saveProgressPercent}%</strong></div></div><div className="rx-save-reassurance"><span className="rx-save-reassurance-icon"><ShieldCheck size={21} /></span><div><strong>Your data is safe and secure</strong><span>Please keep this window open while we complete the process.</span></div></div></> : <><div className="rx-save-summary"><div><span>Patient</span><strong>{resolveHonorific(activePatient)} {activePatient?.patientName || "Patient"}</strong></div><div><span>Mode</span><strong>{mode === "DIGITAL" ? "Digital prescription" : "Scanned prescription"}</strong></div><div><span>Medicines</span><strong>{mode === "DIGITAL" ? `${medCount} item${medCount === 1 ? "" : "s"}` : "Uploaded image"}</strong></div></div><div className="rx-save-confirm-actions"><button type="button" className="rx-save-cancel" onClick={() => setShowSaveConfirm(false)}>Review again</button><button type="button" className="rx-save-confirm-btn" onClick={confirmSave}>Confirm and save</button></div></>}</div></div>}
        </div>
      );
}

function PatientHistoryTable({ activePatient, clinicId, patientHistory=[], vitalsHistory=[], currentAppointmentVital, appointmentStatusMap={}, onWritePrescription }) {
  const [filterDate, setFilterDate] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!clinicId || !activePatient?.patientId) {
      setReports([]);
      return undefined;
    }
    let cancelled = false;
    axios.get(buildApiUrl(`reports/patient/${activePatient.patientId}`), { headers: { "X-Clinic-Id": clinicId } })
      .then(response => { if (!cancelled) setReports(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (!cancelled) setReports([]); });
    return () => { cancelled = true; };
  }, [activePatient?.patientId, clinicId]);

  const reportByAppointment = useMemo(() => {
    const mapped = new Map();
    reports.forEach(report => {
      if (report?.appointmentId && !mapped.has(String(report.appointmentId))) {
        mapped.set(String(report.appointmentId), report);
      }
    });
    return mapped;
  }, [reports]);

  const openReport = async (report) => {
    if (!report?.id || !clinicId) return;
    try {
      const response = await fetch(buildApiUrl(`reports/${report.id}/content`), { headers: { "X-Clinic-Id": clinicId } });
      if (!response.ok) throw new Error("Unable to open report");
      const objectUrl = URL.createObjectURL(await response.blob());
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      toast.error(error.message || "Unable to open report");
    }
  };

  const resolveHonorific = p => {
    const raw = String(p?.gender||p?.patientGender||"").trim().toLowerCase();
    if (["m","male"].includes(raw)) return "Mr.";
    if (["f","female"].includes(raw)) return "Ms/Mrs";
    return "";
  };

  const resolveDisplayName = p => {
    if (!p) return "";
    const honorific = resolveHonorific(p);
    const name = p?.patientName ? String(p.patientName) : "";
    return `${honorific} ${name}`.trim();
  };

  const formatDate = (value) => {
    if (!value) return "-";
    let d;
    if (typeof value === 'object' && value !== null) {
      if (value.year != null && value.month != null && value.day != null) {
        d = new Date(Number(value.year), Number(value.month) - 1, Number(value.day));
      } else if (value.epochMillis != null) {
        d = new Date(Number(value.epochMillis));
      } else if (value.$date != null) {
        d = new Date(value.$date);
      } else {
        d = new Date(JSON.stringify(value));
      }
    } else {
      d = new Date(value);
    }
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric", timeZone:"Asia/Kolkata" });
  };

  const formatTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", hour12:true, timeZone:"Asia/Kolkata" });
  };

  const extractVitalNotes = (v) => {
    if (!v || typeof v !== 'object') return "";
    return v.notes || v.clinicalNotes || v.observations || "";
  };

  const extractVitalFollowUpDate = (v) => {
    if (!v || typeof v !== 'object') return "";
    return v.nextFollowUpDate || v.followUpDate || v.followup || "";
  };

  const extractRxFollowUpDate = (rx) => {
    if (!rx || typeof rx !== 'object') return "";
    // common variations
    const candidates = [
      rx.nextFollowUpDate,
      rx.next_follow_up_date,
      rx.nextFollowupDate,
      rx.nextFollowup,
      rx.next_followup,
      rx.followUpDate,
      rx.followup,
      rx.follow_up_date,
      rx.follow_up,
      rx.followUp,
      rx.next_follow_up_date_ms,
    ];
    for (const c of candidates) if (c) return c;
    // nested metadata
    if (rx.meta && typeof rx.meta === 'object') {
      const m = rx.meta;
      if (m.nextFollowUpDate) return m.nextFollowUpDate;
      if (m.followUpDate) return m.followUpDate;
      if (m.followup) return m.followup;
    }
    return "";
  };

  const formatVitalsList = (v) => {
    // return two rows: top (height, weight, bmi, temp) and bottom (bp, hr, spO2, rr)
    const top = [];
    const bottom = [];
    if (!v || typeof v !== 'object') return { top, bottom };
    const bpS = v.bloodPressureSystolic || v.bpSystolic || v.systolic;
    const bpD = v.bloodPressureDiastolic || v.bpDiastolic || v.diastolic;
    if (v.height != null) top.push({ key:'height', label:`${v.height} ${v.heightUnit || 'cm'}`, value:`${v.height} ${v.heightUnit || 'cm'}`, icon: Ruler, color: 'var(--cyan-600)' });
    if (v.weight != null) top.push({ key:'weight', label:`${v.weight} ${v.weightUnit || 'kg'}`, value:`${v.weight} ${v.weightUnit || 'kg'}`, icon: Scale, color: 'var(--green-600)' });
    if (v.bmi != null) top.push({ key:'bmi', label:`${v.bmi}`, value:`${v.bmi}`, icon: Gauge, color: 'var(--blue-700)' });
    if (v.temperature != null) top.push({ key:'temperature', label:`T ${v.temperature} ${v.temperatureUnit || '°C'}`, icon: Thermometer, color: 'var(--blue-600)' });

    if (bpS && bpD) bottom.push({ key:'bp', label:`BP ${bpS}/${bpD} ${v.bloodPressureUnit || 'mmHg'}`, icon: Scale, color: 'var(--blue-700)' });
    if (v.heartRate != null) bottom.push({ key:'hr', label:`HR ${v.heartRate} ${v.heartRateUnit || 'bpm'}`, icon: HeartPulse, color: 'var(--red-600)' });
    if (v.spO2 != null) bottom.push({ key:'spo2', label:`SpO2 ${v.spO2} ${v.spO2Unit || '%'}`, icon: Droplets, color: 'var(--blue-600)' });
    if (v.respiratoryRate != null) bottom.push({ key:'rr', label:`RR ${v.respiratoryRate} ${v.respiratoryRateUnit || 'br/min'}`, icon: Wind, color: 'var(--violet-600)' });
    if (v.rr != null && !bottom.find(it=>it.key==='rr')) bottom.push({ key:'rr2', label:`RR ${v.rr} br/min`, icon: Wind, color: 'var(--violet-600)' });

    const coreKeys = new Set(["m001", "m002", "m003", "m004", "m005", "m006", "m007", "m008", "m051", "legacy-notes"]);
    (Array.isArray(v.measurements) ? v.measurements : []).forEach((measurement) => {
      if (coreKeys.has(measurement.measurementKey)) return;
      const value = formatObservationValue(measurement.value);
      if (!value) return;
      const label = `${measurement.name}: ${value}${measurement.unit ? ` ${measurement.unit}` : ""}`;
      bottom.push({ key: `dynamic-${measurement.id || measurement.measurementKey}-${bottom.length}`, label: label.length > 58 ? `${label.slice(0, 55)}...` : label, value: label, icon: FileText, color: 'var(--violet-600)' });
    });

    // If sugar exists and bottom has less than 4, add sugar to bottom as fallback
    if (v.sugar != null && bottom.length < 4) bottom.push({ key:'sugar', label:`Sugar ${v.sugar} mg/dL`, icon: FlaskConical, color: 'var(--amber-600)' });

    // include other non-meta useful fields into bottom if space allows
    Object.keys(v).forEach(k => {
      const lk = String(k).toLowerCase();
      if (['bloodpressuresystolic','bloodpressurediastolic','bpsystolic','bpdiastolic','systolic','diastolic','temperature','heartrate','spo2','respiratoryrate','rr','sugar','bloodsugar','weight','height','bmi','measurements','id','appointmentid','recordedat','updatedat','createdat','patientid','patient_id','bookingid','doctorid','clinicid','status','tokeno'].includes(lk)) return;
      if (lk.includes('note') || lk.includes('notes') || lk.includes('follow')) return;
      const val = v[k]; if (val == null) return;
      if (typeof val === 'boolean') return;
      const sval = String(val).trim(); if (!sval) return;
      if (['true','false'].includes(sval.toLowerCase())) return;
      if (lk.includes('normal') || lk.includes('category') || lk.startsWith('is') || lk.startsWith('has')) return;
      if (bottom.length < 4) bottom.push({ key:k, label: sval.length > 36 ? `${sval.slice(0,36)}...` : sval, value: sval, icon: FileText, color: 'var(--violet-600)' });
    });

    return { top, bottom };
  };

  const rows = useMemo(() => {
    const items = [];
    const vitalsByAppointment = new Map();
    const rxByAppointment = new Map();

    (vitalsHistory || []).forEach(v => {
      const appointmentId = v.appointmentId || v.appointment?.id || v.bookingId;
      if (appointmentId) vitalsByAppointment.set(String(appointmentId), v);
      items.push({
        id: `vital-${v.id || appointmentId || items.length}`,
        type: "Vitals",
        timestamp: new Date(v.recordedAt || v.updatedAt || v.createdAt || Date.now()).getTime(),
        vital: v,
        rx: null,
        status: appointmentStatusMap[appointmentId] || "",
      });
    });

    (patientHistory || []).forEach(rx => {
      const appointmentId = rx.appointmentId || rx.appointment?.id;
      if (appointmentId) rxByAppointment.set(String(appointmentId), rx);
      items.push({
        id: `rx-${rx.id || appointmentId || items.length}`,
        type: "Prescription",
        timestamp: new Date(rx.createdAt || rx.created_at || rx.appointmentDate || Date.now()).getTime(),
        vital: appointmentId ? vitalsByAppointment.get(String(appointmentId)) : null,
        rx,
        status: appointmentStatusMap[appointmentId] || "",
      });
    });

    // Link vitals rows with their corresponding prescriptions by appointmentId
    items.forEach(item => {
      if (item.type === "Vitals" && !item.rx) {
        const apptId = item.vital?.appointmentId || item.vital?.appointment?.id || item.vital?.bookingId;
        if (apptId && rxByAppointment.has(String(apptId))) {
          item.rx = rxByAppointment.get(String(apptId));
        }
      }
    });

    if (currentAppointmentVital) {
      items.unshift({
        id: "current-visit",
        type: "Current Visit",
        timestamp: new Date(currentAppointmentVital.recordedAt || currentAppointmentVital.updatedAt || Date.now()).getTime(),
        vital: currentAppointmentVital,
        rx: null,
        status: "CONSULTING",
        isCurrent: true,
      });
    }

    return items
      .filter(item => !filterDate || new Date(item.timestamp).toISOString().slice(0, 10) === filterDate)
      .sort((a,b) => sortOrder === "newest" ? b.timestamp - a.timestamp : a.timestamp - b.timestamp);
  }, [appointmentStatusMap, currentAppointmentVital, filterDate, patientHistory, sortOrder, vitalsHistory]);

  // columns array removed because the rendering is handled directly in JSX below

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0, height:"100%", maxHeight:"100%", minHeight:0 }}>
      <div style={{ ...card, padding:0, overflow:"visible", flex:1, display:"flex", flexDirection:"column", minHeight:0, gap:0 }}>
        <div style={{ background:"linear-gradient(135deg, var(--blue-50) 0%, var(--white) 100%)", borderBottom:"3px solid var(--blue-500)", padding:"12px 18px", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:14, flexWrap:"wrap" }}>
            <div>
              <h2 style={{ fontSize:16, fontWeight:800, color:"var(--blue-900)", marginBottom:3 }}>Patient history</h2>
              <p style={{ fontSize:13, color:"var(--blue-600)" }}><strong>{resolveDisplayName(activePatient) || "Patient"}</strong> previous vitals and prescriptions.</p>
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <input className="rx-input" type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{ width:160, borderColor:"var(--blue-300)", borderRadius:"var(--r-lg)" }} />
              <select className="rx-select" value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={{ width:140, borderColor:"var(--blue-300)", borderRadius:"var(--r-lg)" }}>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", flex:1, minHeight:0, overflow:"hidden", paddingTop:"24px" }}>
          {/* Desktop Table */}
          <div style={{ overflowY:"auto", flex:1, minHeight:0, display:"none" }} className="hist-table-desktop">
            <table style={{ width:"100%", minWidth:820, borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
              <colgroup>
                <col style={{ width:140 }} />
                <col style={{ width:120 }} />
                <col style={{ width:420 }} />
                <col style={{ width:170 }} />
                <col style={{ width:200 }} />
                <col style={{ width:140 }} />
                <col style={{ width:120 }} />
              </colgroup>
              <thead style={{ position:"sticky", top:0, zIndex:10 }}>
                <tr style={{ background:"linear-gradient(90deg, var(--blue-600) 0%, var(--blue-500) 100%)", borderBottom:"2px solid var(--blue-700)" }}>
                  {[
                    { key:"date", label:"Date" },
                    { key:"type", label:"Type" },
                    { key:"vitals", label:"Vitals" },
                    { key:"diagnosis", label:"Diagnosis" },
                    { key:"notes", label:"Notes" },
                    { key:"followup", label:"Follow-up" },
                    { key:"action", label:"Action" },
                  ].map((col, index) => (
                    <th key={col.key} style={{ padding:"14px 14px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.07em", borderRight:index < 7 ? "1px solid rgba(255,255,255,0.25)" : undefined }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding:"48px 20px", textAlign:"center", color:"var(--gray-500)" }}>
                    <Activity size={30} color="var(--gray-300)" strokeWidth={1.6} style={{ margin:"0 auto 10px", display:"block" }} />
                    <strong>No history records</strong>
                    <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:4 }}>Records appear here after consultations.</div>
                  </td>
                </tr>
              ) : rows.map(row => {
                 const vital = row.vital || {};
                 const rx = row.rx || {};
                 const report = reportByAppointment.get(String(rx.appointmentId || row.vital?.appointmentId || ""));
                 const vitalsItems = formatVitalsList(vital);
                 const followDate = (typeof extractRxFollowUpDate === 'function' ? extractRxFollowUpDate(rx) : (rx.nextFollowUpDate || rx.followUpDate || rx.followup)) || (typeof extractVitalFollowUpDate === 'function' ? extractVitalFollowUpDate(vital) : (vital.nextFollowUpDate || vital.followUpDate || vital.followup));

                return (
                  <tr key={row.id} className="hist-row" style={{ borderBottom:"1px solid var(--blue-100)", background:row.isCurrent ? "linear-gradient(90deg, var(--blue-50) 0%, var(--blue-25) 100%)" : (rows.indexOf(row) % 2 === 0 ? "var(--white)" : "var(--blue-25)"), borderLeft:row.isCurrent ? "4px solid var(--blue-600)" : "4px solid transparent", transition:"all 0.15s" }}>
                    <td style={{ padding:"12px 14px", whiteSpace:"nowrap", borderRight:"1px solid var(--blue-100)" }}>
                      <div style={{ fontFamily:"var(--mono)", fontWeight:700, color:"var(--blue-900)" }}>{formatDate(row.timestamp)}</div>
                      <div style={{ color:"var(--blue-500)", marginTop:2, fontSize:11 }}>{formatTime(row.timestamp)}</div>
                    </td>
                    <td style={{ padding:"12px 14px", fontWeight:700, color:"var(--gray-900)", textAlign:"center", borderRight:"1px solid var(--blue-100)" }}>{row.type}</td>
                    <td style={{ padding:"12px 14px", color:"var(--blue-700)", width:420, fontWeight:500, borderRight:"1px solid var(--blue-100)" }}>
                      {vitalsItems && (vitalsItems.top?.length || vitalsItems.bottom?.length) ? (
                        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                          <div style={{ display:"flex", gap:8, flexWrap:"nowrap", alignItems:"center", overflow: 'hidden' }}>
                            {(vitalsItems.top||[]).map((it,i) => {
                              const Icon = it.icon || Thermometer;
                              return (
                                <span key={"t-"+i} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 8px", background:"rgba(15,23,42,0.04)", borderRadius:999, fontSize:12, fontWeight:700, color:"var(--gray-900)", maxWidth:120 }}>
                                  <Icon size={12} strokeWidth={2} color={it.color || 'var(--blue-600)'} />
                                  <span style={{ color:"var(--gray-900)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:110 }}>{it.label}</span>
                                </span>
                              );
                            })}
                          </div>
                          <div style={{ display:"flex", gap:8, flexWrap:"nowrap", alignItems:"center", overflow: 'hidden' }}>
                            {(vitalsItems.bottom||[]).slice(0,4).map((it,i) => {
                              const Icon = it.icon || Thermometer;
                              return (
                                <span key={"b-"+i} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 8px", background:"rgba(15,23,42,0.04)", borderRadius:999, fontSize:12, fontWeight:700, color:"var(--gray-900)", maxWidth:120 }}>
                                  <Icon size={12} strokeWidth={2} color={it.color || 'var(--blue-600)'} />
                                  <span style={{ color:"var(--gray-900)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:110 }}>{it.label}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ) : "-"}
                    </td>
                    <td style={{ padding:"12px 14px", color:"var(--gray-900)", width:170, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:500, borderRight:"1px solid var(--blue-100)", textAlign:"center" }}>{rx.diagnosis || "-"}</td>
                    <td style={{ padding:"12px 14px", color:"var(--gray-900)", width:200, fontWeight:500, borderRight:"1px solid var(--blue-100)", lineHeight:1.4, wordBreak:"break-word", whiteSpace:"normal", maxHeight:80, overflow:"hidden", textOverflow:"ellipsis", textAlign:"center" }}>{(rx.notes || extractVitalNotes(vital)) || "-"}</td>
                    <td style={{ padding:"12px 14px", color:"var(--red-700)", whiteSpace:"nowrap", fontWeight:700, textAlign:"center", borderRight:"1px solid var(--blue-100)" }}>{followDate ? formatDate(followDate) : "-"}</td>
                    <td style={{ padding:"12px 14px", textAlign:"center" }}>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                      {row.isCurrent ? (
                        <Btn variant="primary" size="xs" onClick={onWritePrescription}><Pill size={12} /> Write Rx</Btn>
                      ) : rx.id ? (
                        <Btn variant="view" size="xs" onClick={() => { const appUrl = appConfig.dashboardUrl || process.env.REACT_APP_DASHBOARD_URL || window.location.origin; window.open(rx.imageUrl || `${appUrl}/prescription-view/${rx.id}`, "_blank"); }}><Eye size={12} /> View</Btn>
                      ) : "-"}
                      {report && <Btn variant="view" size="xs" onClick={() => openReport(report)}><FileText size={12} /> View Report</Btn>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>

          {/* Mobile Card Layout */}
          <div style={{ overflowY:"auto", flex:1, minHeight:0, display:"none", padding:0, alignItems:"stretch" }} className="hist-cards-mobile">
            {rows.length === 0 ? (
              <div style={{ padding:"48px 20px", textAlign:"center", color:"var(--gray-500)" }}>
                <Activity size={30} color="var(--gray-300)" strokeWidth={1.6} style={{ margin:"0 auto 10px", display:"block" }} />
                <strong>No history records</strong>
                <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:4 }}>Records appear here after consultations.</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:14, padding:"0 12px 12px 12px", alignItems:"center", width:"100%" }}>
                {rows.map(row => {
                  const vital = row.vital || {};
                  const rx = row.rx || {};
                  const report = reportByAppointment.get(String(rx.appointmentId || row.vital?.appointmentId || ""));
                  const formatVitalItems = (v) => {
                    const seen = new Set();
                    const items = [];
                    if (!v || typeof v !== 'object') return items;
                    const bpS = v.bloodPressureSystolic || v.bpSystolic || v.systolic;
                    const bpD = v.bloodPressureDiastolic || v.bpDiastolic || v.diastolic;
                    if (bpS && bpD) {
                      items.push({ label: `BP ${bpS}/${bpD} mmHg`, icon: Scale });
                      seen.add('bloodPressureSystolic'); seen.add('bloodPressureDiastolic');
                    }
                    if (v.temperature != null) { items.push({ label: `T ${v.temperature}°C`, icon: Thermometer }); seen.add('temperature'); }
                    if (v.heartRate != null) { items.push({ label: `HR ${v.heartRate} bpm`, icon: HeartPulse }); seen.add('heartRate'); }
                    if (v.spO2 != null) { items.push({ label: `SpO2 ${v.spO2}%`, icon: Droplets }); seen.add('spO2'); }
                    if (v.respiratoryRate != null) { items.push({ label: `RR ${v.respiratoryRate} br/min`, icon: Wind }); seen.add('respiratoryRate'); }
                    if (v.rr != null && !seen.has('respiratoryRate')) { items.push({ label: `RR ${v.rr} br/min`, icon: Wind }); seen.add('rr'); }
                    if (v.sugar != null) { items.push({ label: `Sugar ${v.sugar} mg/dL`, icon: FlaskConical, color: 'var(--amber-600)' }); seen.add('sugar'); }
                    if (v.bloodSugar != null && !seen.has('sugar')) { items.push({ label: `Sugar ${v.bloodSugar} mg/dL`, icon: FlaskConical, color: 'var(--amber-600)' }); seen.add('bloodSugar'); }
                    if (v.weight != null) { items.push({ key: 'weight', value: `${v.weight} kg`, icon: Scale, color: 'var(--green-600)' }); seen.add('weight'); }
                    if (v.height != null) { items.push({ key: 'height', value: `${v.height} cm`, icon: Ruler, color: 'var(--cyan-600)' }); seen.add('height'); }
                    if (v.bmi != null) { items.push({ key: 'bmi', value: `${v.bmi}`, icon: Gauge, color: 'var(--blue-700)' }); seen.add('bmi'); }
                    Object.keys(v).forEach(k => {
                      if (seen.has(k)) return;
                      const val = v[k];
                      if (val == null) return;
                      if (['id','appointmentId','recordedAt','updatedAt','createdAt','patientId','patient_id','bookingId','doctorId','clinicId','status','tokenNo'].includes(k)) return;
                      if (/bmi.*category/i.test(k)) return;
                      if (k.toLowerCase().includes('note')) {
                        items.push({ key: k, label: `${String(val)}`, icon: FileText, color: 'var(--violet-600)' });
                        return;
                      }
                    });
                    return items;
                  };
                  const vitalsList = formatVitalItems(vital);
                  const bodyMetrics = vitalsList.filter(item => ['height','weight','bmi'].includes(item.key));
                  const otherVitals = vitalsList.filter(item => !['height','weight','bmi'].includes(item.key));
                  const bpSugarItems = otherVitals.filter(item => /^(BP |Sugar )/.test(item.label));
                  const hrSpo2RrTempItems = otherVitals.filter(item => /^(HR |SpO2 |RR |T )/.test(item.label));
                  const noteItems = otherVitals.filter(item => item.icon === FileText || (item.key && String(item.key).toLowerCase().includes('note')));
                  const miscItems = otherVitals.filter(item => !bpSugarItems.includes(item) && !hrSpo2RrTempItems.includes(item) && !noteItems.includes(item));

                  return (
                    <article key={row.id || row.timestamp} style={{ background:"var(--white)", border:"1px solid rgba(148,163,184,0.16)", borderRadius:18, padding:"18px 16px", boxShadow:"0 18px 40px rgba(15,23,42,0.08)", display:"flex", flexDirection:"column", gap:12, width:"100%", maxWidth:520, boxSizing:"border-box", margin:"0 auto" }}>
                      <header style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, paddingBottom:12, borderBottom:"1px solid var(--gray-100)" }}>
                        <div>
                          <div style={{ fontSize:15, fontWeight:800, color:"var(--gray-900)", fontFamily:"var(--mono)", letterSpacing:"0.03em" }}>{formatDate(row.timestamp)}</div>
                          <div style={{ fontSize:12, color:"var(--blue-600)", marginTop:4 }}>{formatTime(row.timestamp)}</div>
                        </div>
                        <div style={{ padding:"6px 12px", background: row.isCurrent ? "rgba(59,130,246,0.12)" : "var(--gray-100)", borderRadius:999, fontWeight:700, color: row.isCurrent ? "var(--blue-700)" : "var(--gray-600)", fontSize:11, whiteSpace:"nowrap" }}>{row.type}</div>
                      </header>

                      {bodyMetrics.length > 0 && (
                        <div style={{ display:"flex", gap:8, flexWrap:"nowrap", overflowX:"hidden", marginTop:4, justifyContent:"flex-start" }}>
                          {bodyMetrics.map((item,i) => {
                            const Icon = item.icon || Thermometer;
                            return (
                              <div key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 11px", background:"rgba(15,23,42,0.05)", borderRadius:999, minWidth:74, justifyContent:"center", flex:"0 0 auto" }}>
                                <Icon size={14} strokeWidth={2} color={item.color || 'var(--blue-600)'} />
                                <span style={{ fontSize:12, fontWeight:700, color:"var(--gray-900)" }}>{item.value}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {bpSugarItems.length > 0 && (
                        <div style={{ display:"flex", gap:8, flexWrap:"nowrap", overflowX:"hidden", marginTop:4 }}>
                          {bpSugarItems.map((item,i) => {
                            const Icon = item.icon || Thermometer;
                            return (
                              <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", background:"rgba(15,23,42,0.05)", borderRadius:999, fontSize:12, fontWeight:700, color:"var(--gray-900)", flex:"0 0 auto" }}>
                                <Icon size={14} strokeWidth={2} color={item.color || 'var(--blue-600)'} />
                                {item.label}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {hrSpo2RrTempItems.length > 0 && (
                        <div style={{ display:"flex", gap:8, flexWrap:"nowrap", overflowX:"hidden", marginTop:4 }}>
                          {hrSpo2RrTempItems.map((item,i) => {
                            const Icon = item.icon || Thermometer;
                            return (
                              <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", background:"rgba(15,23,42,0.05)", borderRadius:999, fontSize:12, fontWeight:700, color:"var(--gray-900)", flex:"0 0 auto" }}>
                                <Icon size={14} strokeWidth={2} color={item.color || 'var(--blue-600)'} />
                                {item.label}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {miscItems.length > 0 && (
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:4 }}>
                          {miscItems.map((item,i) => {
                            const Icon = item.icon || Thermometer;
                            return (
                              <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", background:"rgba(15,23,42,0.05)", borderRadius:999, fontSize:12, fontWeight:700, color:"var(--gray-900)" }}>
                                <Icon size={14} strokeWidth={2} color={item.color || 'var(--blue-600)'} />
                                {item.label}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {noteItems.length > 0 && (
                        <div style={{ marginTop:6, padding:"12px 14px", background:"var(--gray-50)", borderRadius:16, color:"var(--gray-900)", fontSize:13, lineHeight:1.45, minHeight:44, display:"flex", alignItems:"center" }}>
                          <FileText size={14} strokeWidth={2} color="var(--violet-600)" style={{ marginRight:8 }} />
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", width:"100%", color:"var(--gray-900)" }}>{noteItems.map(item => item.label).join(' - ')}</span>
                        </div>
                      )}

                      <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid var(--gray-100)" }}>
                        <div style={{ fontSize:14, color:"var(--blue-900)", fontWeight:800, lineHeight:1.4 }}>{rx.diagnosis || "-"}</div>
                        {rx.notes ? <div style={{ marginTop:6, fontSize:13, color:"var(--gray-600)", lineHeight:1.5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{rx.notes}</div> : null}
                      </div>

                      <div style={{ display:"flex", gap:8, marginTop:6 }}>
                        {row.isCurrent ? (
                          <Btn variant="primary" size="sm" onClick={onWritePrescription} style={{ flex:1 }}><Pill size={14} /> Write Rx</Btn>
                        ) : rx.id ? (
                          <Btn variant="view" size="sm" onClick={() => { const appUrl = appConfig.dashboardUrl || process.env.REACT_APP_DASHBOARD_URL || window.location.origin; window.open(rx.imageUrl || `${appUrl}/prescription-view/${rx.id}`, "_blank"); }} style={{ flex:1 }}><Eye size={14} /> View</Btn>
                        ) : (
                          <div style={{ color:"var(--gray-400)", fontSize:13, alignSelf:"center", flex:1 }}>No Rx</div>
                        )}
                        {report && <Btn variant="view" size="sm" onClick={() => openReport(report)} style={{ flex:1 }}><FileText size={14} /> View Report</Btn>}

                        {/* Payment collection removed from history cards */}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <style>{`
          @media (max-width: 768px) {
            .hist-table-desktop { display: none !important; height:100% !important; min-height:0 !important; flex:1 !important; }
            .hist-cards-mobile { display: flex !important; height:100% !important; min-height:0 !important; flex:1 !important; align-items:stretch !important; padding-bottom:0 !important; }
          }
          @media (min-width: 769px) {
            .hist-table-desktop { display: flex !important; height:100% !important; min-height:0 !important; flex:1 !important; }
            .hist-cards-mobile { display: none !important; height:100% !important; min-height:0 !important; flex:1 !important; }
          }
        `}</style>
      </div>
    </div>
  );
}

/* ─── Vital Cell ─────────────────────────────────────────── */
function VitalCell({ label:lbl, value, unit, color="var(--blue-600)", icon:Ico }) {
  if (!value) return null;
  return (
    <div style={{ background:"var(--gray-50)", border:"1px solid var(--gray-200)", borderRadius:"var(--r-lg)", padding:"10px 9px", textAlign:"center", transition:"background 0.13s", cursor:"default" }}
      onMouseEnter={e => e.currentTarget.style.background="var(--blue-50)"}
      onMouseLeave={e => e.currentTarget.style.background="var(--gray-50)"}>
      {Ico && <Ico size={13} color={color} strokeWidth={2} style={{ margin:"0 auto 5px", display:"block" }} />}
      <div style={{ fontSize:9, fontWeight:700, color:"var(--gray-400)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>{lbl}</div>
      <div style={{ fontSize:16, fontWeight:800, color, fontFamily:"var(--mono)", letterSpacing:"-0.5px", lineHeight:1 }}>{value}</div>
      {unit && <div style={{ fontSize:8.5, color:"var(--gray-400)", marginTop:3 }}>{unit}</div>}
    </div>
  );
}

export function LegacyClinicalDocumentsPanel({ patient, clinicId, onClose }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editedTypes, setEditedTypes] = useState({});
  const [documentType, setDocumentType] = useState("");
  const [reportName, setReportName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const patientName = patient?.patientName || "Patient";
  const reportCount = documents.filter(document => document.documentType === "Lab report").length;
  const scanCount = documents.filter(document => document.documentType === "Scan / image").length;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    axios.get(buildApiUrl(`clinical-documents/patient/${patient?.patientId}`), { headers:{ "X-Clinic-Id":clinicId } })
      .then(response => { if (!cancelled) setDocuments(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (!cancelled) { setDocuments([]); toast.error("Unable to load patient documents"); } })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [clinicId, patient?.id, patient?.patientId]);

  const handleFiles = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSelectedFile(file);
    setReportName("");
    setDocumentType("");
    setShowUploadModal(true);
  };

  const handleDrop = event => {
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setReportName("");
    setDocumentType("");
    setShowUploadModal(true);
  };

  const openDocument = async document => {
    try {
      const response = await fetch(buildApiUrl(`clinical-documents/${document.id}/content`), { headers:{ "X-Clinic-Id":clinicId } });
      if (!response.ok) throw new Error("Unable to open document");
      const objectUrl = URL.createObjectURL(await response.blob());
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) { toast.error(error.message || "Unable to open document"); }
  };

  const uploadReport = async () => {
    if (!selectedFile || !reportName.trim() || !documentType) {
      toast.error("Enter a filename and select a document type");
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("appointmentId", patient.id);
      formData.append("patientId", patient.patientId);
      formData.append("documentType", documentType);
      formData.append("reportName", reportName.trim());
      const response = await axios.post(buildApiUrl("clinical-documents/upload"), formData, { headers:{ "X-Clinic-Id":clinicId } });
      setDocuments(previous => [response.data, ...previous]);
      setReportName("");
      setDocumentType("");
      setSelectedFile(null);
      setShowUploadModal(false);
      toast.success("Report uploaded");
    } catch (error) { toast.error(error.response?.data?.message || "Document upload failed"); }
    finally { setIsUploading(false); }
  };

  const removeDocument = async document => {
    try {
      await axios.delete(buildApiUrl(`clinical-documents/${document.id}`), { headers:{ "X-Clinic-Id":clinicId } });
      setDocuments(previous => previous.filter(item => item.id !== document.id));
      toast.success("Document removed");
    } catch (error) { toast.error(error.response?.data?.message || "Unable to remove document"); }
  };

  const updateRecords = async () => {
    const changes = Object.entries(editedTypes);
    if (!changes.length) return;
    setIsUpdating(true);
    try {
      const updated = await Promise.all(changes.map(([id, documentType]) => axios.patch(buildApiUrl(`clinical-documents/${id}`), null, { params:{ documentType }, headers:{ "X-Clinic-Id":clinicId } })));
      const savedById = Object.fromEntries(updated.map(response => [response.data.id, response.data]));
      setDocuments(previous => previous.map(document => savedById[document.id] || document));
      setEditedTypes({});
      toast.success("Document records updated");
    } catch (error) { toast.error(error.response?.data?.message || "Unable to update records"); }
    finally { setIsUpdating(false); }
  };

  const newDocuments = documents.filter(document => document.appointmentId === patient?.id);
  const olderDocuments = documents.filter(document => document.appointmentId !== patient?.id);
  const renderDocumentTable = (items, emptyMessage) => items.length ? <div className="clinical-document-table-scroll">
    <div className="clinical-document-table-head" style={{ display:"grid", gridTemplateColumns:"minmax(220px, 1fr) 130px 90px 145px 118px", gap:14, alignItems:"center", padding:"10px 20px", background:"var(--gray-50)", borderBottom:"1px solid var(--border)", color:"var(--gray-400)", fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase" }}><span>Document</span><span>Category</span><span>Size</span><span>Added</span><span>Actions</span></div>
    {items.map(document => <div className="clinical-document-row" key={document.id} style={{ display:"grid", gridTemplateColumns:"minmax(220px, 1fr) 130px 90px 145px 42px", gap:14, alignItems:"center", padding:"13px 20px", borderBottom:"1px solid var(--border-light)", transition:"background 0.15s" }}>
      <div style={{ display:"flex", alignItems:"center", gap:11, minWidth:0 }}><div style={{ width:36, height:36, borderRadius:"var(--r-md)", background:document.fileName?.toLowerCase().endsWith(".pdf") ? "var(--red-50)" : "var(--cyan-50)", color:document.fileName?.toLowerCase().endsWith(".pdf") ? "var(--red-600)" : "var(--cyan-600)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{document.fileName?.toLowerCase().endsWith(".pdf") ? <FileType size={17} /> : <FileImage size={17} />}</div><div style={{ minWidth:0 }}><div style={{ fontSize:13, fontWeight:700, color:"var(--gray-800)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{document.fileName}</div><div style={{ fontSize:11, color:"var(--gray-400)", marginTop:3 }}>Patient visit document</div></div></div>
      <select value={editedTypes[document.id] ?? document.documentType} onChange={event => setEditedTypes(previous => ({ ...previous, [document.id]:event.target.value }))} aria-label={`Category for ${document.fileName}`} style={{ width:"100%", border:"1px solid var(--gray-200)", borderRadius:"var(--r-md)", background:"var(--white)", color:"var(--gray-700)", padding:"6px 5px", fontSize:11, fontWeight:600, outline:"none" }}><option>Lab report</option><option>Scan / image</option><option>Referral letter</option><option>Medical certificate</option><option>Other document</option></select><span style={{ color:"var(--gray-500)", fontSize:11.5, fontFamily:"var(--mono)" }}>{document.fileSize ? `${(document.fileSize / 1024 / 1024).toFixed(1)} MB` : "-"}</span><span style={{ color:"var(--gray-500)", fontSize:11.5, whiteSpace:"nowrap" }}>{document.createdAt ? new Date(document.createdAt).toLocaleString("en-IN", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "-"}</span>
      <div className="report-row-actions"><button type="button" onClick={() => openDocument(document)} title={`View ${document.fileName}`}><Eye size={15} /></button><button type="button" onClick={() => openDocument(document)} title={`Download ${document.fileName}`}><Download size={15} /></button><button type="button" onClick={() => removeDocument(document)} title={`Remove ${document.fileName}`}><Trash2 size={15} /></button></div>
    </div>)}
  </div> : <div className="clinical-document-empty" style={{ padding:"34px 20px", textAlign:"center", color:"var(--gray-500)" }}><FileText size={25} color="var(--gray-300)" style={{ marginBottom:8 }} /><div style={{ fontSize:13, fontWeight:700 }}>{emptyMessage}</div></div>;

  return (
    <div style={{ height:"100%", overflowY:"auto", background:"var(--bg)" }}>
      <div className="documents-workspace" style={{ maxWidth:1100, margin:"0 auto", padding:"26px 24px 48px" }}>
        <div className="documents-hero" style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:18, marginBottom:20, padding:"20px 22px", background:"linear-gradient(135deg, var(--blue-800), #1e3a8a 58%, var(--violet-700))", borderRadius:"var(--r-xl)", color:"#fff", boxShadow:"0 10px 24px rgba(30,64,175,0.18)" }}>
          <div>
            <button type="button" onClick={onClose} style={{ display:"inline-flex", alignItems:"center", gap:6, border:0, background:"transparent", color:"rgba(255,255,255,0.76)", fontSize:12, fontWeight:700, padding:0, cursor:"pointer", marginBottom:18 }}><ArrowRight size={14} style={{ transform:"rotate(180deg)" }} /> Back to patient dashboard</button>
            <div style={{ display:"flex", alignItems:"center", gap:13 }}>
              <div style={{ width:46, height:46, borderRadius:"var(--r-xl)", background:"rgba(255,255,255,0.14)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid rgba(255,255,255,0.2)" }}><FileText size={22} /></div>
              <div><h2 style={{ fontSize:24, fontWeight:800, color:"#fff", letterSpacing:"-0.04em" }}>Reports & documents</h2><p style={{ color:"rgba(255,255,255,0.72)", fontSize:13, marginTop:4 }}>{patientName} <span style={{ color:"rgba(255,255,255,0.35)", margin:"0 7px" }}>•</span> Token #{patient?.tokenNo || "—"}</p></div>
            </div>
          </div>
          <button type="button" onClick={onClose} title="Close documents" style={{ width:36, height:36, borderRadius:"var(--r-lg)", border:"1px solid rgba(255,255,255,0.2)", background:"rgba(255,255,255,0.12)", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><X size={17} /></button>
        </div>

        <div className="documents-summary" style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:10, marginBottom:18 }}>
          {[ ["All documents", documents.length, FileText, "var(--blue-600)"], ["Lab reports", reportCount, FlaskConical, "var(--violet-600)"], ["Scans & images", scanCount, FileImage, "var(--cyan-600)"] ].map(([label, value, Icon, color]) => <div key={label} style={{ display:"flex", alignItems:"center", gap:11, padding:"12px 15px", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--r-lg)", boxShadow:"var(--sh-xs)" }}><div style={{ width:32, height:32, borderRadius:"var(--r-md)", background:"var(--gray-100)", color, display:"flex", alignItems:"center", justifyContent:"center" }}><Icon size={16} /></div><div><div style={{ fontSize:18, lineHeight:1, fontWeight:800, color:"var(--gray-900)" }}>{value}</div><div style={{ fontSize:10.5, color:"var(--gray-500)", marginTop:4 }}>{label}</div></div></div>)}
        </div>

        <div className="documents-layout" style={{ display:"grid", gridTemplateColumns:"minmax(0, 1.7fr) minmax(300px, 0.9fr)", gap:20, alignItems:"start" }}>
          <section className="documents-list-panel" style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--r-xl)", boxShadow:"var(--sh-sm)", overflow:"hidden" }}>
            <div style={{ padding:"18px 20px", borderBottom:"1px solid var(--border-light)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}><div><h3 style={{ fontSize:15, fontWeight:800, color:"var(--gray-900)" }}>Attached documents</h3><p style={{ fontSize:12, color:"var(--gray-500)", marginTop:4 }}>Files shared for this patient visit</p></div><div style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"5px 9px", borderRadius:"var(--r-full)", background:"var(--green-50)", border:"1px solid var(--green-200)", color:"var(--green-700)", fontSize:11, fontWeight:700 }}><BadgeCheck size={12} /> Ready</span><button type="button" disabled={!Object.keys(editedTypes).length || isUpdating} onClick={updateRecords} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 11px", border:0, borderRadius:"var(--r-md)", background:Object.keys(editedTypes).length ? "var(--blue-600)" : "var(--gray-100)", color:Object.keys(editedTypes).length ? "#fff" : "var(--gray-400)", fontSize:11, fontWeight:800, cursor:Object.keys(editedTypes).length ? "pointer" : "not-allowed" }}>{isUpdating ? <RefreshCw size={13} className="anim-spin" /> : <CheckCircle2 size={13} />} {isUpdating ? "Saving..." : "Update records"}</button></div></div>
            {isLoading ? <div style={{ padding:"52px 20px", textAlign:"center", color:"var(--gray-500)" }}><RefreshCw size={24} className="anim-spin" color="var(--blue-500)" style={{ marginBottom:10 }} /><div style={{ fontSize:13, fontWeight:700 }}>Loading documents...</div></div> : <>
              <div style={{ padding:"14px 20px 8px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid var(--border-light)" }}><div><h4 style={{ fontSize:13, fontWeight:800, color:"var(--blue-800)" }}>New records</h4><p style={{ fontSize:11, color:"var(--gray-500)", marginTop:3 }}>Documents from this visit</p></div><span style={{ fontSize:11, fontWeight:800, color:"var(--blue-700)", background:"var(--blue-50)", padding:"4px 8px", borderRadius:"var(--r-full)" }}>{newDocuments.length}</span></div>
              {renderDocumentTable(newDocuments, "No new documents for this visit")}
              <div style={{ borderTop:"5px solid var(--gray-50)", padding:"14px 20px 8px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid var(--border-light)" }}><div><h4 style={{ fontSize:13, fontWeight:800, color:"var(--gray-700)" }}>Older records</h4><p style={{ fontSize:11, color:"var(--gray-500)", marginTop:3 }}>Documents from previous visits</p></div><span style={{ fontSize:11, fontWeight:800, color:"var(--gray-600)", background:"var(--gray-100)", padding:"4px 8px", borderRadius:"var(--r-full)" }}>{olderDocuments.length}</span></div>
              {renderDocumentTable(olderDocuments, "No older documents available")}
            </>}
          </section>

          <section className="documents-upload-panel" style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--r-xl)", boxShadow:"var(--sh-sm)", padding:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:5 }}><div style={{ width:30, height:30, borderRadius:"var(--r-md)", background:"var(--blue-100)", color:"var(--blue-700)", display:"flex", alignItems:"center", justifyContent:"center" }}><Upload size={15} /></div><h3 style={{ fontSize:15, fontWeight:800, color:"var(--gray-900)" }}>Add a document</h3></div><p style={{ fontSize:12, lineHeight:1.6, color:"var(--gray-500)", margin:"0 0 18px 39px" }}>Upload reports, scans, referrals, and other patient files.</p>
            <div className={`report-dropzone${isDragActive ? " is-dragging" : ""}`} onDragOver={event => { event.preventDefault(); setIsDragActive(true); }} onDragLeave={() => setIsDragActive(false)} onDrop={handleDrop}>
              <CloudUpload size={34} strokeWidth={1.7} /><strong>{isUploading ? "Uploading..." : "Drag & drop files here"}</strong><span>or</span><button type="button" disabled={isUploading} onClick={() => fileInputRef.current?.click()}>Choose files</button><small>Supported formats: PDF, JPG, PNG, DICOM<br />Max file size: 50 MB</small>
            </div>
            <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={handleFiles} style={{ display:"none" }} />
            <div className="report-quick-tips"><div className="quick-tips-title"><Info size={14} /> Quick tips</div><div><BadgeCheck size={14} /> Upload lab reports, scans, or referrals</div><div><BadgeCheck size={14} /> Files are securely stored in the patient record</div><div><BadgeCheck size={14} /> Add one file at a time with a clear name</div></div>
          </section>
        </div>
      </div>
      {showUploadModal && <ReportUploadModal selectedFile={selectedFile} reportName={reportName} setReportName={setReportName} documentType={documentType} setDocumentType={setDocumentType} isUploading={isUploading} onUpload={uploadReport} onClose={() => { setShowUploadModal(false); setSelectedFile(null); }} />}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        .documents-workspace { font-family:'DM Sans', var(--font, sans-serif); color:#183b4c; }
        .documents-workspace h2, .documents-workspace h3, .documents-workspace h4 { font-family:'Space Grotesk', var(--font, sans-serif); letter-spacing:-.02em; }
        .documents-workspace > .documents-hero { position:relative; overflow:hidden; background:linear-gradient(118deg, #092d4b 0%, #075e75 56%, #0e918c 100%) !important; border:1px solid rgba(255,255,255,.16); box-shadow:0 18px 38px rgba(8,65,83,.22) !important; }
        .documents-workspace > .documents-hero::after { content:''; position:absolute; width:260px; height:260px; right:-70px; top:-115px; border:1px solid rgba(255,255,255,.12); border-radius:50%; box-shadow:0 0 0 28px rgba(255,255,255,.035), 0 0 0 56px rgba(255,255,255,.025); pointer-events:none; }
        .documents-workspace > .documents-hero > * { position:relative; z-index:1; }
        .documents-workspace { width:100% !important; max-width:1440px !important; height:100% !important; min-height:0 !important; overflow:hidden !important; display:flex; flex-direction:column; padding:28px clamp(18px, 3vw, 42px) 24px !important; }
        .documents-layout { grid-template-columns:minmax(0, 1fr) minmax(320px, 380px) !important; gap:24px !important; flex:1; min-height:0; align-items:stretch !important; }
        .documents-list-panel, .documents-upload-panel { min-width:0; border-color:#d5e5e8 !important; box-shadow:0 16px 38px rgba(20,67,82,.08) !important; }
        .documents-list-panel { display:flex; flex-direction:column; min-height:0; }
        .documents-list-panel > div:first-child { flex-shrink:0; }
        .documents-list-panel > div:nth-child(2) { min-height:0; }
        .clinical-document-table-scroll { max-height:clamp(150px, 22vh, 260px); overflow-y:auto; overflow-x:hidden; scrollbar-gutter:stable; }
        .clinical-document-table-head { position:sticky; top:0; z-index:2; }
        .report-row-actions { display:flex; align-items:center; justify-content:flex-start; gap:8px; }
        .report-row-actions button { width:28px; height:28px; display:grid; place-items:center; border:0; border-radius:8px; background:transparent; color:#2563eb; cursor:pointer; transition:all .16s ease; }
        .report-row-actions button:hover { background:#e5f3f5; color:#075985; transform:translateY(-1px); }
        .report-row-actions button:last-child { color:#ef4444; }
        .report-row-actions button:last-child:hover { background:#fff1f2; color:#dc2626; }
        .report-dropzone { min-height:196px; border:1.5px dashed #65c9c5; border-radius:16px; background:linear-gradient(145deg,#f1fffe,#e6f8f7); color:#138b88; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; text-align:center; transition:all .18s ease; }
        .report-dropzone.is-dragging, .report-dropzone:hover { border-color:#0e9f98; background:linear-gradient(145deg,#e5fbf9,#d8f4f1); box-shadow:0 10px 24px rgba(14,159,152,.12); transform:translateY(-1px); }
        .report-dropzone strong { color:#087f7a; font-size:13px; }
        .report-dropzone > span { color:#79929a; font-size:11px; }
        .report-dropzone button { border:0; border-radius:9px; padding:9px 22px; background:linear-gradient(135deg,#6366f1,#7c3aed); color:#fff; font:inherit; font-size:12px; font-weight:800; cursor:pointer; box-shadow:0 7px 15px rgba(99,102,241,.2); }
        .report-dropzone button:hover { filter:brightness(1.05); transform:translateY(-1px); }
        .report-dropzone small { color:#78919a; font-size:10px; line-height:1.5; }
        .report-quick-tips { margin-top:22px; padding-top:18px; border-top:1px solid #dbeaec; color:#52717c; font-size:11px; line-height:1.35; }
        .report-quick-tips > div { display:flex; align-items:center; gap:8px; margin-top:9px; }
        .report-quick-tips > div:not(.quick-tips-title) svg { color:#16a394; flex-shrink:0; }
        .quick-tips-title { color:#214959; font-size:12px; font-weight:800; }
        .documents-upload-panel { position:sticky; top:18px; background:linear-gradient(180deg,#ffffff 0%,#f5fbfb 100%) !important; }
        .documents-hero { padding:24px 28px !important; min-height:142px; }
        .documents-summary { gap:14px !important; }
        .documents-summary > div { min-height:82px; padding:16px 18px !important; border-color:#d5e5e8 !important; box-shadow:0 10px 24px rgba(20,67,82,.055) !important; }
        .documents-summary > div > div:first-child { width:38px !important; height:38px !important; border-radius:11px !important; }
        .clinical-document-table-head, .clinical-document-row { grid-template-columns:minmax(240px, 1fr) 145px 92px 150px 118px !important; }
        .clinical-document-table-head { padding:12px 22px !important; background:#eff8f9 !important; color:#68858e !important; }
        .clinical-document-row { padding:15px 22px !important; background:#fff; }
        .clinical-document-row > div:first-child > div:first-child { width:40px !important; height:40px !important; border-radius:11px !important; }
        .documents-list-panel > div:first-child { padding:21px 22px !important; background:linear-gradient(180deg,#fff,#f8fcfc); }
        .documents-list-panel h3 { font-size:17px !important; }
        .documents-upload-panel h3 { font-size:17px !important; }
        .documents-upload-panel > .report-dropzone { min-height:196px; }
        .clinical-document-row:hover { background:var(--gray-50); }
        @media (max-width: 1100px) {
          .documents-layout { grid-template-columns:minmax(0, 1fr) minmax(280px, 330px) !important; gap:16px !important; }
          .clinical-document-table-head, .clinical-document-row { grid-template-columns:minmax(190px, 1fr) 120px 78px 104px !important; }
          .clinical-document-table-head span:nth-child(4), .clinical-document-row > span:nth-child(4) { display:none; }
        }
        @media (max-width: 760px) {
          .documents-workspace { padding:16px 12px 36px !important; }
          .documents-layout, .documents-summary { grid-template-columns:1fr !important; }
          .documents-upload-panel { position:static; }
          .documents-workspace { overflow-y:auto !important; }
          .clinical-document-table-scroll { max-height:240px; }
          .documents-hero { padding:18px !important; min-height:0; }
          .documents-hero h2 { font-size:20px !important; }
          .documents-summary { gap:9px !important; }
          .documents-summary > div { min-height:62px; }
          .documents-upload-panel { padding:17px !important; }
          .documents-upload-panel > button { min-height:145px !important; }
          .documents-list-panel > div:first-child { padding:17px !important; }
          .clinical-document-table-head, .clinical-document-row { padding-left:15px !important; padding-right:15px !important; }
          .clinical-document-table-head { display:none !important; }
          .clinical-document-row { grid-template-columns:minmax(0, 1fr) 104px !important; gap:10px !important; }
          .clinical-document-row > span { display:none; }
          .report-row-actions { justify-content:flex-end; gap:4px; }
        }
      `}</style>
    </div>
  );
}


/* ─── MAIN DASHBOARD ─────────────────────────────────────── */
export default function DoctorOpdSessionDashboard() {
  const { scheduleId } = useParams();
  const { auth } = useAuth();
  const CLINIC_ID = auth?.clinic?.id || null;

  const [sessionInfo, setSessionInfo] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [queue, setQueue] = useState({ current: null });
  const [clock, setClock] = useState(getISTDate());
  const [sessionStatus, setSessionStatus] = useState("NOT_STARTED");
  const [searchQuery, setSearchQuery] = useState("");
  const [showRxModal, setShowRxModal] = useState(false);
  const [showCompleteReviewModal, setShowCompleteReviewModal] = useState(false);
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [doctorEPrescriptionEnabled, setDoctorEPrescriptionEnabled] = useState(true);
  const [showWalkInSuccess, setShowWalkInSuccess] = useState(false);
  const [generatedWalkInData, setGeneratedWalkInData] = useState(null);
  const [activePatient, setActivePatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [currentAppointmentVital, setCurrentAppointmentVital] = useState(null);
  const [isLoadingVisitReview, setIsLoadingVisitReview] = useState(false);
  const [walkInForm, setWalkInForm] = useState({ patientName:"", phone:"", age:"", gender:"MALE" });
  const [showPaymentWorkflow, setShowPaymentWorkflow] = useState(false);
  const [lastPrescriptionId] = useState(null);
  const [paymentPatientInfo, setPaymentPatientInfo] = useState(null);
  const [, setPrescriptionDataMap] = useState({});
  const [paymentDataMap, setPaymentDataMap] = useState({});
  const [historyAppointmentStatusMap, setHistoryAppointmentStatusMap] = useState({});
  const [walkInPreviousBooking, setWalkInPreviousBooking] = useState(null);
  const [walkInAccessCode, setWalkInAccessCode] = useState("");
  const [walkInFamilyMembers, setWalkInFamilyMembers] = useState([]);
  const [isNewWalkInPatient, setIsNewWalkInPatient] = useState(false);
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [vitalsPatientData, setVitalsPatientData] = useState(null);
  const [, setVitalsPatientId] = useState(null);
  const [pendingAppointmentForVitals, setPendingAppointmentForVitals] = useState(null);
  const vitalsModalCompleted = useRef(false);
  const [showConsultationPaymentModal, setShowConsultationPaymentModal] = useState(false);
  const [consultationPaymentAppointment, setConsultationPaymentAppointment] = useState(null);
  const [patientInvoiceMap, setPatientInvoiceMap] = useState({});
  const [patientInvoiceModal, setPatientInvoiceModal] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!CLINIC_ID) return;
    const h = { "X-Clinic-Id": CLINIC_ID };
    try {
      const [sRes, bRes, qRes] = await Promise.all([
        fetch(buildApiUrl(`doctor-booking/find-by-code/${scheduleId}`), { headers:h }),
        fetch(buildApiUrl(`appointments/doctor/bookings?scheduleId=${scheduleId}`), { headers:h }),
        fetch(buildApiUrl(`appointments/live-queue?scheduleId=${scheduleId}`), { headers:h }),
      ]);
      if (sRes.ok) { const d=await sRes.json(); setSessionInfo(d); setSessionStatus(d.opdStatus||"NOT_STARTED"); }
      if (bRes.ok) {
        const bd=await bRes.json()||[];
        setBookings(bd);
        const pm={}, pay={};
        for (const b of bd) {
          if (b.patientId) { try { const p=await fetch(buildApiUrl(`prescriptions/patient/${b.patientId}`),{headers:h}); if (p.ok) { const rxs=await p.json()||[]; rxs.forEach(rx => { if (rx.id&&rx.appointmentId) pm[rx.appointmentId]=rx; }); } } catch {} }
          if (b.id) { try { const rev=await fetch(buildApiUrl(`clinic/revenue-payment/appointment/${b.id}`),{headers:h}); if (rev.ok) { const r=await rev.json(); if (r.success&&r.data) pay[b.id]=r.data; } } catch {} }
        }
        setPrescriptionDataMap(pm); setPaymentDataMap(pay);
      }
      const invoiceRes = await fetch(buildApiUrl("clinic/patient-invoices"), { headers:h });
      if (invoiceRes.ok) {
        const invoicePayload = await invoiceRes.json();
        const invoiceMap = {};
        (invoicePayload?.data || []).forEach(invoice => { if (invoice.appointmentId) invoiceMap[invoice.appointmentId] = invoice; });
        setPatientInvoiceMap(invoiceMap);
      } else {
        setPatientInvoiceMap({});
      }
      if (qRes.ok) setQueue(await qRes.json());
    } catch (e) { console.error(e); }
  }, [scheduleId, CLINIC_ID]);

  useEffect(() => { fetchAll(); const iv=setInterval(fetchAll,5000); return ()=>clearInterval(iv); },[fetchAll]);
  useEffect(() => { const t=setInterval(()=>setClock(getISTDate()),1000); return ()=>clearInterval(t); },[]);

  const loadHistoryStatuses = useCallback(async (ph, vh) => {
    if (!CLINIC_ID) return;
    const ids = new Set();
    [...(ph||[]),...(vh||[])].forEach(item => { const id=item.appointmentId||item.appointment_id; if (id) ids.add(String(id)); });
    if (!ids.size) { setHistoryAppointmentStatusMap({}); return; }
    try {
      const results = await Promise.allSettled(Array.from(ids).map(id => axios.get(buildApiUrl(`appointments/${id}`),{headers:{"X-Clinic-Id":CLINIC_ID}}).then(r => ({ id, status:r?.data?.status||null }))));
      const map={};
      results.forEach(r => { if (r.status==="fulfilled"&&r.value?.id&&r.value?.status) map[r.value.id]=r.value.status; });
      setHistoryAppointmentStatusMap(map);
    } catch {}
  },[CLINIC_ID]);

  useEffect(() => { if (patientHistory.length||vitalsHistory.length) loadHistoryStatuses(patientHistory,vitalsHistory); },[patientHistory,vitalsHistory,loadHistoryStatuses]);

  useEffect(() => {
    const doctorId=sessionInfo?.doctorId;
    if (!doctorId||!CLINIC_ID) return;
    axios.get(buildApiUrl(`doctors/e-prescription/${doctorId}`),{headers:{"X-Clinic-Id":CLINIC_ID}})
      .then(r => { if (r.data?.success) setDoctorEPrescriptionEnabled(r.data.enableEPrescription!==false); })
      .catch(()=>setDoctorEPrescriptionEnabled(true));
  },[sessionInfo?.doctorId,CLINIC_ID]);

  useEffect(() => {
    const handler = e => {
      if (e.detail?.appointmentId) setPaymentDataMap(prev => ({ ...prev, [e.detail.appointmentId]:{ paymentStatus:"COLLECTED", paymentMethod:e.detail.paymentMethod } }));
      fetchAll();
    };
    window.addEventListener("paymentCollected",handler);
    return ()=>window.removeEventListener("paymentCollected",handler);
  },[fetchAll]);

  useEffect(() => {
    const phone = String(walkInForm.phone || "").replace(/\D/g, "");
    if (phone.length===10) {
      fetch(buildApiUrl(`appointments/patients-by-phone?phone=${encodeURIComponent(phone)}&status=COMPLETED`), { headers: { "X-Clinic-Id": String(CLINIC_ID || "") } })
        .then(r=>r.ok ? r.json() : Promise.reject(new Error("Patient lookup failed")))
        .then(data => {
          const members = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
          if (members.length>0) { setWalkInFamilyMembers(members); const code=members.find(m=>m.accessCode)?.accessCode||""; setWalkInAccessCode(code); const p=members[0]; setWalkInForm(prev=>({...prev,phone,patientName:p.patientName||prev.patientName,age:p.age||prev.age,gender:p.gender||"MALE"})); setIsNewWalkInPatient(false); setWalkInPreviousBooking(p.appointmentDate?{appointmentDate:p.appointmentDate,slotTime:p.slotTime}:null); }
          else { setWalkInFamilyMembers([]); setWalkInPreviousBooking(null); setWalkInAccessCode(""); setIsNewWalkInPatient(true); }
        })
        .catch(()=>{ setWalkInFamilyMembers([]); setWalkInPreviousBooking(null); setWalkInAccessCode(""); setIsNewWalkInPatient(true); });
    } else { setWalkInFamilyMembers([]); setWalkInPreviousBooking(null); setWalkInAccessCode(""); setIsNewWalkInPatient(false); }
  },[walkInForm.phone, CLINIC_ID]);

  const apiPost = url => axios.post(url,null,{headers:{"X-Clinic-Id":CLINIC_ID}}).then(fetchAll);

  const hydratePatient = async p => {
    if (!p?.patientId) return p;
    try { const r=await axios.get(buildApiUrl(`clinic/patients/id/${p.patientId}`),{headers:{"X-Clinic-Id":CLINIC_ID}}); if (r.data) return {...p,...r.data,id:p.id}; } catch {}
    return p;
  };


  const handleRxSave = async (padData, onProgress) => {
    try {
      if (!doctorEPrescriptionEnabled) {
        toast.error("E-prescription is disabled for this doctor.");
        throw new Error("E-prescription is disabled for this doctor.");
      }
      if (!activePatient?.patientId) {
        toast.error("Patient reference not found.");
        throw new Error("Patient reference not found.");
      }
      const cid=Number(CLINIC_ID);
      if (!cid||Number.isNaN(cid)) {
        toast.error("Clinic context missing.");
        throw new Error("Clinic context missing.");
      }
      const drId = Number(sessionInfo?.doctorId ?? null);
      // upload and inspect response to ensure DIGITAL-generated PDF saved correctly
      console.debug("Uploading prescription file", { size: padData.fileBlob?.size, type: padData.fileBlob?.type, mode: padData.mode });
      const uploadResp = await uploadPrescription(padData.fileBlob,{ appointmentId:activePatient.id, patientId:activePatient.patientId, doctorId:Number.isFinite(drId)&&drId>0?drId:undefined, diagnosis:padData.diagnosis, medicines:padData.medicines, notes:padData.notes, mode:padData.mode, nextFollowUpDate:padData.nextFollowUpDate },cid);
      console.debug("Prescription upload response:", uploadResp);
      if (uploadResp && (uploadResp.id || uploadResp.prescriptionId || uploadResp.prescriptionCode)) {
        onProgress?.({ stage: "updating", completed: 3, prescriptionStatus: uploadResp.status });
      } else {
        throw new Error("Prescription upload completed but server returned an unexpected response.");
      }
      const statusResponse = await axios.put(buildApiUrl(`appointments/update-status/${activePatient.id}?status=COMPLETED`),null,{headers:{"X-Clinic-Id":cid}});
      if (!statusResponse.data?.success || statusResponse.data.status !== "COMPLETED") {
        throw new Error("Prescription was saved, but the visit status was not confirmed by the server.");
      }
      onProgress?.({ stage: "updating", completed: 3, appointmentStatus: statusResponse.data.status });
      toast.success("Prescription and visit saved successfully");
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Save failed: "+err.message);
      throw err;
    }
  };

  const openRx = async p => {
    if (!doctorEPrescriptionEnabled) {
      toast.error("E-prescription is disabled for this doctor.");
      return;
    }
    const enriched=await hydratePatient(p);
    setActivePatient(enriched);
    axios.get(buildApiUrl(`prescriptions/patient/${p.patientId}`),{headers:{"X-Clinic-Id":CLINIC_ID}}).then(r=>{
      let rx=[]; if (Array.isArray(r.data)) rx=r.data; else if (r.data?.data) rx=r.data.data; else if (r.data?.prescriptions) rx=r.data.prescriptions; else if (r.data) rx=[r.data];
      // normalize common follow-up fields into `nextFollowUpDate`
      rx = (rx||[]).map(it => ({
        ...it,
        nextFollowUpDate: it.nextFollowUpDate || it.next_follow_up_date || it.nextFollowupDate || it.nextFollowup || it.next_followup || it.followUpDate || it.followup || it.follow_up_date || it.follow_up || (it.meta && it.meta.nextFollowUpDate) || it.next_follow_up_date_ms || it.next_follow_up_ms || it.next_follow_up
      }));
      setPatientHistory(rx);
    }).catch(()=>setPatientHistory([]));
    vitalMeasurementService.getPatientMeasurements(p.patientId, CLINIC_ID)
      .then((rows) => setVitalsHistory(groupMeasurementHistory(rows)))
      .catch(() => setVitalsHistory([]));
    setShowRxModal(true);
  };

  const openReviewModal = async p => {
    setActivePatient(p); setPatientHistory([]); setVitalsHistory([]); setCurrentAppointmentVital(null);
    setShowCompleteReviewModal(true); setIsLoadingVisitReview(true);
    const h={"X-Clinic-Id":CLINIC_ID};
    try {
      const [pr,vr,avr]=await Promise.allSettled([
        axios.get(buildApiUrl(`clinic/patients/id/${p.patientId}`),{headers:h}),
        vitalMeasurementService.getPatientMeasurements(p.patientId, CLINIC_ID),
        vitalMeasurementService.getAppointmentMeasurements(p.id, sessionInfo?.doctorId, CLINIC_ID),
      ]);
      if (pr.status==="fulfilled"&&pr.value?.data) setActivePatient(prev=>({...prev,...pr.value.data,id:p.id}));
      if (vr.status==="fulfilled") setVitalsHistory(groupMeasurementHistory(vr.value));
      if (avr.status==="fulfilled") setCurrentAppointmentVital(groupMeasurementHistory(avr.value)[0] || null);
    } catch {} finally { setIsLoadingVisitReview(false); }
  };

  const markCompleteAndPay = async p => {
    try {
      const id=p?.appointmentId||p?.id;
      if (!id) throw new Error("Appointment ID missing.");
      await axios.put(buildApiUrl(`appointments/update-status/${id}?status=COMPLETED`),null,{headers:{"X-Clinic-Id":Number(CLINIC_ID)}});
      setShowCompleteReviewModal(false); setActivePatient(null);
      await fetchAll();
    } catch (err) { alert("Error: "+err.message); }
  };

  const openPatientInvoice = b => {
    const invoice = patientInvoiceMap[b.id];
    setPatientInvoiceModal({ appointment: b, invoice, mode: invoice?.invoiceStatus === "FINALIZED" ? "payment" : "invoice" });
  };

  const handleOpenPayment = useCallback(async b => {
    let fee = Number(b.consultationFee ?? b.fee ?? 0);
    if (!fee && b.id) {
      try {
        const r = await fetch(buildApiUrl(`clinic/revenue-payment/appointment/${b.id}`), { headers: { "X-Clinic-Id": CLINIC_ID } });
        if (r.ok) {
          const d = await r.json();
          if (d?.data?.consultationFee) fee = Number(d.data.consultationFee);
        }
      } catch {}
    }
    setConsultationPaymentAppointment({
      id: b.id,
      patientId: b.patientId,
      patientName: b.patientName,
      consultationFee: fee || paymentDataMap[b.id]?.consultationFee || 0,
      paymentStatus: paymentDataMap[b.id]?.paymentStatus || "PENDING",
      status: b.status
    });
    setShowConsultationPaymentModal(true);
  }, [CLINIC_ID, paymentDataMap]);

  useEffect(() => {
    const handler = (e) => {
      try {
        const appt = e?.detail?.appointment;
        if (appt) handleOpenPayment(appt);
      } catch (err) { console.error(err); }
    };
    window.addEventListener('openPaymentForAppointment', handler);
    return () => window.removeEventListener('openPaymentForAppointment', handler);
  }, [handleOpenPayment]);

  const resolveHonorific = p => {
    const raw = String(p?.gender||p?.patientGender||"").trim().toLowerCase();
    if (["m","male"].includes(raw)) return "Mr.";
    if (["f","female"].includes(raw)) return "Ms/Mrs";
    return "";
  };

  const resolveDisplayName = p => {
    if (!p) return "";
    const honorific = resolveHonorific(p);
    const name = p?.patientName ? String(p.patientName) : "";
    return `${honorific} ${name}`.trim();
  };

  const handleWalkInSubmit = async e => {
    e.preventDefault();
    try {
      const r=await axios.post(buildApiUrl(`appointments/walk-in?${new URLSearchParams({ patientName:walkInForm.patientName, phone:walkInForm.phone, age:parseInt(walkInForm.age), gender:walkInForm.gender, scheduleId, ...(walkInAccessCode&&{accessCode:walkInAccessCode}) })}`),null,{headers:{"X-Clinic-Id":CLINIC_ID}});
      if (r.data.success) {
        const { patientId, appointmentId, token, vaultPin }=r.data;
        const displayName = `${resolveHonorific(walkInForm)} ${walkInForm.patientName}`.trim();
        setVitalsPatientData({ patientName:displayName, patientId, appointmentId });
        setVitalsPatientId(patientId);
        setPendingAppointmentForVitals({ id:appointmentId, isNewWalkIn:true });
        setGeneratedWalkInData({ name:displayName, token, accessCode:vaultPin, patientId, appointmentId, status:"Vitals pending" });
        setShowVitalsModal(true); setShowWalkIn(false);
        setWalkInForm({ patientName:"", phone:"", age:"", gender:"MALE" });
      } else throw new Error(r.data.message||"Registration failed");
    } catch (err) { alert(`Error: ${err.message}`); }
  };

  const handleVitalsSave = async (isSkipped=false) => {
    try {
      const pa=pendingAppointmentForVitals;
      let shouldShowSuccess=false, updatedId=null, newStatus=null;
      if (pa) {
        vitalsModalCompleted.current=true; updatedId=pa.id;
        const r=await axios.put(buildApiUrl(`appointments/update-status/${pa.id}?status=ARRIVED`),null,{headers:{"X-Clinic-Id":CLINIC_ID}});
        if (!r.data?.success) throw new Error("Failed to update status");
        newStatus=r.data?.status; if (pa.isNewWalkIn) shouldShowSuccess=true;
        if (r.data?.token != null) {
          setGeneratedWalkInData(prev => prev ? { ...prev, token: r.data.token } : prev);
        }
        setPendingAppointmentForVitals(null);
      } else if (generatedWalkInData?.appointmentId) {
        vitalsModalCompleted.current=true; updatedId=generatedWalkInData.appointmentId;
        const r=await axios.put(buildApiUrl(`appointments/update-status/${generatedWalkInData.appointmentId}?status=ARRIVED`),null,{headers:{"X-Clinic-Id":CLINIC_ID}});
        if (!r.data?.success) throw new Error("Failed to update status");
        newStatus=r.data?.status; shouldShowSuccess=true;
        if (r.data?.token != null) {
          setGeneratedWalkInData(prev => prev ? { ...prev, token: r.data.token } : prev);
        }
      }
      setShowVitalsModal(false); setVitalsPatientData(null); setVitalsPatientId(null);
      if (shouldShowSuccess) {
        setGeneratedWalkInData(prev => prev ? { ...prev, vitalsSkipped:isSkipped, status:isSkipped ? "Vitals skipped" : "Vitals recorded" } : prev);
        setShowWalkInSuccess(true);
      }
      if (updatedId && newStatus) setBookings(prev=>prev.map(b=>b.id===updatedId?{...b,status:newStatus}:b));
      if (updatedId) {
        try {
          const rows = await vitalMeasurementService.getAppointmentMeasurements(updatedId, sessionInfo?.doctorId, CLINIC_ID);
          const refreshedVitals = groupMeasurementHistory(rows)[0] || null;
          if (refreshedVitals) {
            setCurrentAppointmentVital(refreshedVitals);
          }
        } catch (err) {
          console.error("Failed to refresh current appointment vitals:", err);
        }
      }
      if (updatedId && activePatient?.id === updatedId) {
        setPatientHistory(prev => prev);
      }
      await fetchAll();
    } catch (err) { console.error(err); toast.error("Error updating status."); setShowVitalsModal(false); setVitalsPatientData(null); }
  };

  const handleVitalsCancel = async () => {
    if (pendingAppointmentForVitals?.isNewWalkIn && pendingAppointmentForVitals.id) {
      try {
        await axios.post(buildApiUrl(`appointments/cancel/${pendingAppointmentForVitals.id}`), null, { headers: { "X-Clinic-Id": CLINIC_ID } });
        setBookings(prev => (prev||[]).filter(b => b?.id !== pendingAppointmentForVitals.id));
      } catch (err) {
        console.error("Failed to cancel pending walk-in appointment:", err);
      }
    }
    setShowVitalsModal(false);
    setVitalsPatientData(null);
    setVitalsPatientId(null);
    setPendingAppointmentForVitals(null);
    setGeneratedWalkInData(null);
    fetchAll();
  };

  const openCurrentVitals = () => {
    if (!activePatient?.id) { toast.error("No active appointment."); return; }
    setPendingAppointmentForVitals({ id:activePatient.id, isNewWalkIn:false });
    setVitalsPatientData({ patientName:activePatient.patientName||"Patient", patientId:activePatient.patientId||null, appointmentId:activePatient.id, isBookedPatient:true });
    setVitalsPatientId(activePatient.patientId||null);
    setShowVitalsModal(true);
  };

  const handleBookedArrival = async appt => {
    setPendingAppointmentForVitals({ id:appt.id, isNewWalkIn:false });
    setVitalsPatientData({ patientName:appt.patientName, patientId:appt.patientId, appointmentId:appt.id, isBookedPatient:true });
    setVitalsPatientId(appt.patientId);
    setShowVitalsModal(true);
  };

  const pendingVitalsAppointmentId = pendingAppointmentForVitals?.id || (showVitalsModal && generatedWalkInData?.appointmentId) || null;
  const visibleBookings = (bookings||[]).filter(b => b?.status !== "WALK_IN_PENDING");
  const filtered = visibleBookings.filter(b => b?.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) || b?.tokenNo?.toString().includes(searchQuery));
  const total     = visibleBookings.length;
  const done      = visibleBookings.filter(b => b.status==="COMPLETED").length;
  const waiting   = visibleBookings.filter(b => ["ARRIVED","WAITING","BOOKED"].includes(b.status)).length;
  const consulting = visibleBookings.filter(b => b.status==="CONSULTING").length;
  const clinicName = sessionInfo?.clinicName||"OPD Command Centre";
  const doctorName = sessionInfo?.doctorName||"Physician";
  const effectiveQueue = queue?.current?.id===pendingVitalsAppointmentId?{...queue,current:null}:queue;

  const effectiveStatusMap = useMemo(() => {
    const m={};
    bookings.forEach(b=>{ if (b?.id!=null) m[b.id]=b.status; });
    Object.entries(historyAppointmentStatusMap).forEach(([id,s])=>{ if (id!=null) m[id]=s; });
    return m;
  },[bookings,historyAppointmentStatusMap]);

  /* Complete Review Screen */
  if (showCompleteReviewModal) {
    return (
      <>
        <GlobalCSS />
        <VisitReviewScreen
          activePatient={activePatient}
          currentAppointmentVital={currentAppointmentVital}
          patientHistory={patientHistory}
          vitalsHistory={vitalsHistory}
          onClose={() => setShowCompleteReviewModal(false)}
          onConfirm={() => markCompleteAndPay(activePatient)}
          isLoading={isLoadingVisitReview}
        />
      </>
    );
  }

  return (
    <>
      <GlobalCSS />

      {/* ── FIXED SHELL: full viewport, no page scroll ── */}
      <div className="opd-shell" style={{
        display:"flex", flexDirection:"column",
        height:"100vh", overflow:"hidden",
        background:"var(--bg)",
      }}>

        {/* ── HEADER ── */}
        <header className="opd-header" style={{
          background:"var(--surface)", borderBottom:"1px solid var(--border)",
          padding:"0 20px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          height:60, flexShrink:0,
          boxShadow:"0 1px 0 var(--border), 0 2px 8px rgba(0,0,0,0.04)",
          position:"relative", zIndex:10,
        }}>
          {/* Left accent bar */}
          <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:"linear-gradient(180deg, var(--blue-600), var(--violet-600))", borderRadius:"0 2px 2px 0" }} />

          <div style={{ display:"flex", alignItems:"center", gap:14, paddingLeft:10 }}>
            {/* Clinic icon */}
            <div style={{ width:36, height:36, borderRadius:"var(--r-lg)", background:"linear-gradient(135deg, var(--blue-50), var(--violet-50))", border:"1px solid var(--blue-100)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Building2 size={18} color="var(--blue-600)" strokeWidth={2} />
            </div>
            <div style={{ minWidth:0, display:"flex", flexDirection:"column", gap:2 }}>
              <h1 className="opd-header-title" style={{ fontSize:15, fontWeight:800, color:"var(--gray-900)", letterSpacing:"-0.03em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"40vw" }}>{clinicName}</h1>
              <div className="opd-session-info" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, minWidth:0, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:7, minWidth:0, flexWrap:"wrap" }}>
                  <span style={{ fontSize:11.5, color:"var(--gray-500)", whiteSpace:"nowrap" }}>Dr. {doctorName}</span>
                </div>
                <SessionBadge status={sessionStatus} />
              </div>
            </div>
          </div>

          {/* Right clock */}
          <div className="opd-header-clock" style={{ textAlign:"right", flexShrink:0 }}>
            <div style={{ fontSize:22, fontWeight:800, color:"var(--gray-900)", fontFamily:"var(--mono)", letterSpacing:"-1px", lineHeight:1, animation:"ticker 1s ease-in-out infinite" }}>{getISTTime(clock)}</div>
            <div style={{ fontSize:11, color:"var(--gray-400)", fontWeight:500, marginTop:3 }}>{formatIST(clock,{weekday:"short",day:"numeric",month:"short"})}</div>
          </div>
        </header>

        {/* ── STAT BAR (4 cards) ── */}
        <div className="opd-stat-bar" style={{
          display:"grid", gridTemplateColumns:"repeat(4,1fr)",
          background:"var(--surface)",
          borderBottom:"1px solid var(--border)",
          flexShrink:0,
        }}>
          <StatCard label="Total Patients" value={total}     icon={Users}       accent="var(--blue-600)"   delay={0}    borderRight />
          <StatCard label="Completed"      value={done}      icon={BadgeCheck}  accent="var(--green-600)"  delay={0.06} borderRight />
          <StatCard label="In Queue"       value={waiting}   icon={Clock}       accent="var(--amber-600)"  delay={0.12} borderRight />
          <StatCard label="Consulting"     value={consulting} icon={Stethoscope} accent="var(--violet-600)" delay={0.18} borderRight={false} />
        </div>

        {/* ── TOOLBAR ── */}
        <div className="opd-toolbar" style={{
          display:"flex", alignItems:"center", gap:10,
          padding:"10px 20px",
          background:"var(--surface)",
          borderBottom:"1px solid var(--border)",
          flexShrink:0,
          flexWrap:"wrap",
        }}>
          {/* Session controls */}
          <div style={{ display:"flex", gap:7, alignItems:"center" }}>
            {(sessionStatus==="NOT_STARTED"||sessionStatus==="PAUSED") && (
              <Btn variant="success" size="sm" dataCompact onClick={() => apiPost(buildApiUrl(`doctor-booking/opd/start/${scheduleId}`))}>
                <Play size={13} strokeWidth={2.5} /> {sessionStatus==="PAUSED"?"Resume":"Start"}
              </Btn>
            )}
            {sessionStatus==="RUNNING" && (
              <Btn variant="warn" size="sm" dataCompact onClick={() => apiPost(buildApiUrl(`doctor-booking/opd/pause/${scheduleId}`))}>
                <Pause size={13} strokeWidth={2.5} /> Pause
              </Btn>
            )}
            {sessionStatus!=="ENDED" && (
              <Btn variant="danger" size="sm" dataCompact onClick={() => setShowEndModal(true)}>
                <Square size={13} strokeWidth={2.5} /> End
              </Btn>
            )}
          </div>

          {/* Divider */}
          <div style={{ width:1, height:22, background:"var(--gray-200)", flexShrink:0 }} />

          <code style={{ background:"var(--gray-100)", color:"var(--gray-600)", padding:"4px 8px", borderRadius:"var(--r-sm)", fontFamily:"var(--mono)", fontSize:10.5, border:"1px solid var(--gray-200)", whiteSpace:"nowrap" }}>{scheduleId}</code>

          {/* Now consulting pill */}
          {effectiveQueue?.current && (
            <div style={{ display:"flex", alignItems:"center", gap:7, padding:"5px 12px", background:"var(--blue-50)", borderRadius:"var(--r-full)", border:"1px solid var(--blue-200)", flexShrink:0, maxWidth:220, overflow:"hidden" }}>
              <span style={{ position:"relative", width:7, height:7, borderRadius:"50%", background:"var(--blue-500)", flexShrink:0, display:"inline-block" }}>
                <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:"var(--blue-500)", animation:"pulse-ring 1.6s ease-out infinite" }} />
              </span>
              <span style={{ fontSize:11.5, fontWeight:700, color:"var(--blue-700)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>Now: {resolveDisplayName(effectiveQueue.current)}</span>
              <span style={{ fontFamily:"var(--mono)", fontSize:10.5, color:"var(--blue-500)", fontWeight:700, flexShrink:0 }}>#{effectiveQueue.current.tokenNo}</span>
            </div>
          )}

          {/* Search + Walk-in right side */}
          <div style={{ display:"flex", gap:8, marginLeft:"auto", alignItems:"center" }}>
            <div className="opd-toolbar-search" style={{ position:"relative", width:220 }}>
              <Search size={14} strokeWidth={2} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"var(--gray-400)", pointerEvents:"none" }} />
              <input className="opd-input" style={{ paddingLeft:32, height:34, fontSize:12.5 }} placeholder="Search patient or token..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <Btn variant="primary" size="sm" dataCompact disabled={sessionStatus!=="RUNNING"} onClick={() => setShowWalkIn(true)}>
              <Plus size={14} strokeWidth={2.5} /> Add Walkin
            </Btn>
            <button onClick={() => fetchAll()} title="Refresh"
              style={{ width:34, height:34, borderRadius:"var(--r-lg)", border:"1.5px solid var(--gray-200)", background:"var(--white)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--gray-500)", transition:"all 0.13s", flexShrink:0 }}
              onMouseEnter={e => { e.currentTarget.style.background="var(--blue-50)"; e.currentTarget.style.color="var(--blue-600)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="var(--white)"; e.currentTarget.style.color="var(--gray-500)"; }}>
              <RefreshCw size={14} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* ── TABLE AREA (only this scrolls) ── */}
        <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
          <div className="q-table-scroll" style={{ flex:1, overflowY:"auto", overflowX:"auto", paddingBottom: 56 }}>
            <table className="q-table" style={{ width:"100%", borderCollapse:"collapse", fontSize:13, tableLayout:"fixed" }}>
              <thead style={{ position:"sticky", top:0, zIndex:5 }}>
                <tr style={{ background:"linear-gradient(90deg, var(--blue-600) 0%, var(--violet-600) 100%)", borderBottom:"2px solid rgba(255,255,255,0.08)" }}>
                  <th className="q-token-col" style={{ padding:"11px 16px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.08em", width:86 }}>Token</th>
                  <th className="q-slot-col" style={{ padding:"11px 12px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.08em", width:86 }}>Slot</th>
                  <th className="q-name-col" style={{ padding:"11px 16px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.08em", width:"28%" }}>Patient</th>
                  <th className="q-phone-col" style={{ padding:"11px 16px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.08em", width:170 }}>Phone</th>
                  <th style={{ padding:"11px 16px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.08em", width:110 }}>Status</th>
                  <th style={{ padding:"11px 16px", textAlign:"center", fontSize:10, fontWeight:800, color:"var(--white)", textTransform:"uppercase", letterSpacing:"0.08em", width:132 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign:"center", padding:"80px 20px" }}>
                      <div style={{ width:64, height:64, borderRadius:"var(--r-2xl)", background:"var(--gray-100)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", border:"1px solid var(--gray-200)" }}>
                        <Users size={28} color="var(--gray-300)" strokeWidth={1.5} />
                      </div>
                      <div style={{ fontSize:15, fontWeight:700, color:"var(--gray-500)", marginBottom:5 }}>No patients in queue</div>
                      <div style={{ fontSize:13, color:"var(--gray-400)" }}>Start the session and register walk-ins or check in booked patients</div>
                    </td>
                  </tr>
                ) : filtered.map((b, idx) => (
                  <tr key={b.id} className="q-row" style={{
                    background:b.status==="CONSULTING"?"rgba(59,130,246,0.04)":idx%2===0?"var(--surface)":"var(--gray-50)",
                    borderLeft:`3px solid ${b.status==="CONSULTING"?"var(--blue-500)":"transparent"}`,
                    animation:`rise 0.3s var(--ease) ${idx*0.025}s both`,
                  }}>
                    <td className="q-token-col" style={{ padding:"14px 16px", textAlign:"center", width:86 }}>
                      <div className="q-token-badge" style={{ display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto",
                        width:36, height:36, background:b.status==="CONSULTING"?"var(--blue-100)":"var(--gray-100)",
                        borderRadius:"var(--r-lg)", fontFamily:"var(--mono)", fontWeight:700, fontSize:12,
                        color:b.status==="CONSULTING"?"var(--blue-700)":"var(--gray-600)",
                        border:`1px solid ${b.status==="CONSULTING"?"var(--blue-200)":"var(--gray-200)"}` }}>
                        {b.tokenNo}
                      </div>
                      <div className="q-token-sub" style={{ fontFamily:"var(--mono)", fontSize:11.5, color:"var(--gray-500)", fontWeight:600, marginTop:4, textAlign:"center" }}>
                        {formatSlotTime(b?.slotTime)}
                      </div>
                    </td>
                    <td className="q-slot-col" style={{ padding:"14px 12px", fontFamily:"var(--mono)", fontSize:12.5, color:"var(--gray-500)", fontWeight:600, textAlign:"center", width:86 }}>
                      {formatSlotTime(b?.slotTime)}
                    </td>
                    <td className="q-name-col" style={{ padding:"14px 10px", minWidth:0, overflow:"hidden", width:"28%" }}>
                      <div style={{ fontWeight:700, fontSize:14, color:"var(--gray-900)", letterSpacing:"-0.01em", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{resolveDisplayName(b)}</div>
                      {b.phone && <div className="q-phone-sub" style={{ fontSize:11.5, color:"var(--gray-400)", marginTop:2, fontFamily:"var(--mono)" }}>{b.phone}</div>}
                    </td>
                    <td className="q-phone-col" style={{ padding:"14px 16px", fontFamily:"var(--mono)", fontSize:12.5, color:"var(--gray-500)", fontWeight:600, whiteSpace:"nowrap", textAlign:"center", width:170 }}>
                      {b.phone || "—"}
                    </td>
                    <td style={{ padding:"14px 8px", whiteSpace:"nowrap", textAlign:"center", width:110 }}>
                      <StatusBadge status={b.status} />
                    </td>
                    <td style={{ padding:"14px 8px", textAlign:"center", width:132 }}>
                      <div className="mobile-action-compact" style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"center" }}>
                        {sessionStatus === "RUNNING" && (
                          <>
                            {b.status==="BOOKED" && (
                              <Btn variant="soft" size="xs" onClick={() => handleBookedArrival(b)}>
                                <CheckCircle size={12} strokeWidth={2} /> Check In
                              </Btn>
                            )}
                            {b.status==="ARRIVED" && (
                              <Btn variant="ghost" size="xs" onClick={() => handleBookedArrival(b)} dataCompact>
                                <Activity size={12} strokeWidth={2} /> Vitals
                              </Btn>
                            )}
                            {b.status==="CONSULTING" && (
                              <>
                                <Btn variant="ghost" size="xs" onClick={() => handleBookedArrival(b)} dataCompact>
                                  <Activity size={12} strokeWidth={2} /> Vitals
                                </Btn>
                                {doctorEPrescriptionEnabled ? (
                                  <Btn variant="violet" size="xs" onClick={() => openRx(b)} dataCompact>
                                    <Pill size={12} strokeWidth={2} /> E-Rx
                                  </Btn>
                                ) : (
                                  <Btn variant="violet" size="xs" onClick={() => openReviewModal(b)}>
                                    <CheckCircle2 size={12} strokeWidth={2} /> Complete
                                  </Btn>
                                )}
                              </>
                            )}
                          </>
                        )}
                        {b.status === "COMPLETED" && (
                          patientInvoiceMap[b.id]?.paymentStatus === "PAID" ? (
                            <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 6px", background:"var(--green-50)", border:"1px solid var(--green-200)", borderRadius:"var(--r-full)", color:"var(--green-700)", fontSize:11, fontWeight:600 }}>
                              <BadgeCheck size={12} strokeWidth={2} /> Paid
                            </span>
                          ) : (
                            <Btn variant="warn" size="xs" onClick={() => openPatientInvoice(b)} dataCompact>
                              <FileText size={12} strokeWidth={2} /> {patientInvoiceMap[b.id]?.invoiceStatus === "FINALIZED" ? "Collect" : "Invoice"}
                            </Btn>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>

      {/* ── MODALS ── */}
      {showRxModal && (
        <FullPageView onClose={() => setShowRxModal(false)}>
          <PrescriptionPad
            activePatient={activePatient} patientHistory={patientHistory}
            vitalsHistory={vitalsHistory} currentAppointmentVital={currentAppointmentVital}
            appointmentStatusMap={effectiveStatusMap} doctorName={doctorName}
            sessionInfo={sessionInfo} clinicId={CLINIC_ID} onClose={() => setShowRxModal(false)}
            onSave={handleRxSave} onOpenVitalsModal={openCurrentVitals}
          />
        </FullPageView>
      )}

      {showPaymentWorkflow && lastPrescriptionId && (
        <Overlay onBackdropClick={() => setShowPaymentWorkflow(false)}>
          <PrescriptionPaymentWorkflow prescriptionId={lastPrescriptionId} clinicId={CLINIC_ID} patientName={paymentPatientInfo?.patientName}
            onClose={() => { setShowPaymentWorkflow(false); setPaymentPatientInfo(null); fetchAll(); }}
            onPaymentComplete={async () => {
              setShowPaymentWorkflow(false); setPaymentPatientInfo(null);
              if (paymentPatientInfo?.patientId) {
                try { const p=await fetch(buildApiUrl(`prescriptions/patient/${paymentPatientInfo.patientId}`),{headers:{"X-Clinic-Id":CLINIC_ID}}); if (p.ok) { const rxs=await p.json()||[]; setPrescriptionDataMap(prev => { const u={...prev}; rxs.forEach(rx => { if (rx.id&&rx.appointmentId) u[rx.appointmentId]=rx; }); return u; }); } } catch { fetchAll(); }
              } else fetchAll();
              toast.success("Payment received successfully.");
            }}
          />
        </Overlay>
      )}

      {showWalkIn && (
        <Overlay onBackdropClick={() => { setShowWalkIn(false); setWalkInForm({ patientName:"", phone:"", age:"", gender:"MALE" }); }}>
          <WalkInModal onClose={() => setShowWalkIn(false)} onBasicSubmit={handleWalkInSubmit} walkInForm={walkInForm} setWalkInForm={setWalkInForm} previousBooking={walkInPreviousBooking} accessCode={walkInAccessCode} familyMembers={walkInFamilyMembers} isNewPatient={isNewWalkInPatient} setIsNewPatient={setIsNewWalkInPatient} Button={Btn} />
        </Overlay>
      )}

      {showVitalsModal && vitalsPatientData && (
        <Overlay maxWidth={760}>
          <DynamicVitalsCaptureModal onSave={handleVitalsSave} onCancel={handleVitalsCancel} patientData={vitalsPatientData} doctorId={sessionInfo?.doctorId} clinicId={CLINIC_ID} />
        </Overlay>
      )}

      {showConsultationPaymentModal && consultationPaymentAppointment && (
        <ConsultationPaymentModal
          isOpen={showConsultationPaymentModal} clinicId={CLINIC_ID}
          onClose={() => { setShowConsultationPaymentModal(false); setConsultationPaymentAppointment(null); }}
          appointment={consultationPaymentAppointment} patientName={consultationPaymentAppointment.patientName}
          onPaymentComplete={() => { setShowConsultationPaymentModal(false); setConsultationPaymentAppointment(null); fetchAll(); toast.success("Payment collected successfully"); }}
          onPaymentFailed={err => { console.error(err); toast.error("Payment collection failed"); }}
        />
      )}

      {patientInvoiceModal && (
        <PatientInvoiceBillingModal
          clinicId={CLINIC_ID}
          appointment={patientInvoiceModal.appointment}
          existingInvoice={patientInvoiceModal.invoice}
          mode={patientInvoiceModal.mode}
          onClose={() => setPatientInvoiceModal(null)}
          onComplete={(invoice, action) => {
            setPatientInvoiceMap(prev => ({ ...prev, [invoice.appointmentId]: invoice }));
            if (action !== "saved") setPatientInvoiceModal(null);
            fetchAll();
            toast.success(action === "paid" ? "Payment confirmed and receipt sent." : action === "sent" ? "Invoice sent successfully." : "Invoice draft saved.");
          }}
        />
      )}


      {showWalkInSuccess && generatedWalkInData && (
        <Overlay onBackdropClick={() => { setShowWalkInSuccess(false); setGeneratedWalkInData(null); }}>
          <WalkInSuccessModal data={generatedWalkInData} onClose={() => { setShowWalkInSuccess(false); setGeneratedWalkInData(null); }} />
        </Overlay>
      )}

      {showEndModal && (
        <Overlay onBackdropClick={() => setShowEndModal(false)}>
          <EndSessionModal
            onClose={() => setShowEndModal(false)}
            onConfirm={async () => {
              await apiPost(buildApiUrl(`doctor-booking/opd/end/${scheduleId}`));
              fetchAll();
            }}
            done={done}
            total={total}
          />
        </Overlay>
      )}
    </>
  );
}